/**
 * CommandDock – Bottom-center selection panel.
 * Shows ship info when a player-owned unit is selected,
 * or asteroid info when an asteroid is selected.
 * Figma: 653px wide, #131026 bg, green 3px border top/left/right.
 * Slides up on selection, slides down on deselect.
 */

import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { MMONetworkManager } from '../../managers/MMONetworkManager';
import { EventBus } from '../../utils/EventBus';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import {
  Fonts, Anim, Depth,
} from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { distanceToFootprint, STATION_FOOTPRINT, hexNeighbors, hexDistance } from '../../shared/common/HexUtils';
import type { FootprintSize } from '../../shared/common/HexUtils';
import { isMobile } from '../../utils/ResponsiveHelper';


// ── Mineral storage capacity per station type / Earth ──────────
const MINERAL_STORAGE_CAPACITY: Record<string, number> = {
  waypoint: 300,
  depot: 1000,
  fortress: 5000,
  capital: 99900,
  earth: 10000,
};

// ── Platform tier config ──────────────────────────────────────
const PLATFORM_TIER_CONFIG: Record<string, { color: string; hex: number; label: string }> = {
  basic:     { color: '#AAAAAA', hex: 0x888888, label: 'Basic Platform' },
  advanced:  { color: '#4488FF', hex: 0x3366cc, label: 'Advanced Platform' },
  elite:     { color: '#FFD700', hex: 0xccaa00, label: 'Elite Platform' },
  legendary: { color: '#CC66FF', hex: 0xaa44dd, label: 'Legendary Platform' },
};

// ── Figma panel specs ──────────────────────────────────────────
const DOCK_W = 780;
const DOCK_H = 144;
const BG_COLOR = 0x131026;
const BORDER_COLOR = 0x00ff41;
const BORDER_ALPHA = 0.4;
const BORDER_W = 3;

// ── Mobile vertical-sidebar specs ──────────────────────────────
// When a unit is selected on mobile the dock pivots 90° — it becomes a
// narrow column anchored to the left edge so the map gets back the
// entire bottom of the screen. These constants drive that layout.
//
// Sizing note: bumped ~1.5x from the original 60px column to 90px for
// proper mobile tap targets (Apple HIG / Material min 44px). Buttons,
// tab chips, and internal paddings were scaled in proportion.
const MOBILE_SIDEBAR_W           = 90;
const MOBILE_SIDEBAR_TOP         = 56;   // below 44px top bar + 12px gap
const MOBILE_SIDEBAR_MARGIN_X    = 8;    // from left edge
// MOBILE_SIDEBAR_PAD_TOP retired — replaced by MOBILE_SIDEBAR_TABS_H below,
// which reserves space for the tab-chip header instead of a blank gap.
const MOBILE_SIDEBAR_PAD_BOTTOM  = 14;
// Action buttons: kept at a compact 42px so the 4-button stack (MOVE / ENGAGE
// / RETURN / GOTO) reliably fits between the tab header and the bottom of
// the viewport on small landscape phones. Hit zone is still 42×42 — close to
// the 44px HIG minimum and consistent with the other mobile FABs.
const MOBILE_SIDEBAR_BTN_SIZE    = 42;
const MOBILE_SIDEBAR_BTN_VGAP    = 6;
// Tab-header: 2×2 grid of tab chips so ACTIONS / ABILITIES / CARGO / CREW
// fit inside the sidebar rather than spilling across the top of the
// viewport. Active chip fills cyan, inactive sits dark. Switching rebuilds
// the content below; the header position is rock stable (see
// MOBILE_SIDEBAR_MIN_CONTENT_H) so tapping tabs doesn't make the panel jump.
// Tabs are deliberately kept at 40×28 per the "ACT ABI CAR CRW buttons are
// fine in size" design note — only the action buttons below got reduced.
const MOBILE_SIDEBAR_TAB_TOP     = 8;
const MOBILE_SIDEBAR_TAB_W       = 40;
const MOBILE_SIDEBAR_TAB_H       = 28;
const MOBILE_SIDEBAR_TAB_GAP_X   = 4;
const MOBILE_SIDEBAR_TAB_GAP_Y   = 4;
// Total header height = top padding + 2 rows of tab_h + 1 vgap + 6 spacer
const MOBILE_SIDEBAR_TABS_H      = MOBILE_SIDEBAR_TAB_TOP + 2 * MOBILE_SIDEBAR_TAB_H + MOBILE_SIDEBAR_TAB_GAP_Y + 6;
// Minimum height of the content area (below tab header). Keeps short tabs
// like ABILITIES / CREW from collapsing the panel. Sized to comfortably
// fit 4 action buttons at 42px + labels + tight vgap without forcing the
// panel past the bottom of the viewport on short phones.
const MOBILE_SIDEBAR_MIN_CONTENT_H = 210;

// ── Mobile info-panel specs ────────────────────────────────────
// Wider left-anchored panel used for PLANET (Earth) on mobile.
// Capped at half the viewport so the map is always visible on the
// right side. Content is paginated to avoid cramming everything
// into one view.
const MOBILE_INFO_TOP            = 56;
const MOBILE_INFO_MARGIN_X       = 6;
const MOBILE_INFO_BOTTOM_PAD     = 16;
const MOBILE_INFO_MIN_W          = 260;
const MOBILE_INFO_MAX_W          = 360;
const MOBILE_INFO_PAD_X          = 12;   // inner horizontal padding
const MOBILE_INFO_PAD_TOP        = 12;
const MOBILE_INFO_FOOTER_H       = 34;   // page dots + prev/next arrows
const MOBILE_INFO_PORT_SIZE      = 64;   // circular portrait inside header

// Portrait (just the image, no bg/border)
const PORT_W = 134;
const PORT_H = 75;
const PORT_LEFT = 13;
const PORT_TOP = 10;

// Ship name
const NAME_LEFT = 169;
const NAME_TOP = 31;

// Owner line
const OWNER_LEFT = 169;
const OWNER_TOP = 56;

// Stats row
const STATS_LEFT = 27;
const STATS_BOTTOM = 7;           // from bottom of panel
const STAT_VALUE_GAP = 8;         // value px to right of label
const STAT_GAP = 20;              // gap between stat groups (fits 4 stats before action buttons)

// Tabs
const TAB_ACTIONS_LEFT = 445;
const TAB_TOP = 23;
const TAB_UNDERLINE_Y = 40;       // absolute Y for underline

// Action buttons
const ACTION_BTN_SIZE = 54;       // circle diameter
const ACTION_BTN_GAP = 12;
const ACTION_ROW_TOP = 56;        // top of circles
const ACTION_ROW_LEFT = 362;      // aligned with ACTIONS tab (shifted left to fit 6 buttons)
const ACTION_ICON_SIZE = 26;

// Label badge on action buttons
const BADGE_PAD = 5;              // padding inside badge (inline-flex style)
const BADGE_TOP = 116;            // absolute Y for badge top
const BADGE_STROKE = 3;

// ── Ship icon map ──────────────────────────────────────────────
const SHIP_ICON_MAP: Record<string, string> = {
  scout: 'scout',
  fighter: 'fighter',
  bomber: 'bomber',
  cruiser: 'cruiser',
  carrier: 'carrier',
  battleship: 'battleship',
  destroyer: 'destroyer',
  titan: 'titan',
  dreadnaught: 'dreadnaught',
  tradeship: 'tradeship',
  trade_ship: 'tradeship',
  space_tug: 'tug',
  tug: 'tug',
  leviathan: 'leviathan',
  alien_leviathan: 'alien-leviathan',
};

// ── Default variant names (Mk1 for each class) ──────────────────
const DEFAULT_VARIANT_NAME: Record<string, string> = {
  scout: 'Sparrow Mk1',
  fighter: 'Wraith Mk1',
  destroyer: 'Corsair Mk1',
  bomber: 'Hornet Mk1',
  space_tug: 'Mule Mk1',
  cruiser: 'Tempest Mk1',
  carrier: 'Aegis Mk1',
  battleship: 'Warden Mk1',
  dreadnought: 'Imperator Mk1',
  titan: 'Atlas Mk1',
};

// ── Ability icon map ───────────────────────────────────────────
const ABILITY_ICON_MAP: Record<string, string> = {
  sensor_sweep: 'icon-scan',
  sensor_boosted: 'icon-scan',
  evasive_maneuvers: 'icon-evade',
  evasive: 'icon-evade',
  afterburner: 'icon-evade',               // reuse evade icon
  speed_boosted: 'icon-evade',
  missile_barrage: 'icon-attack',           // reuse attack icon
  broadside: 'icon-attack',
  shield_overcharge: 'icon-scan',           // reuse scan icon
  shield_boosted: 'icon-scan',
  emergency_jump: 'icon-evade',
  tow: 'icon-tow',
};

// ── Tooltip descriptions (getter functions — I18n.t() requires runtime call) ──
function getActionTooltips(): Record<string, string> {
  return {
    MOVE: I18n.t('tooltip.move'),
    ATTACK: I18n.t('tooltip.attack'),
    DOCK: I18n.t('tooltip.dock'),
    UNDOCK: I18n.t('tooltip.undock'),
  };
}

function getStatTooltips(): Record<string, string> {
  return {
    SPEED: I18n.t('tooltip.speed'),
    ATTACK: I18n.t('tooltip.attack_stat'),
    DEFENSE: I18n.t('tooltip.defense'),
    RANGE: I18n.t('tooltip.range'),
  };
}

function getAbilityTooltips(): Record<string, string> {
  return {
    sensor_sweep: I18n.t('tooltip.sensor_sweep'),
    evasive_maneuvers: I18n.t('tooltip.evasive_maneuvers'),
    afterburner: I18n.t('tooltip.afterburner'),
    missile_barrage: I18n.t('tooltip.missile_barrage'),
    broadside: I18n.t('tooltip.broadside'),
    shield_overcharge: I18n.t('tooltip.shield_overcharge'),
    emergency_jump: I18n.t('tooltip.emergency_jump'),
  };
}

// Short canonical names for ability badges
function getAbilityShortNames(): Record<string, string> {
  return {
    sensor_sweep: I18n.t('ability.scan'),
    evasive_maneuvers: I18n.t('ability.evade'),
    afterburner: I18n.t('ability.boost'),
    missile_barrage: I18n.t('ability.volley'),
    broadside: I18n.t('ability.broad'),
    shield_overcharge: I18n.t('ability.shield'),
    emergency_jump: I18n.t('ability.jump'),
  };
}

// Facing direction labels (0-5 map to hex directions)
const FACING_LABELS = ['E', 'SE', 'SW', 'W', 'NW', 'NE'];

// ── Asteroid image map (variant/type -> image key) ──────────
const ASTEROID_IMAGE_MAP: Record<string, string> = {
  golden: 'asteroid-gold',
  common_gold: 'asteroid-gold',
  rare_platinum: 'asteroid-platinum',
  legendary_dark_matter: 'asteroid-dark-matter',
  welcome_gift: 'asteroid-exotic',
  // Regular asteroids map by richness tier
  rich: 'asteroid-rare',
  moderate: 'asteroid-copper',
  poor: 'asteroid-common',
  default: 'asteroid-iron',
};

// Richness tier labels — colors match MMOAsteroid.getRichnessConfig() on the world map
function getRichnessTier(richness: number): { label: string; color: string; imageKey: string } {
  if (richness >= 80) return { label: I18n.t('asteroid.legendary'), color: '#cc66ff', imageKey: 'asteroid-exotic' };
  if (richness >= 60) return { label: I18n.t('asteroid.very_rich'), color: '#ffcc33', imageKey: 'asteroid-rare-crystal' };
  if (richness >= 40) return { label: I18n.t('asteroid.rich'), color: '#ddaa44', imageKey: 'asteroid-rare' };
  if (richness >= 20) return { label: I18n.t('asteroid.moderate'), color: '#99886a', imageKey: 'asteroid-copper' };
  return { label: I18n.t('asteroid.poor'), color: '#777777', imageKey: 'asteroid-common' };
}

function getAsteroidStatTooltips(): Record<string, string> {
  return {
    RICHNESS: I18n.t('tooltip.richness'),
    'REMAINING ORE': I18n.t('tooltip.remaining_ore'),
    RARITY: I18n.t('tooltip.rarity'),
  };
}

function getAsteroidRarity(a: SelectedAsteroid): { label: string; color: string } {
  if (a.asteroidType === 'golden') {
    switch (a.variant) {
      case 'legendary_dark_matter': return { label: I18n.t('asteroid.legendary'), color: '#CC66FF' };
      case 'rare_platinum': return { label: I18n.t('mineral.rare'), color: '#88AADD' };
      default: return { label: I18n.t('mineral.common'), color: '#FFD700' };
    }
  }
  const r = a.resourceRichness;
  if (r >= 80) return { label: I18n.t('asteroid.legendary'), color: '#cc66ff' };
  if (r >= 60) return { label: I18n.t('mineral.epic'), color: '#ffcc33' };
  if (r >= 40) return { label: I18n.t('mineral.rare'), color: '#ddaa44' };
  if (r >= 20) return { label: I18n.t('mineral.uncommon'), color: '#99886a' };
  return { label: I18n.t('mineral.common'), color: '#777777' };
}

interface SelectedAsteroid {
  id: string;
  ownerId: string | null;
  hexCoord: { q: number; r: number };
  resourceRichness: number;
  miningLevel: number;
  storedResources: number;
  maxStorage: number;
  asteroidType?: 'regular' | 'golden' | 'special' | 'crypto';
  variant?: 'common_gold' | 'rare_platinum' | 'legendary_dark_matter';
  rewardValue?: number | null;
  mineralComposition?: Record<string, number> | null;
}

interface SelectedUnit {
  id: string;
  type: string;
  unitType?: string;
  ownerId: string;
  currentHp: number;
  maxHp: number;
  currentShields: number;
  maxShields: number;
  attack: number;
  defense: number;
  speed: number;
  attackRange?: number;
  hexCoord: { q: number; r: number };
  facing?: number;
  dockedAtPlanetId?: string | null;
  cargoCapacity?: number;
  cargoUsed?: number;
  miningAsteroidId?: string | null;
  abilities?: any[];
  ownerName?: string;
  customName?: string | null;
  allianceTag?: string;
  allianceName?: string;
  laserTier?: string;
  laserConditionPercent?: number;
}

interface SelectedPlanet {
  id: string;
  planetName: string;
  ownerId: string | null;
  hexCoord: { q: number; r: number };
  isHomePlanet: boolean;
  currentHp: number;
  maxHp: number;
  currentShields: number;
  maxShields: number;
  buildings: any[];
  productionQueue: any[];
  maxStorage?: number;
}

interface SelectedStation {
  id: string;
  stationType: string;
  userId: string;
  hexCoord: { q: number; r: number };
  currentHp: number;
  maxHp: number;
  currentShields?: number;
  maxShields?: number;
  storedAsteroids: number;
  maxStorage: number;
  stationFeePct: number;
  status: string;
}

interface SelectedWreck {
  id: string;
  ownerId: string;
  unitType: string;
  hexCoord: { q: number; r: number };
  towStatus: string;
  repairStatus: string;
  destroyedAt: string;
  expiresAt: string;
}

interface SelectedPlatformEntity {
  id: string;
  userId: string | null;
  planetId: string;
  platformTier: string;
  hexCoord: { q: number; r: number };
  currentHp: number;
  maxHp: number;
  currentShields: number;
  maxShields: number;
  mineralStorageCapacity: number;
  hasRepairBay: boolean;
  hasShipyard: boolean;
  hasUnitBuilding: boolean;
  hasOrbitalMining: boolean;
  isPublic: boolean;
  status: string;
}

// ── Docked ship tab dimensions ─────────────────────────────────
const DOCKED_TAB_H = 26;
const DOCKED_TAB_PAD_H = 12;
const DOCKED_TAB_GAP = 4;
const DOCKED_TAB_ICON_SIZE = 16;

export class CommandDock extends Phaser.GameObjects.Container {
  private mmoState: MMOStateManager;
  private dockBg: Phaser.GameObjects.Graphics;
  private contentContainer: Phaser.GameObjects.Container;
  private currentUnit: SelectedUnit | null = null;
  private currentAsteroid: SelectedAsteroid | null = null;
  private currentPlanet: SelectedPlanet | null = null;
  private currentStation: SelectedStation | null = null;
  private currentWreck: SelectedWreck | null = null;
  private currentPlatform: SelectedPlatformEntity | null = null;
  private currentLootDrop: any | null = null;
  private currentAmbientEntity: any | null = null;
  private displayMode: 'unit' | 'enemy_unit' | 'asteroid' | 'planet' | 'station' | 'platform' | 'wreck' | 'loot_drop' | 'ambient_entity' | null = null;
  private isVisible = false;
  // True when the dock is rendering as a left-side vertical sidebar
  // (mobile + unit/enemy_unit modes). Drives layout + slide direction.
  private inSidebarMode = false;
  // Cached sidebar height for the current action count — used by
  // drawBackground so the pill fits the buttons exactly.
  private sidebarH = 200;
  // True when dock is rendering as the left half-screen info panel
  // (mobile + planet mode). Paginated content.
  private inInfoPanelMode = false;
  private infoPanelW = 320;
  private infoPanelH = 400;
  private infoPanelPage = 0;    // active page index
  private infoPanelPages = 1;   // total page count
  private activeTab: 'actions' | 'abilities' | 'cargo' | 'crew' | 'upgrades' | 'buildings' | 'producing' | 'storage' | 'manage' | 'buy_ship' | 'design' | 'research' | 'tech' | 'scan' | 'mine' | 'alliance' = 'actions';
  private cargoData: { minerals: Record<string, number>; stellarValue: number; cargoCapacity?: number; cargoUsed?: number; components?: { id: string; definitionId: string; name: string; category: string; rarity: string; starRating: number; conditionPercent: number }[]; cryptoTokens?: { name: string; chain: string; amount: number; value: number }[] } | null = null;
  private isLoadingCargo = false;
  private storageData: { mineralTypeId: string; mineralName: string; displayName: string; rarity: string; color: string; amount: number }[] | null = null;
  private isLoadingStorage = false;
  private scanData: { scanQuality: number; resources: Record<string, { estimatedAmount: number; mineralName: string; rarity: string }> } | null = null;
  private isScanning = false;
  private crewData: { hasCrew: boolean; crew?: { id: string; name: string; level: number; combatSkill: number; pilotingSkill: number; navigationSkill: number; engineeringSkill: number; scienceSkill: number; totalXp: number; status: string } } | null = null;
  private isLoadingCrew = false;
  private isInMoveMode = false;
  private _lastTabRightEdge = TAB_ACTIONS_LEFT;
  private tooltipContainer: Phaser.GameObjects.Container | null = null;

  // Tracks the last selected player unit's mining laser rarity for asteroid composition display
  private lastPlayerMaxRarity: string = 'common';

  // Enemy owner info cache
  private ownerCache = new Map<string, { displayName: string; allianceTag: string | null; allianceName: string | null }>();

  // Docked ship tabs state
  private dockedTabContainer: Phaser.GameObjects.Container | null = null;
  private dockedShips: SelectedUnit[] = [];
  private activeDockedTab: number = -1; // -1 = planet/station view, 0+ = docked ship index
  /** The original planet/station entity that was selected (preserved when switching to docked ship tab) */
  private dockedParentPlanet: SelectedPlanet | null = null;
  private dockedParentStation: SelectedStation | null = null;
  private gotoOverlay: HTMLDivElement | null = null;

  constructor(scene: Phaser.Scene, mmoState: MMOStateManager) {
    const { width, height } = scene.cameras.main;
    super(scene, (width - DOCK_W) / 2, height);

    this.mmoState = mmoState;

    // Background
    this.dockBg = scene.add.graphics();
    this.drawBackground();
    this.add(this.dockBg);

    // Content
    this.contentContainer = scene.add.container(0, 0);
    this.add(this.contentContainer);

    // Events
    EventBus.on('mmo-entity-selection-resolved', this.onEntitySelected, this);
    EventBus.on('mmo-deselect', this.onDeselect, this);
    EventBus.on('mmo-entity-deselected', this.onDeselect, this);
    EventBus.on('build-queue-updated', this.onBuildQueueUpdated, this);
    EventBus.on('fleet-activate-docked-tab', this.onFleetActivateDockedTab, this);
    EventBus.on('mmo-cargo-unloaded', this.onCargoUnloaded, this);
    EventBus.on('mmo-movement-mode-started', this.onMovementModeStarted, this);
    EventBus.on('mmo-movement-mode-cancelled', this.onMovementModeCancelled, this);
    EventBus.on('mmo-scan-result', this.onScanResult, this);
    EventBus.on('mmo-planet-scan-data', this.onPlanetScanData, this);
    EventBus.on('mmo-station-mine-started', this.onStationMineStarted, this);
    EventBus.on('mmo-station-mine-stopped', this.onStationMineStopped, this);
    EventBus.on('mmo-ambient-scan-result', this.onAmbientScanResult, this);
    EventBus.on('mmo-station-mine-result', this.onStationMineResult, this);

    this.setDepth(Depth.topBar);
    this.setVisible(false);
    scene.add.existing(this);
  }

  // ── Background ──────────────────────────────────────────────

  private drawBackground(w: number = DOCK_W, h: number = DOCK_H): void {
    const g = this.dockBg;
    g.clear();

    // Enemy-unit mobile mode uses a floating bottom-center pill bar instead
    // of the sidebar chrome. Skip drawing the (otherwise empty) sidebar frame.
    if (this.inSidebarMode && this.displayMode === 'enemy_unit' && isMobile(this.scene)) {
      return;
    }

    if (this.inSidebarMode) {
      // Vertical sidebar shape: rounded-rect pill with cyan-green glow.
      // Stroke is drawn on all 4 sides so it looks like a free-floating chip.
      g.fillStyle(0x00ff41, 0.04);
      g.fillRoundedRect(-10, -10, w + 20, h + 20, 16);
      g.fillStyle(0x00ff41, 0.08);
      g.fillRoundedRect(-5, -5, w + 10, h + 10, 14);
      g.fillStyle(BG_COLOR, 0.97);
      g.fillRoundedRect(0, 0, w, h, 12);
      g.lineStyle(BORDER_W, BORDER_COLOR, BORDER_ALPHA);
      g.strokeRoundedRect(0, 0, w, h, 12);
      return;
    }

    if (this.inInfoPanelMode) {
      // Left-anchored half-screen info panel — rounded card with subtle glow.
      g.fillStyle(0x00ff41, 0.03);
      g.fillRoundedRect(-8, -8, w + 16, h + 16, 18);
      g.fillStyle(0x00ff41, 0.06);
      g.fillRoundedRect(-4, -4, w + 8, h + 8, 16);
      g.fillStyle(BG_COLOR, 0.97);
      g.fillRoundedRect(0, 0, w, h, 14);
      g.lineStyle(BORDER_W, BORDER_COLOR, BORDER_ALPHA);
      g.strokeRoundedRect(0, 0, w, h, 14);
      return;
    }

    // Desktop / horizontal bottom dock (original Figma layout).
    // Drop shadow glow: x=0 y=0 blur=25 spread=0 #00FF41 at 15%
    g.fillStyle(0x00ff41, 0.04);
    g.fillRect(-12, -12, w + 24, h + 12);
    g.fillStyle(0x00ff41, 0.08);
    g.fillRect(-6, -6, w + 12, h + 6);

    // Main fill
    g.fillStyle(BG_COLOR, 1);
    g.fillRect(0, 0, w, h);

    // 3px border: top, left, right only (no bottom)
    g.lineStyle(BORDER_W, BORDER_COLOR, BORDER_ALPHA);
    g.beginPath();
    g.moveTo(0, h);
    g.lineTo(0, 0);
    g.lineTo(w, 0);
    g.lineTo(w, h);
    g.strokePath();
  }

  // ── Entity handling ───────────────────────────────────────

  private onEntitySelected(data: { entity: any; type: string }): void {
    const entity = data.entity;
    if (!entity) { this.hide(); return; }

    // Detect re-selection of the same entity (e.g. tick updates while moving)
    const isSameEntity = entity.id != null && (
      (this.currentUnit?.id === entity.id) ||
      (this.currentAsteroid?.id === entity.id) ||
      (this.currentPlanet?.id === entity.id) ||
      (this.currentStation?.id === entity.id) ||
      (this.currentPlatform?.id === entity.id) ||
      (this.currentWreck?.id === entity.id) ||
      (this.currentLootDrop?.id === entity.id) ||
      (this.currentAmbientEntity?.id === entity.id)
    );

    if (isSameEntity && !(data as any).forceRebuild) {
      // Preserve current tab and cached data — just refresh the display
      if ('towStatus' in entity && 'destroyedAt' in entity) this.currentWreck = entity as SelectedWreck;
      else if ('attackRange' in entity) this.currentUnit = entity as SelectedUnit;
      else if ('resourceRichness' in entity) this.currentAsteroid = entity as SelectedAsteroid;
      else if ('planetName' in entity) {
        this.currentPlanet = entity as SelectedPlanet;
        this.dockedShips = this.getPlayerDockedShipsAtEntity(this.currentPlanet.id);
        if (this.activeDockedTab >= this.dockedShips.length) this.activeDockedTab = -1;
      } else if ('platformTier' in entity && this.currentPlatform) {
        Object.assign(this.currentPlatform, entity);
        this.currentPlatform.hexCoord = entity.hexCoord ?? { q: entity.positionQ, r: entity.positionR };
        this.dockedShips = this.getPlayerDockedShipsAtEntity(this.currentPlatform.id);
        if (this.activeDockedTab >= this.dockedShips.length) this.activeDockedTab = -1;
      } else if ('stationType' in entity) {
        this.currentStation = entity as SelectedStation;
        this.dockedShips = this.getPlayerDockedShipsAtEntity(this.currentStation.id);
        if (this.activeDockedTab >= this.dockedShips.length) this.activeDockedTab = -1;
      } else if ('entityType' in entity) {
        this.currentAmbientEntity = entity;
      }
      this.buildDisplay();
      return;
    }

    // Clear all entity refs for a new selection
    this.currentUnit = null;
    this.currentAsteroid = null;
    this.currentPlanet = null;
    this.currentStation = null;
    this.currentPlatform = null;
    this.currentWreck = null;
    this.currentLootDrop = null;
    this.currentAmbientEntity = null;
    this.dockedShips = [];
    this.activeDockedTab = -1;
    this.scanData = null;
    this.isScanning = false;
    this.dockedParentPlanet = null;
    this.dockedParentStation = null;
    this.cargoData = null;
    this.isLoadingCargo = false;
    this.crewData = null;
    this.isLoadingCrew = false;
    this.storageData = null;
    this.isLoadingStorage = false;

    // Check if this is a wreck (has towStatus + destroyedAt — must check before unit since wrecks have unitType too)
    if ('towStatus' in entity && 'destroyedAt' in entity) {
      this.currentWreck = entity as SelectedWreck;
      this.displayMode = 'wreck';
      this.activeTab = 'actions';
      this.buildDisplay();
      this.show();
      return;
    }

    // Check if this is a loot drop (has exclusiveUntil + sourceUnitType)
    if ('exclusiveUntil' in entity && 'sourceUnitType' in entity) {
      this.currentLootDrop = entity;
      this.displayMode = 'loot_drop';
      this.activeTab = 'actions';
      this.buildDisplay();
      this.show();
      return;
    }

    // Check if this is a unit (has attackRange)
    if ('attackRange' in entity) {
      this.currentUnit = entity as SelectedUnit;
      const isPlayerUnit = entity.ownerId === this.mmoState.getUserId();
      this.displayMode = isPlayerUnit ? 'unit' : 'enemy_unit';
      this.activeTab = 'actions';
      if (isPlayerUnit) {
        this.lastPlayerMaxRarity = (entity as any).maxMineralRarity || 'common';
      } else {
        this.fetchOwnerInfo(entity.ownerId);
      }
      this.buildDisplay();
      this.show();
      return;
    }

    // Check if this is an orbital platform (has platformTier)
    if ('platformTier' in entity) {
      this.currentPlatform = {
        id: entity.id,
        userId: entity.userId,
        planetId: entity.planetId,
        platformTier: entity.platformTier,
        hexCoord: entity.hexCoord ?? { q: entity.positionQ, r: entity.positionR },
        currentHp: entity.currentHp,
        maxHp: entity.maxHp,
        currentShields: entity.currentShields,
        maxShields: entity.maxShields,
        mineralStorageCapacity: entity.mineralStorageCapacity,
        hasRepairBay: entity.hasRepairBay,
        hasShipyard: entity.hasShipyard,
        hasUnitBuilding: entity.hasUnitBuilding,
        hasOrbitalMining: entity.hasOrbitalMining,
        isPublic: entity.isPublic,
        status: entity.status,
      };
      this.displayMode = 'platform';
      this.activeTab = 'storage';
      this.dockedShips = this.getPlayerDockedShipsAtEntity(this.currentPlatform.id);
      this.buildDisplay();
      this.show();
      return;
    }

    // Check if this is a station (has stationType)
    if ('stationType' in entity) {
      this.currentStation = entity as SelectedStation;
      this.displayMode = 'station';
      this.activeTab = 'storage';
      // Query docked ships at this station
      this.dockedParentStation = this.currentStation;
      this.dockedShips = this.getPlayerDockedShipsAtEntity(this.currentStation.id);
      this.buildDisplay();
      this.show();
      return;
    }

    // Check if this is a planet (has planetName)
    if ('planetName' in entity) {
      this.currentPlanet = entity as SelectedPlanet;
      this.displayMode = 'planet';
      this.activeTab = 'storage';
      this.infoPanelPage = 0;  // reset pagination for new selection
      // Query docked ships at this planet
      this.dockedParentPlanet = this.currentPlanet;
      this.dockedShips = this.getPlayerDockedShipsAtEntity(this.currentPlanet.id);
      this.buildDisplay();
      this.show();
      return;
    }

    // Check if this is an asteroid (has resourceRichness)
    if ('resourceRichness' in entity) {
      this.currentAsteroid = entity as SelectedAsteroid;
      this.displayMode = 'asteroid';
      this.buildDisplay();
      this.show();
      return;
    }

    // Check if this is an ambient entity (has entityType)
    if ('entityType' in entity) {
      this.currentAmbientEntity = entity;
      this.displayMode = 'ambient_entity';
      this.activeTab = 'actions';
      this.buildDisplay();
      this.show();
      return;
    }

    // Unknown entity type
    this.hide();
  }

  /** Get player-owned units docked at a specific planet or station */
  private getPlayerDockedShipsAtEntity(entityId: string): SelectedUnit[] {
    const userId = this.mmoState.getUserId();
    const units = this.mmoState.getDockedUnitsAtPlanet(entityId);
    return units
      .filter(u => u.ownerId === userId)
      .map(u => ({
        id: u.id,
        type: u.type,
        unitType: u.type,
        shipVariant: (u as any).shipVariant || null,
        ownerId: u.ownerId,
        currentHp: u.currentHp,
        maxHp: u.maxHp,
        currentShields: u.shields,
        maxShields: u.maxShields,
        attack: u.attack,
        defense: u.defense,
        speed: u.speed,
        attackRange: u.attackRange,
        hexCoord: u.position,
        dockedAtPlanetId: u.dockedAtPlanetId,
        abilities: u.abilities || [],
        cargoCapacity: u.cargoCapacity,
        cargoUsed: u.cargoUsed,
      }));
  }

  private onDeselect(): void {
    this.currentPlanet = null;
    this.currentWreck = null;
    this.currentLootDrop = null;
    this.hide();
  }

  private onBuildQueueUpdated(): void {
    // If we're currently showing a planet with the PRODUCING tab active, refresh
    if (this.currentPlanet && this.activeTab === 'producing') {
      this.buildDisplay();
    }
  }

  /** Cargo was unloaded — refresh both storage and cargo tabs */
  private onCargoUnloaded(): void {
    this.storageData = null;
    this.isLoadingStorage = false;
    this.cargoData = null;
    this.isLoadingCargo = false;
    if ((this.displayMode === 'planet' || this.displayMode === 'station' || this.displayMode === 'platform') && this.activeTab === 'storage') {
      this.buildDisplay();
    } else if (this.displayMode === 'unit' && this.activeTab === 'cargo') {
      this.buildDisplay();
    }
  }

  private onMovementModeStarted(): void {
    this.isInMoveMode = true;
    if (this.displayMode === 'unit') this.buildDisplay();
  }

  private onMovementModeCancelled(): void {
    this.isInMoveMode = false;
    if (this.displayMode === 'unit') this.buildDisplay();
  }

  /** Fleet sidebar requests activating a specific docked ship tab */
  private onFleetActivateDockedTab(data: { unitId: string }): void {
    this.selectDockedShipTab(data.unitId);
  }

  // ── Enemy owner info fetch ────────────────────────────────

  private fetchOwnerInfo(ownerId: string): void {
    // Use cache if available
    const cached = this.ownerCache.get(ownerId);
    if (cached && this.currentUnit) {
      this.currentUnit.ownerName = cached.displayName;
      this.currentUnit.allianceTag = cached.allianceTag ?? undefined;
      this.currentUnit.allianceName = cached.allianceName ?? undefined;
      return;
    }

    const token = localStorage.getItem('crimsonmandate_token');
    fetch(`/api/profile/player/${ownerId}`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) return;
        const d = json.data;
        this.ownerCache.set(ownerId, {
          displayName: d.displayName,
          allianceTag: d.alliance?.tag ?? null,
          allianceName: d.alliance?.name ?? null,
        });
        // Update current unit if still showing this owner
        if (this.currentUnit && this.currentUnit.ownerId === ownerId && this.displayMode === 'enemy_unit') {
          this.currentUnit.ownerName = d.displayName;
          this.currentUnit.allianceTag = d.alliance?.tag ?? undefined;
          this.currentUnit.allianceName = d.alliance?.name ?? undefined;
          this.buildDisplay();
        }
      })
      .catch((err) => console.error('[CommandDock] fetchOwnerInfo error:', err));
  }

  // ── Build display ─────────────────────────────────────────

  /** Current effective panel width (varies by entity type, responsive on mobile) */
  private get currentDockW(): number {
    if (this.inSidebarMode) return MOBILE_SIDEBAR_W;
    if (this.inInfoPanelMode) return this.infoPanelW;
    if (isMobile(this.scene)) {
      return this.scene.cameras.main.width - 16;
    }
    return DOCK_W;
  }

  /** Current effective panel height (DOCK_H horizontally, dynamic in sidebar). */
  private get currentDockH(): number {
    if (this.inSidebarMode) return this.sidebarH;
    if (this.inInfoPanelMode) return this.infoPanelH;
    return DOCK_H;
  }

  /** Should a given displayMode render as the left sidebar on mobile? */
  private modeUsesSidebar(mode: typeof this.displayMode): boolean {
    if (!isMobile(this.scene)) return false;
    return mode === 'unit' || mode === 'enemy_unit';
  }

  /** Should a given displayMode render as the paginated left info panel on mobile? */
  private modeUsesInfoPanel(mode: typeof this.displayMode): boolean {
    if (!isMobile(this.scene)) return false;
    return mode === 'planet';
  }

  /** Compute clamped half-screen width for the info panel. */
  private computeInfoPanelW(): number {
    const { width } = this.scene.cameras.main;
    const half = Math.floor(width / 2);
    return Math.max(MOBILE_INFO_MIN_W, Math.min(MOBILE_INFO_MAX_W, half));
  }

  /** Compute clamped usable height for the info panel. */
  private computeInfoPanelH(): number {
    const { height } = this.scene.cameras.main;
    return Math.max(260, height - MOBILE_INFO_TOP - MOBILE_INFO_BOTTOM_PAD);
  }

  private buildDisplay(): void {
    this.hideShipPicker();
    this.contentContainer.removeAll(true);
    this.clearDockedTabs();

    // Flip to left-side layout on mobile for sidebar (unit) and info-panel (planet) modes.
    this.inSidebarMode = this.modeUsesSidebar(this.displayMode);
    this.inInfoPanelMode = this.modeUsesInfoPanel(this.displayMode);

    // Freeze info-panel dimensions up front so currentDockW/H + drawBackground
    // all agree. Height is clamped; width is half-screen.
    if (this.inInfoPanelMode) {
      this.infoPanelW = this.computeInfoPanelW();
      this.infoPanelH = this.computeInfoPanelH();
    }

    // Redraw background at the correct dimensions for this entity type.
    // Sidebar height is recomputed by buildActionButtons based on actions.length;
    // we redraw the bg again at the end with the final height.
    this.drawBackground(this.currentDockW, this.currentDockH);

    // Reposition the container for the current layout mode.
    const { width } = this.scene.cameras.main;
    if (this.inSidebarMode) {
      // Anchor to the top-left — NOT vertically centered. Centering made the
      // tab header jump every time the active tab changed; pinning to
      // MOBILE_SIDEBAR_TOP keeps the chips rock solid under the finger.
      this.setPosition(MOBILE_SIDEBAR_MARGIN_X, MOBILE_SIDEBAR_TOP);
    } else if (this.inInfoPanelMode) {
      // Left-anchored info panel. Top-align below the 44px top bar.
      this.setPosition(MOBILE_INFO_MARGIN_X, MOBILE_INFO_TOP);
    } else {
      this.setX((width - this.currentDockW) / 2);
    }

    // Check if we're showing a docked ship tab (within planet/station context)
    if (this.activeDockedTab >= 0 && this.dockedShips[this.activeDockedTab]) {
      const u = this.dockedShips[this.activeDockedTab];
      this.currentUnit = u;
      const unitType = (u.unitType || u.type || 'scout').toLowerCase();
      this.buildPortrait(unitType, u);
      this.buildInfo(unitType, u);
      this.buildStats(u);
      this.buildActionArea(u);
      // Build the docked tabs above the card
      this.buildDockedTabs();
      return;
    }

    if (this.displayMode === 'unit' && this.currentUnit) {
      const u = this.currentUnit;
      const unitType = (u.unitType || u.type || 'scout').toLowerCase();
      if (!isMobile(this.scene)) {
        this.buildPortrait(unitType, u);
        this.buildInfo(unitType, u);
        this.buildStats(u);
      }
      this.buildActionArea(u);
    } else if (this.displayMode === 'enemy_unit' && this.currentUnit) {
      const u = this.currentUnit;
      const unitType = (u.unitType || u.type || 'scout').toLowerCase();
      if (!isMobile(this.scene)) {
        this.buildPortrait(unitType, u);
        this.buildEnemyInfo(unitType, u);
        this.buildStats(u);
      }
      this.buildEnemyActions(u.hexCoord, u.ownerId);
    } else if (this.displayMode === 'platform' && this.currentPlatform) {
      this.buildPlatformDisplay(this.currentPlatform);
      this.buildDockedTabs();
    } else if (this.displayMode === 'station' && this.currentStation) {
      this.buildStationDisplay(this.currentStation);
      this.buildDockedTabs();
    } else if (this.displayMode === 'planet' && this.currentPlanet) {
      if (this.inInfoPanelMode) {
        this.buildMobilePlanetPanel(this.currentPlanet);
      } else {
        this.buildPlanetDisplay(this.currentPlanet);
        this.buildDockedTabs();
      }
    } else if (this.displayMode === 'asteroid' && this.currentAsteroid) {
      this.buildAsteroidDisplay(this.currentAsteroid);
    } else if (this.displayMode === 'wreck' && this.currentWreck) {
      this.buildWreckDisplay(this.currentWreck);
    } else if (this.displayMode === 'loot_drop' && this.currentLootDrop) {
      this.buildLootDropDisplay(this.currentLootDrop);
    } else if (this.displayMode === 'ambient_entity' && this.currentAmbientEntity) {
      this.buildAmbientEntityDisplay(this.currentAmbientEntity);
    }
  }

  // ── Ship portrait + HP/SH bars underneath ──────────────────

  private buildPortrait(unitType: string, u: SelectedUnit): void {
    const iconKey = SHIP_ICON_MAP[unitType] || SHIP_ICON_MAP['scout'];
    if (this.scene.textures.exists(iconKey)) {
      const img = this.scene.add.image(
        PORT_LEFT + PORT_W / 2,
        PORT_TOP + PORT_H / 2,
        iconKey,
      );
      img.setDisplaySize(PORT_W, PORT_H);
      this.contentContainer.add(img);
    } else {
      const fallback = this.scene.add.text(
        PORT_LEFT + PORT_W / 2, PORT_TOP + PORT_H / 2, '🚀',
        { fontSize: '32px' },
      );
      fallback.setOrigin(0.5);
      this.contentContainer.add(fallback);
    }

    // HP and SH side by side under portrait: label+value above, bar below
    // Aligned with STATS_LEFT so HP bar starts at same x as MOVEMENT label
    const barStartX = STATS_LEFT;
    const textY = PORT_TOP + PORT_H + 4;    // label row
    const barY = textY + 14;                // bar directly under text
    const barH = 10;
    const barW = 150;                       // bar width (2 bars + gap must fit before ACTION_ROW_LEFT)
    const gap = 12;                         // gap between HP and SH groups

    // HP group
    this.drawStatBarAbove(barStartX, textY, barY, barW, barH,
      u.currentHp, u.maxHp, 0x00ff88, I18n.t('stat.health'), I18n.t('tooltip.hull_integrity'), 'health');

    // SH group (right of HP) — always shown for consistency
    this.drawStatBarAbove(barStartX + barW + gap, textY, barY, barW, barH,
      u.currentShields, u.maxShields || 1, 0x4488ff, I18n.t('stat.shields'), I18n.t('tooltip.shields_desc'), 'health');

  }

  /** Format a number with commas for readability */
  private static fmtNum(n: number): string {
    return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  /** Draw a stat bar with label + value above and a clean bar below, with tooltip.
   *  barType controls color behaviour:
   *    'health'  – green when healthy, amber <50%, red <25%
   *    'storage' – blue when low, amber >70%, red >90%
   *    'default' – uses supplied color, fades to amber/red when low
   */
  private drawStatBarAbove(
    x: number, textY: number, barY: number,
    barW: number, barH: number,
    current: number, max: number, color: number,
    label: string, tooltip?: string,
    barType: 'health' | 'storage' | 'default' = 'default',
  ): void {
    const pct = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;

    // Label (muted grey, left-aligned)
    const lbl = this.scene.add.text(x, textY, label, {
      fontSize: '10px',
      fontStyle: '900',
      color: '#667799',
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    lbl.setOrigin(0, 0);
    this.contentContainer.add(lbl);

    // Value (bright, right-aligned above bar) with comma formatting
    const valStr = `${CommandDock.fmtNum(Math.round(current))}/${CommandDock.fmtNum(Math.round(max))}`;
    const valText = this.scene.add.text(x + barW, textY, valStr, {
      fontSize: '10px',
      fontStyle: '900',
      color: '#E0E8FF',
      fontFamily: Fonts.heading,
    });
    valText.setOrigin(1, 0);
    this.contentContainer.add(valText);

    // Bar background (slightly brighter so consumed portion is visible)
    const g = this.scene.add.graphics();
    g.fillStyle(0x1a2a3a, 0.9);
    g.fillRoundedRect(x, barY, barW, barH, 3);

    // Bar fill — color depends on barType
    if (pct > 0) {
      let fillColor: number;
      if (barType === 'health') {
        // Green > 50%, amber 25-50%, red < 25%
        fillColor = pct > 0.5 ? 0x00ff88 : pct > 0.25 ? 0xffaa44 : 0xff4444;
      } else if (barType === 'storage') {
        // Blue when low, amber > 70%, red > 90%
        fillColor = pct > 0.9 ? 0xff4444 : pct > 0.7 ? 0xffaa44 : 0x4488ff;
      } else {
        // Default: use provided color, fade when low
        fillColor = pct > 0.5 ? color : pct > 0.25 ? 0xffaa44 : 0xff4444;
      }
      g.fillStyle(fillColor, 0.9);
      g.fillRoundedRect(x, barY, barW * pct, barH, 3);
    }
    this.contentContainer.add(g);

    // Tooltip hit zone over the whole bar group
    if (tooltip) {
      const hitH = (barY + barH) - textY;
      const hitZone = this.scene.add.zone(x, textY, barW, hitH).setOrigin(0, 0);
      hitZone.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, barW, hitH),
        Phaser.Geom.Rectangle.Contains,
      );
      this.contentContainer.add(hitZone);

      hitZone.on('pointerover', () => {
        this.showTooltip(x + barW / 2, textY - 4, tooltip);
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        this.hideTooltip();
        this.scene.input.setDefaultCursor('default');
      });
    }
  }

  // ── Name + Owner/Facing/Coords (compact) ──────────────────

  private buildInfo(unitType: string, u: SelectedUnit): void {
    // Ship variant name — use actual variant if available, otherwise default Mk1
    const shipVariant = (u as any).shipVariant as string | undefined;
    const variantDisplay = shipVariant
      ? shipVariant.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
      : null;
    const variantName = u.customName || variantDisplay || DEFAULT_VARIANT_NAME[unitType] || I18n.shipName(unitType);
    const name = this.scene.add.text(NAME_LEFT, NAME_TOP - 4, variantName, {
      fontSize: '18px',
      fontStyle: '900',
      color: '#E0E8FF',
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    name.setOrigin(0, 0.5);
    this.contentContainer.add(name);

    // Class subtitle below variant name
    const className = I18n.shipName(unitType);
    const classLabel = this.scene.add.text(NAME_LEFT, NAME_TOP + 12, `Class: ${className}`, {
      fontSize: '10px',
      color: '#6677AA',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    classLabel.setOrigin(0, 0.5);
    this.contentContainer.add(classLabel);

    // Ship Progress icon (to the right of name)
    const iconX = NAME_LEFT + name.width + 14;
    const iconY = NAME_TOP - 4;
    this.createSmallImageIcon(iconX, iconY, 'icon-ship-progress', 'Ship Progress', () => {
      EventBus.emit('open_ship_progression', { unitId: u.id });
    });
    // Components icon (18px icon + 10px gap = 28px offset)
    const compIconX = iconX + 28;
    this.createSmallImageIcon(compIconX, iconY, 'icon-components', 'Components', () => {
      EventBus.emit('open_component_upgrade', { unitId: u.id });
    });

    // Red warning dot on components icon when laser condition < 20%
    const laserTier = u.laserTier;
    const laserCond = u.laserConditionPercent ?? 100;
    if (laserTier && laserTier !== 'mk0' && laserCond < 20) {
      const dotX = compIconX + 18;
      const dotY = iconY - 7;
      const dot = this.scene.add.graphics();
      dot.fillStyle(0xff2222, 1);
      dot.fillCircle(dotX, dotY, 4);
      dot.lineStyle(1, 0x000000, 0.6);
      dot.strokeCircle(dotX, dotY, 4);
      this.contentContainer.add(dot);
      const bang = this.scene.add.text(dotX, dotY, '!', {
        fontSize: '7px', fontStyle: '900', color: '#ffffff', fontFamily: Fonts.heading,
      });
      bang.setOrigin(0.5, 0.5);
      this.contentContainer.add(bang);
    }

    // Owner display name (clickable) + Direction + Coords
    const playerName = u.ownerName || localStorage.getItem('crimsonmandate_display_name') || u.ownerId.substring(0, 7) + '...';
    const facingDir = FACING_LABELS[u.facing ?? 0] || 'E';

    // Clickable player name
    const ownerLink = this.scene.add.text(OWNER_LEFT, OWNER_TOP, playerName, {
      fontSize: '10px',
      color: '#44AAFF',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    ownerLink.setOrigin(0, 0);
    ownerLink.setInteractive({ useHandCursor: true });
    ownerLink.on('pointerover', () => ownerLink.setColor('#77CCFF'));
    ownerLink.on('pointerout', () => ownerLink.setColor('#44AAFF'));
    ownerLink.on('pointerdown', () => {
      window.open(`/profile.html?id=${u.ownerId}`, '_blank');
    });
    this.contentContainer.add(ownerLink);

    // Direction + Coords after player name
    const separatorX = OWNER_LEFT + ownerLink.width + 4;
    const coordStr = `|  ${facingDir}  |  (${u.hexCoord.q}, ${u.hexCoord.r})`;
    const coordLine = this.scene.add.text(separatorX, OWNER_TOP, coordStr, {
      fontSize: '10px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    coordLine.setOrigin(0, 0);
    this.contentContainer.add(coordLine);
  }

  /** Enemy ship info — same layout as player ship but red-tinted name, owner name link, no action icons */
  private buildEnemyInfo(unitType: string, u: SelectedUnit): void {
    // Variant name — use actual variant if available
    const shipVariant = (u as any).shipVariant as string | undefined;
    const variantDisplay = shipVariant
      ? shipVariant.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
      : null;
    const variantName = u.customName || variantDisplay || DEFAULT_VARIANT_NAME[unitType] || I18n.shipName(unitType);
    const name = this.scene.add.text(NAME_LEFT, NAME_TOP - 4, variantName, {
      fontSize: '18px',
      fontStyle: '900',
      color: '#FF8866',
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    name.setOrigin(0, 0.5);
    this.contentContainer.add(name);

    // Class subtitle below variant name
    const className = I18n.shipName(unitType);
    const classLabel = this.scene.add.text(NAME_LEFT, NAME_TOP + 12, `Class: ${className}`, {
      fontSize: '10px',
      color: '#6677AA',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    classLabel.setOrigin(0, 0.5);
    this.contentContainer.add(classLabel);

    // Owner name next to ship type (clickable link)
    const ownerDisplay = u.ownerName || u.ownerId.substring(0, 8) + '...';
    const ownerLink = this.scene.add.text(NAME_LEFT + name.width + 10, NAME_TOP - 4, ownerDisplay, {
      fontSize: '12px',
      fontStyle: '900',
      color: '#44AAFF',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    ownerLink.setOrigin(0, 0.5);
    ownerLink.setInteractive({ useHandCursor: true });
    ownerLink.on('pointerover', () => { ownerLink.setColor('#88CCFF'); this.scene.input.setDefaultCursor('pointer'); });
    ownerLink.on('pointerout', () => { ownerLink.setColor('#44AAFF'); this.scene.input.setDefaultCursor('default'); });
    ownerLink.on('pointerdown', () => { EventBus.emit('open-enemy-profile', { userId: u.ownerId }); });
    this.contentContainer.add(ownerLink);

    // Alliance tag pill (e.g. VDR) — positioned after owner name
    let badgeX = NAME_LEFT + name.width + 10 + ownerLink.width + 8;

    // Alliance tag pill (e.g. VDR)
    if (u.allianceTag) {
      const tagStr = u.allianceTag;
      const tagText = this.scene.add.text(0, 0, tagStr, {
        fontSize: '9px',
        fontStyle: '900',
        color: '#FFCC00',
        fontFamily: Fonts.heading,
        letterSpacing: 1,
      });
      const pillW = tagText.width + 10;
      const pillH = tagText.height + 6;
      const pillY = NAME_TOP - pillH / 2;

      const pillGfx = this.scene.add.graphics();
      pillGfx.lineStyle(1, 0xffcc00, 0.5);
      pillGfx.strokeRoundedRect(badgeX, pillY, pillW, pillH, 6);
      pillGfx.fillStyle(0xffcc00, 0.08);
      pillGfx.fillRoundedRect(badgeX, pillY, pillW, pillH, 6);
      this.contentContainer.add(pillGfx);

      tagText.setPosition(badgeX + 5, pillY + 3);
      tagText.setOrigin(0, 0);
      this.contentContainer.add(tagText);

      // Tooltip for alliance name
      if (u.allianceName) {
        const hit = this.scene.add.zone(badgeX, pillY, pillW, pillH).setOrigin(0, 0);
        hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, pillW, pillH), Phaser.Geom.Rectangle.Contains);
        this.contentContainer.add(hit);
        hit.on('pointerover', () => {
          this.showTooltip(badgeX + pillW / 2, pillY - 4, u.allianceName!);
          this.scene.input.setDefaultCursor('pointer');
        });
        hit.on('pointerout', () => {
          this.hideTooltip();
          this.scene.input.setDefaultCursor('default');
        });
      }
    }

    // Owner name (clickable link to profile)
    const ownerStr = u.ownerName || 'Loading...';
    const ownerText = this.scene.add.text(OWNER_LEFT, OWNER_TOP, ownerStr, {
      fontSize: '10px',
      fontStyle: '900',
      color: '#44AAFF',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    ownerText.setOrigin(0, 0);
    ownerText.setInteractive({ useHandCursor: true });
    ownerText.on('pointerover', () => { ownerText.setColor('#88CCFF'); this.scene.input.setDefaultCursor('pointer'); });
    ownerText.on('pointerout', () => { ownerText.setColor('#44AAFF'); this.scene.input.setDefaultCursor('default'); });
    ownerText.on('pointerdown', () => { EventBus.emit('open-enemy-profile', { userId: u.ownerId }); });
    this.contentContainer.add(ownerText);

    // Facing + coords after owner name
    const facingDir = FACING_LABELS[u.facing ?? 0] || 'E';
    const coordStr = `  |  ${facingDir}  |  (${u.hexCoord.q}, ${u.hexCoord.r})`;
    const coordLine = this.scene.add.text(OWNER_LEFT + ownerText.width, OWNER_TOP, coordStr, {
      fontSize: '10px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    coordLine.setOrigin(0, 0);
    this.contentContainer.add(coordLine);
  }

  // ── Enemy contextual actions (Message / Move To / Attack) ──

  private buildEnemyActions(_targetHex: { q: number; r: number }, targetOwnerId: string): void {
    const targetShipId = this.currentUnit?.id;
    const actions = [
      {
        label: I18n.t('enemy.player'),
        tooltip: 'View this player\'s profile',
        color: 0x44aaff,
        colorCSS: '#44AAFF',
        action: () => {
          EventBus.emit('open-enemy-profile', { userId: targetOwnerId });
        },
      },
      {
        label: I18n.t('enemy.ship'),
        tooltip: 'View this ship\'s details',
        color: 0xbb88ff,
        colorCSS: '#BB88FF',
        action: () => {
          if (targetShipId) {
            EventBus.emit('open-enemy-ship', { userId: targetOwnerId, shipId: targetShipId });
          }
        },
      },
    ];

    // Mobile layout: pills live on a fixed bottom-center bar so they're always
    // in the same thumb-reachable spot regardless of where the ship is tapped.
    // Desktop layout: pills inline with the HEALTH/SHIELDS bar row inside the dock.
    const mobile = isMobile(this.scene);
    const pillGap = mobile ? 12 : 8;
    const padH    = mobile ? 16 : 10;
    const padV    = mobile ? 10 : 6;
    const borderR = mobile ? 14 : 10;
    const fontPx  = mobile ? 13 : 9;

    // First pass: create text objects to measure pill widths
    type PillSpec = typeof actions[number] & {
      text: Phaser.GameObjects.Text;
      w: number;
      h: number;
    };
    const specs: PillSpec[] = [];
    let maxH = 0;
    for (const act of actions) {
      const t = this.scene.add.text(0, 0, act.label, {
        fontSize: `${fontPx}px`,
        fontStyle: '900',
        color: act.colorCSS,
        fontFamily: Fonts.heading,
        letterSpacing: 2,
      });
      const w = t.width + padH * 2;
      const h = t.height + padV * 2;
      if (h > maxH) maxH = h;
      specs.push({ ...act, text: t, w, h });
    }

    // Compute the starting (top-left) position of the first pill in
    // contentContainer-local coordinates.
    let startX: number;
    let startY: number;
    if (mobile) {
      // Screen-space bottom-center. Translate to content-container-local
      // coordinates by subtracting the CommandDock's (this.x, this.y).
      const { width: sw, height: sh } = this.scene.cameras.main;
      const totalW = specs.reduce((a, s) => a + s.w, 0) + pillGap * (specs.length - 1);
      const MOBILE_PILL_BOTTOM_PAD = 28; // distance from bottom of viewport
      const scrX = (sw - totalW) / 2;
      const scrY = sh - MOBILE_PILL_BOTTOM_PAD - maxH;
      startX = scrX - this.x;
      startY = scrY - this.y;
    } else {
      // Desktop: align with the HEALTH/SHIELDS bar row inside the dock
      startX = TAB_ACTIONS_LEFT;
      startY = PORT_TOP + PORT_H + 4;
    }

    let px = startX;
    for (const s of specs) {
      const pillW = s.w;
      const pillH = s.h;
      const pillX = px;
      const pillY = startY;

      const gfx = this.scene.add.graphics();
      gfx.lineStyle(mobile ? 2 : 1.5, s.color, mobile ? 0.9 : 0.5);
      gfx.strokeRoundedRect(pillX, pillY, pillW, pillH, borderR);
      gfx.fillStyle(s.color, mobile ? 0.18 : 0.06);
      gfx.fillRoundedRect(pillX, pillY, pillW, pillH, borderR);
      this.contentContainer.add(gfx);

      s.text.setPosition(pillX + padH, pillY + padV);
      s.text.setOrigin(0, 0);
      this.contentContainer.add(s.text);

      const hit = this.scene.add.zone(pillX, pillY, pillW, pillH).setOrigin(0, 0);
      hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, pillW, pillH), Phaser.Geom.Rectangle.Contains);
      this.contentContainer.add(hit);

      const act = s;
      const tooltip = act.tooltip;
      const clickFn = act.action;
      hit.on('pointerover', () => {
        act.text.setColor('#FFFFFF');
        gfx.clear();
        gfx.lineStyle(mobile ? 2 : 1.5, act.color, 1);
        gfx.strokeRoundedRect(pillX, pillY, pillW, pillH, borderR);
        gfx.fillStyle(act.color, mobile ? 0.32 : 0.15);
        gfx.fillRoundedRect(pillX, pillY, pillW, pillH, borderR);
        this.showTooltip(pillX + pillW / 2, pillY - 4, tooltip);
        this.scene.input.setDefaultCursor('pointer');
      });
      hit.on('pointerout', () => {
        act.text.setColor(act.colorCSS);
        gfx.clear();
        gfx.lineStyle(mobile ? 2 : 1.5, act.color, mobile ? 0.9 : 0.5);
        gfx.strokeRoundedRect(pillX, pillY, pillW, pillH, borderR);
        gfx.fillStyle(act.color, mobile ? 0.18 : 0.06);
        gfx.fillRoundedRect(pillX, pillY, pillW, pillH, borderR);
        this.hideTooltip();
        this.scene.input.setDefaultCursor('default');
      });
      hit.on('pointerdown', clickFn);

      px += pillW + pillGap;
    }
  }

  // ── Ship picker overlay ────────────────────────────────────

  private shipPickerContainer: Phaser.GameObjects.Container | null = null;

  // @ts-ignore -- kept for future use (move-to / attack enemy actions)
  private showShipPicker(mode: 'move' | 'attack', targetHex: { q: number; r: number }): void {
    this.hideShipPicker();

    const units = this.mmoState.getPlayerUnits();
    if (units.length === 0) {
      this.showTooltip(DOCK_W / 2, -10, 'You have no ships');
      return;
    }

    const container = this.scene.add.container(0, 0);
    container.setDepth(9999);

    // Panel dimensions
    const panelW = DOCK_W;
    const rowH = 44;
    const headerH = 36;
    const panelH = headerH + units.length * rowH + 12;

    // Position above the dock
    container.setY(-panelH - 6);

    // Background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0a0e1e, 0.96);
    bg.fillRoundedRect(0, 0, panelW, panelH, 6);
    bg.lineStyle(2, mode === 'attack' ? 0xff4444 : 0x00ff41, 0.4);
    bg.strokeRoundedRect(0, 0, panelW, panelH, 6);
    container.add(bg);

    // Header
    const titleColor = mode === 'attack' ? '#FF4444' : '#00FF41';
    const titleText = mode === 'attack' ? I18n.t('action.select_ship_attack') : I18n.t('action.select_ship_move');
    const header = this.scene.add.text(16, headerH / 2, titleText, {
      fontSize: '11px',
      fontStyle: '900',
      color: titleColor,
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    header.setOrigin(0, 0.5);
    container.add(header);

    // Close button
    const closeText = this.scene.add.text(panelW - 16, headerH / 2, I18n.t('action.cancel'), {
      fontSize: '9px',
      fontStyle: '900',
      color: '#8899BB',
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    closeText.setOrigin(1, 0.5);
    container.add(closeText);
    const closeHit = this.scene.add.zone(panelW - 60, 0, 60, headerH).setOrigin(0, 0);
    closeHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 60, headerH), Phaser.Geom.Rectangle.Contains);
    closeHit.on('pointerdown', () => this.hideShipPicker());
    closeHit.on('pointerover', () => { closeText.setColor('#FFFFFF'); this.scene.input.setDefaultCursor('pointer'); });
    closeHit.on('pointerout', () => { closeText.setColor('#8899BB'); this.scene.input.setDefaultCursor('default'); });
    container.add(closeHit);

    // Divider
    const div = this.scene.add.graphics();
    div.lineStyle(1, 0x334466, 0.4);
    div.lineBetween(12, headerH, panelW - 12, headerH);
    container.add(div);

    // Ship rows
    units.forEach((unit, idx) => {
      const ry = headerH + idx * rowH + 6;
      const unitType = (unit.type || 'scout').toLowerCase();
      const displayName = I18n.shipName(unitType);

      // Row background (alternating)
      const rowBg = this.scene.add.graphics();
      if (idx % 2 === 0) {
        rowBg.fillStyle(0x1a2240, 0.4);
        rowBg.fillRect(8, ry, panelW - 16, rowH - 4);
      }
      container.add(rowBg);

      // Ship icon
      const iconKey = SHIP_ICON_MAP[unitType] || SHIP_ICON_MAP['scout'];
      if (this.scene.textures.exists(iconKey)) {
        const icon = this.scene.add.image(36, ry + (rowH - 4) / 2, iconKey);
        icon.setDisplaySize(32, 18);
        container.add(icon);
      }

      // Ship name + coords
      const nameText = this.scene.add.text(62, ry + 8, displayName, {
        fontSize: '12px',
        fontStyle: '900',
        color: '#E0E8FF',
        fontFamily: Fonts.heading,
      });
      container.add(nameText);

      const coordText = this.scene.add.text(62, ry + 24, `(${unit.position.q}, ${unit.position.r})`, {
        fontSize: '9px',
        color: '#667799',
        fontFamily: Fonts.body,
      });
      container.add(coordText);

      // HP indicator
      const hpPct = unit.maxHp > 0 ? unit.currentHp / unit.maxHp : 0;
      const hpColor = hpPct > 0.5 ? '#00FF88' : hpPct > 0.25 ? '#FFAA00' : '#FF4444';
      const hpText = this.scene.add.text(200, ry + (rowH - 4) / 2, `HP ${Math.round(hpPct * 100)}%`, {
        fontSize: '10px',
        color: hpColor,
        fontFamily: Fonts.body,
      });
      hpText.setOrigin(0, 0.5);
      container.add(hpText);

      // Action pill
      const actionLabel = mode === 'attack' ? I18n.t('action.attack') : I18n.t('action.move');
      const actionColor = mode === 'attack' ? 0xff4444 : 0x00ff41;
      const actionCSS = mode === 'attack' ? '#FF4444' : '#00FF41';

      const actionText = this.scene.add.text(0, 0, actionLabel, {
        fontSize: '9px',
        fontStyle: '900',
        color: actionCSS,
        fontFamily: Fonts.heading,
        letterSpacing: 1,
      });
      const aPillW = actionText.width + 20;
      const aPillH = 24;
      const aPillX = panelW - aPillW - 20;
      const aPillY = ry + (rowH - 4) / 2 - aPillH / 2;

      const aPillGfx = this.scene.add.graphics();
      aPillGfx.lineStyle(1.5, actionColor, 0.5);
      aPillGfx.strokeRoundedRect(aPillX, aPillY, aPillW, aPillH, 6);
      aPillGfx.fillStyle(actionColor, 0.06);
      aPillGfx.fillRoundedRect(aPillX, aPillY, aPillW, aPillH, 6);
      container.add(aPillGfx);

      actionText.setPosition(aPillX + 10, aPillY + aPillH / 2);
      actionText.setOrigin(0, 0.5);
      container.add(actionText);

      // Row hit zone
      const rowHit = this.scene.add.zone(8, ry, panelW - 16, rowH - 4).setOrigin(0, 0);
      rowHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, panelW - 16, rowH - 4), Phaser.Geom.Rectangle.Contains);
      container.add(rowHit);

      rowHit.on('pointerover', () => {
        rowBg.clear();
        rowBg.fillStyle(actionColor, 0.1);
        rowBg.fillRect(8, ry, panelW - 16, rowH - 4);
        aPillGfx.clear();
        aPillGfx.lineStyle(1.5, actionColor, 1);
        aPillGfx.strokeRoundedRect(aPillX, aPillY, aPillW, aPillH, 6);
        aPillGfx.fillStyle(actionColor, 0.15);
        aPillGfx.fillRoundedRect(aPillX, aPillY, aPillW, aPillH, 6);
        this.scene.input.setDefaultCursor('pointer');
      });
      rowHit.on('pointerout', () => {
        rowBg.clear();
        if (idx % 2 === 0) {
          rowBg.fillStyle(0x1a2240, 0.4);
          rowBg.fillRect(8, ry, panelW - 16, rowH - 4);
        }
        aPillGfx.clear();
        aPillGfx.lineStyle(1.5, actionColor, 0.5);
        aPillGfx.strokeRoundedRect(aPillX, aPillY, aPillW, aPillH, 6);
        aPillGfx.fillStyle(actionColor, 0.06);
        aPillGfx.fillRoundedRect(aPillX, aPillY, aPillW, aPillH, 6);
        this.scene.input.setDefaultCursor('default');
      });
      rowHit.on('pointerdown', () => {
        if (mode === 'move') {
          // Emit move command: move this unit toward the target hex
          EventBus.emit('mmo-action-move-to', { unitId: unit.id, targetHex });
        } else {
          // Emit follow/attack command: pursue and attack target
          EventBus.emit('mmo-action-attack-target', { unitId: unit.id, targetHex });
        }
        this.hideShipPicker();
      });
    });

    this.contentContainer.add(container);
    this.shipPickerContainer = container;
  }

  private hideShipPicker(): void {
    if (this.shipPickerContainer) {
      this.shipPickerContainer.destroy(true);
      this.shipPickerContainer = null;
      this.scene.input.setDefaultCursor('default');
    }
  }

  private createSmallImageIcon(
    x: number, y: number, imageKey: string,
    tooltipText: string,
    onClick: () => void,
  ): void {
    const size = 18;
    const hitPad = 4;  // extra padding around icon for easier clicking

    if (this.scene.textures.exists(imageKey)) {
      const icon = this.scene.add.image(x + size / 2, y, imageKey);
      icon.setDisplaySize(size, size);
      icon.setOrigin(0.5, 0.5);
      this.contentContainer.add(icon);

      // Use a zone for reliable hit detection (avoids origin offset issues)
      const hitZone = this.scene.add.zone(
        x + size / 2, y, size + hitPad * 2, size + hitPad * 2,
      );
      hitZone.setOrigin(0.5, 0.5);
      hitZone.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, size + hitPad * 2, size + hitPad * 2),
        Phaser.Geom.Rectangle.Contains,
      );
      this.contentContainer.add(hitZone);

      hitZone.on('pointerover', () => {
        icon.setAlpha(1);
        this.showTooltip(x + size / 2, NAME_TOP - 16, tooltipText);
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        icon.setAlpha(0.7);
        this.hideTooltip();
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        onClick();
      });
      icon.setAlpha(0.7);
    } else {
      // Fallback to text icon
      const fallback = this.scene.add.text(x, y, '⚙', { fontSize: '14px' });
      fallback.setOrigin(0, 0.5);
      fallback.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, fallback.width + hitPad * 2, fallback.height + hitPad * 2),
        Phaser.Geom.Rectangle.Contains,
      );
      this.contentContainer.add(fallback);
      fallback.on('pointerover', () => {
        fallback.setAlpha(1);
        this.showTooltip(x + 8, NAME_TOP - 16, tooltipText);
        this.scene.input.setDefaultCursor('pointer');
      });
      fallback.on('pointerout', () => {
        fallback.setAlpha(0.7);
        this.hideTooltip();
        this.scene.input.setDefaultCursor('default');
      });
      fallback.on('pointerdown', () => onClick());
      fallback.setAlpha(0.7);
    }
  }

  // ── Asteroid display ─────────────────────────────────────

  private buildAsteroidDisplay(a: SelectedAsteroid): void {
    const isCrypto = a.asteroidType === 'crypto' || a.asteroidType === 'golden' || a.asteroidType === 'special';
    const hasReward = isCrypto;
    const tier = getRichnessTier(a.resourceRichness);

    // Pick image: golden/special variants first, then richness-based
    let imageKey: string;
    if (isCrypto && a.variant) {
      imageKey = ASTEROID_IMAGE_MAP[a.variant] || 'asteroid-gold';
    } else if (isCrypto) {
      imageKey = 'asteroid-gold';
    } else {
      imageKey = tier.imageKey;
    }

    // Portrait image (fit inside box, preserve aspect ratio)
    if (this.scene.textures.exists(imageKey)) {
      const img = this.scene.add.image(
        PORT_LEFT + PORT_W / 2,
        PORT_TOP + PORT_H / 2,
        imageKey,
      );
      const texW = img.width;
      const texH = img.height;
      const scale = Math.min(PORT_W / texW, PORT_H / texH);
      img.setScale(scale);
      this.contentContainer.add(img);
    } else {
      const fallback = this.scene.add.text(
        PORT_LEFT + PORT_W / 2, PORT_TOP + PORT_H / 2, '☄️',
        { fontSize: '32px' },
      );
      fallback.setOrigin(0.5);
      this.contentContainer.add(fallback);
    }

    // Name — tier for regular, variant for golden
    const VARIANT_NAMES: Record<string, string> = {
      common_gold: 'Gold',
      rare_platinum: 'Platinum',
      legendary_dark_matter: 'Dark Matter',
    };
    let displayName: string;
    if (isCrypto) {
      const tokenName = (a as any).cryptoToken || (a as any).tokenName || 'USDC';
      const material = VARIANT_NAMES[a.variant || 'common_gold'] || '';
      displayName = material ? `${material} ${tokenName} Asteroid` : `${tokenName} Crypto Asteroid`;
    } else {
      displayName = `${tier.label} Asteroid`;
    }

    const name = this.scene.add.text(NAME_LEFT, NAME_TOP, displayName, {
      fontSize: '20px',
      fontStyle: '900',
      color: hasReward ? '#FFD700' : tier.color,
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    name.setOrigin(0, 0.5);
    this.contentContainer.add(name);

    // Location line
    const infoStr = `(${a.hexCoord.q}, ${a.hexCoord.r})`;
    const infoLine = this.scene.add.text(OWNER_LEFT, OWNER_TOP, infoStr, {
      fontSize: '10px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    infoLine.setOrigin(0, 0);
    this.contentContainer.add(infoLine);

    // Remaining Ore bar (left side, under portrait)
    const barStartX = STATS_LEFT;
    const textY = PORT_TOP + PORT_H + 4;
    const barY = textY + 14;
    const barH = 10;
    const barW = TAB_ACTIONS_LEFT - STATS_LEFT - 30;

    let remaining: number;
    let oreMax: number;
    if (a.mineralComposition && Object.keys(a.mineralComposition).length > 0) {
      remaining = Object.values(a.mineralComposition).reduce((sum, v) => sum + (Number(v) || 0), 0);
      if (Math.ceil(remaining) > a.maxStorage) {
        a.maxStorage = Math.ceil(remaining);
      }
      oreMax = a.maxStorage;
    } else {
      remaining = a.maxStorage - a.storedResources;
      oreMax = a.maxStorage;
    }
    if (isCrypto) {
      // Crypto asteroids: show token value remaining
      const tokenName = (a as any).cryptoToken || (a as any).tokenName || 'USDC';
      const tokenChain = (a as any).cryptoChain || (a as any).tokenChain || '';
      const rv = Number(a.rewardValue) || 0;
      const chainSuffix = tokenChain ? ` (${tokenChain})` : '';
      const isStable = ['USDC', 'USDT', 'GUSDC', 'GUSDT'].includes(tokenName);
      const tokenLabel = isStable ? `$${rv.toFixed(2)} ${tokenName}${chainSuffix}` : `${rv.toLocaleString()} ${tokenName}${chainSuffix}`;
      const tokenText = this.scene.add.text(barStartX, textY + 2, tokenLabel, {
        fontSize: '12px', fontStyle: '900', color: '#FFD700', fontFamily: Fonts.heading,
      });
      tokenText.setOrigin(0, 0);
      this.contentContainer.add(tokenText);

      // Show required component if set
      const reqCompName = (a as any).requiredComponentName;
      if (reqCompName) {
        const reqText = this.scene.add.text(barStartX, textY + 16, `Requires: ${reqCompName}`, {
          fontSize: '10px', color: '#ffcc00', fontFamily: Fonts.body,
        });
        reqText.setOrigin(0, 0);
        this.contentContainer.add(reqText);
      }
    } else if (oreMax > 0) {
      const orePct = remaining / oreMax;
      const oreColor = orePct <= 0.1 ? 0xff4444 : orePct <= 0.3 ? 0xffaa00 : 0x00ccff;
      this.drawStatBarAbove(barStartX, textY, barY, barW, barH,
        Math.round(remaining), oreMax, oreColor,
        I18n.t('asteroid.remaining_ore'), getAsteroidStatTooltips()['REMAINING ORE']);
    }

    // Stats row at bottom
    const rarity = getAsteroidRarity(a);
    let rewardDisplay: string;
    let rewardTooltip: string;
    if (isCrypto) {
      const tokenName = (a as any).cryptoToken || (a as any).tokenName || 'USDC';
      const rv = Number(a.rewardValue) || 0;
      const isStableStat = ['USDC', 'USDT', 'GUSDC', 'GUSDT'].includes(tokenName);
      rewardDisplay = rv > 0 ? (isStableStat ? `$${rv.toFixed(2)}` : `${rv.toLocaleString()}`) : tokenName;
      rewardTooltip = `This asteroid contains ${rv > 0 ? rv.toLocaleString() + ' ' : ''}${tokenName} crypto tokens`;
    } else {
      rewardDisplay = 'No';
      rewardTooltip = I18n.t('tooltip.asteroid_no_stellar');
    }
    const rewardColor = hasReward ? '#FFD700' : '#556677';
    const asteroidTips = getAsteroidStatTooltips();
    const rewardLabel = isCrypto ? ((a as any).cryptoToken || (a as any).tokenName || 'CRYPTO') : I18n.t('asteroid.stellar_label');
    const stats: { label: string; value: string; tooltip?: string; color?: string }[] = [
      { label: I18n.t('asteroid.richness'), value: tier.label, tooltip: asteroidTips['RICHNESS'], color: tier.color },
      { label: I18n.t('asteroid.rarity'), value: rarity.label, tooltip: asteroidTips['RARITY'], color: rarity.color },
      { label: rewardLabel, value: rewardDisplay, tooltip: rewardTooltip, color: rewardColor },
    ];

    const statsY = DOCK_H - STATS_BOTTOM;
    let sx = STATS_LEFT;

    for (const stat of stats) {
      const groupX = sx;

      const lbl = this.scene.add.text(sx, statsY, stat.label, {
        fontSize: '11px', color: '#8899BB', fontFamily: Fonts.body, letterSpacing: 2,
      });
      lbl.setOrigin(0, 1);
      this.contentContainer.add(lbl);

      const val = this.scene.add.text(sx + lbl.width + STAT_VALUE_GAP, statsY, stat.value, {
        fontSize: '11px', fontStyle: '900', color: stat.color || '#00FFFF', fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 1);
      this.contentContainer.add(val);

      const groupW = lbl.width + STAT_VALUE_GAP + val.width;

      if (stat.tooltip) {
        const hitH = 16;
        const hitZone = this.scene.add.zone(groupX, statsY - hitH, groupW, hitH).setOrigin(0, 0);
        hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, groupW, hitH), Phaser.Geom.Rectangle.Contains);
        this.contentContainer.add(hitZone);
        const tipText = stat.tooltip;
        hitZone.on('pointerover', () => { this.showTooltip(groupX + groupW / 2, statsY - hitH - 4, tipText); this.scene.input.setDefaultCursor('pointer'); });
        hitZone.on('pointerout', () => { this.hideTooltip(); this.scene.input.setDefaultCursor('default'); });
      }

      sx += groupW + STAT_GAP;
    }

    // ── Right-side: Composition ──
    if (a.mineralComposition && Object.keys(a.mineralComposition).length > 0) {
      this.buildAsteroidComposition(a);
    }

    // ── Miners section (crypto asteroids only) ──
    if (isCrypto) {
      this.fetchAsteroidMiners(a.id);
    }
  }

  // ── Asteroid miners fetch + display ──────────────────────────

  private fetchAsteroidMiners(asteroidId: string): void {
    const token = localStorage.getItem('crimsonmandate_token');
    fetch(`/api/minerals/asteroid-miners/${asteroidId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!data || !Array.isArray(data.miners) || data.miners.length === 0) return;
        // Only render if we're still showing the same asteroid
        if (!this.currentAsteroid || this.currentAsteroid.id !== asteroidId) return;
        this.buildMinersSection(data.miners);
      })
      .catch((err) => console.error('[CommandDock] fetchAsteroidMiners error:', err));
  }

  private buildMinersSection(miners: Array<{ displayName: string; email: string; inCargo: number; delivered: number; total: number }>): void {
    // Place miners list below the portrait area, right side (reuse right column area)
    const startX = TAB_ACTIONS_LEFT;
    const startY = PORT_TOP + PORT_H + 4;
    const lineH = 13;

    const headerText = this.scene.add.text(startX, startY, 'MINERS', {
      fontSize: '9px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 2,
    });
    headerText.setOrigin(0, 0);
    this.contentContainer.add(headerText);

    const maxMiners = 4; // limit to avoid overflow
    const shown = miners.slice(0, maxMiners);
    for (let i = 0; i < shown.length; i++) {
      const m = shown[i];
      const name = m.displayName || m.email || 'Unknown';
      const truncated = name.length > 14 ? name.substring(0, 12) + '..' : name;
      const totalStr = m.total >= 1 ? m.total.toFixed(2) : m.total.toFixed(4);
      const lineStr = `${truncated}  ${totalStr}`;

      const lineText = this.scene.add.text(startX, startY + lineH + i * lineH, lineStr, {
        fontSize: '9px',
        color: '#FFD700',
        fontFamily: Fonts.body,
      });
      lineText.setOrigin(0, 0);
      this.contentContainer.add(lineText);
    }

    if (miners.length > maxMiners) {
      const moreText = this.scene.add.text(startX, startY + lineH + maxMiners * lineH, `+${miners.length - maxMiners} more`, {
        fontSize: '9px',
        color: '#556677',
        fontFamily: Fonts.body,
      });
      moreText.setOrigin(0, 0);
      this.contentContainer.add(moreText);
    }
  }

  // ── Wreck display ──────────────────────────────────────────

  private buildWreckDisplay(w: SelectedWreck): void {
    const unitType = (w.unitType || 'unknown').toLowerCase();
    const iconKey = SHIP_ICON_MAP[unitType] || SHIP_ICON_MAP['scout'];

    // Portrait
    if (iconKey && this.scene.textures.exists(iconKey)) {
      const img = this.scene.add.image(
        PORT_LEFT + PORT_W / 2,
        PORT_TOP + PORT_H / 2,
        iconKey,
      );
      img.setDisplaySize(PORT_W, PORT_H);
      img.setTint(0x664444); // dark red tint for wreck
      this.contentContainer.add(img);
    } else {
      const fallback = this.scene.add.text(
        PORT_LEFT + PORT_W / 2, PORT_TOP + PORT_H / 2, '💀',
        { fontSize: '32px' },
      );
      fallback.setOrigin(0.5);
      this.contentContainer.add(fallback);
    }

    // Name
    const displayName = `${I18n.shipName(unitType)} Wreck`;
    const name = this.scene.add.text(NAME_LEFT, NAME_TOP, displayName, {
      fontSize: '20px',
      fontStyle: '900',
      color: '#AA4444',
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    name.setOrigin(0, 0.5);
    this.contentContainer.add(name);

    // Location + status line
    const statusParts: string[] = [`(${w.hexCoord.q}, ${w.hexCoord.r})`];
    if (w.towStatus && w.towStatus !== 'none') statusParts.push(`Tow: ${w.towStatus}`);
    if (w.repairStatus && w.repairStatus !== 'none') statusParts.push(`Repair: ${w.repairStatus}`);
    const infoLine = this.scene.add.text(OWNER_LEFT, OWNER_TOP, statusParts.join('  |  '), {
      fontSize: '10px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    infoLine.setOrigin(0, 0);
    this.contentContainer.add(infoLine);

    // Time remaining bar
    const barStartX = STATS_LEFT;
    const textY = PORT_TOP + PORT_H + 4;
    const barY = textY + 14;
    const barH = 10;
    const barW = TAB_ACTIONS_LEFT - STATS_LEFT - 30;

    const expiresMs = new Date(w.expiresAt).getTime();
    const destroyedMs = new Date(w.destroyedAt).getTime();
    const nowMs = Date.now();
    const totalMs = expiresMs - destroyedMs;
    const remainMs = Math.max(0, expiresMs - nowMs);
    if (totalMs > 0) {
      const remainHours = Math.ceil(remainMs / 3600000);
      const pct = remainMs / totalMs;
      const color = pct <= 0.2 ? 0xff4444 : pct <= 0.5 ? 0xffaa00 : 0x00ccff;
      this.drawStatBarAbove(barStartX, textY, barY, barW, barH,
        remainHours, Math.ceil(totalMs / 3600000), color,
        'TIME LEFT (hrs)', `This wreck expires in ${remainHours} hours`);
    }

    // Stats row at bottom
    const statsY = DOCK_H - STATS_BOTTOM;
    let sx = STATS_LEFT;
    const stats: { label: string; value: string; color?: string }[] = [
      { label: I18n.t('wreck.type'), value: unitType.toUpperCase(), color: '#AAAAAA' },
      { label: I18n.t('wreck.tow'), value: (w.towStatus || 'none').toUpperCase(), color: w.towStatus === 'towing' ? '#4488FF' : '#556677' },
      { label: I18n.t('wreck.repair'), value: (w.repairStatus || 'none').toUpperCase(), color: w.repairStatus === 'repairing' ? '#44CC44' : '#556677' },
    ];
    for (const stat of stats) {
      const lbl = this.scene.add.text(sx, statsY, stat.label, {
        fontSize: '11px', color: '#8899BB', fontFamily: Fonts.body, letterSpacing: 2,
      });
      lbl.setOrigin(0, 1);
      this.contentContainer.add(lbl);
      const val = this.scene.add.text(sx + lbl.width + STAT_VALUE_GAP, statsY, stat.value, {
        fontSize: '11px', fontStyle: '900', color: stat.color || '#00FFFF', fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 1);
      this.contentContainer.add(val);
      sx += lbl.width + STAT_VALUE_GAP + val.width + STAT_GAP;
    }

    // ── Right-side: CLAIM button ──
    const contentLeft = TAB_ACTIONS_LEFT;

    // Check if this wreck is claimable (not already being towed or repaired)
    const isClaimable = (!w.towStatus || w.towStatus === 'none') && (!w.repairStatus || w.repairStatus === 'none');
    const isOwned = w.ownerId === this.mmoState.getUserId();

    if (isClaimable && !isOwned) {
      // CLAIM button
      const btnW = 120;
      const btnH = 40;
      const btnX = contentLeft + (DOCK_W - contentLeft - 14) / 2;
      const btnY = DOCK_H / 2;

      const btnGfx = this.scene.add.graphics();
      btnGfx.fillStyle(0x00aa44, 1);
      btnGfx.fillRoundedRect(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH, 6);
      btnGfx.lineStyle(2, 0x00ff66, 0.8);
      btnGfx.strokeRoundedRect(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH, 6);
      this.contentContainer.add(btnGfx);

      const btnText = this.scene.add.text(btnX, btnY, I18n.t('wreck.claim'), {
        fontSize: '16px', fontStyle: '900', color: '#FFFFFF',
        fontFamily: Fonts.heading, letterSpacing: 3,
      });
      btnText.setOrigin(0.5);
      this.contentContainer.add(btnText);

      const hitZone = this.scene.add.zone(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
      this.contentContainer.add(hitZone);

      hitZone.on('pointerover', () => { this.scene.input.setDefaultCursor('pointer'); });
      hitZone.on('pointerout', () => { this.scene.input.setDefaultCursor('default'); });
      hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        this.claimWreck(w.id);
      });
    } else if (isOwned) {
      // Show "YOUR WRECK" label + TOW/REPAIR options link
      const label = this.scene.add.text(contentLeft + (DOCK_W - contentLeft - 14) / 2, TAB_TOP + 10, I18n.t('wreck.your_wreck'), {
        fontSize: '14px', fontStyle: '900', color: '#44CC44',
        fontFamily: Fonts.heading, letterSpacing: 2,
      });
      label.setOrigin(0.5, 0);
      this.contentContainer.add(label);

      // "MANAGE" button to open full wreckage dialog
      const btnW = 120;
      const btnH = 34;
      const btnX = contentLeft + (DOCK_W - contentLeft - 14) / 2;
      const btnY = ACTION_ROW_TOP + btnH / 2 + 10;

      const btnGfx = this.scene.add.graphics();
      btnGfx.fillStyle(0x222244, 1);
      btnGfx.fillRoundedRect(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH, 6);
      btnGfx.lineStyle(2, 0x4488ff, 0.8);
      btnGfx.strokeRoundedRect(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH, 6);
      this.contentContainer.add(btnGfx);

      const btnText = this.scene.add.text(btnX, btnY, I18n.t('wreck.manage'), {
        fontSize: '14px', fontStyle: '900', color: '#AACCFF',
        fontFamily: Fonts.heading, letterSpacing: 2,
      });
      btnText.setOrigin(0.5);
      this.contentContainer.add(btnText);

      const hitZone = this.scene.add.zone(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
      this.contentContainer.add(hitZone);

      hitZone.on('pointerover', () => { this.scene.input.setDefaultCursor('pointer'); });
      hitZone.on('pointerout', () => { this.scene.input.setDefaultCursor('default'); });
      hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        EventBus.emit('open-wreckage-dialog');
      });
    } else {
      // Wreck is being processed (towing/repairing)
      const statusLabel = w.towStatus === 'towing' ? I18n.t('wreck.being_towed') :
                          w.repairStatus === 'repairing' ? I18n.t('wreck.being_repaired') : I18n.t('wreck.claimed');
      const label = this.scene.add.text(contentLeft + (DOCK_W - contentLeft - 14) / 2, DOCK_H / 2, statusLabel, {
        fontSize: '14px', fontStyle: '900', color: '#8899BB',
        fontFamily: Fonts.heading, letterSpacing: 2,
      });
      label.setOrigin(0.5);
      this.contentContainer.add(label);
    }
  }

  private async claimWreck(wreckId: string): Promise<void> {
    try {
      const { MMOMonetizationManager } = await import('../../managers/MMOMonetizationManager');
      const monetization = ServiceLocator.get<InstanceType<typeof MMOMonetizationManager>>(Services.MMO_MONETIZATION);

      // Find a player unit at the wreck's hex for the proximity check
      let unitId: string | undefined;
      if (this.currentWreck) {
        const playerUnits = this.mmoState.getPlayerUnits();
        const wreckQ = this.currentWreck.hexCoord.q;
        const wreckR = this.currentWreck.hexCoord.r;
        const unitAtWreck = playerUnits.find(
          (u) => u.position.q === wreckQ && u.position.r === wreckR,
        );
        if (unitAtWreck) unitId = unitAtWreck.id;
      }

      const result = await monetization.claimWreck(wreckId, unitId);
      if (result.success) {
        // Refresh the display — the wreck will now be owned by the player
        if (this.currentWreck) {
          this.currentWreck.ownerId = this.mmoState.getUserId() || '';
        }
        this.buildDisplay();
      } else {
        console.error('[CommandDock] Claim wreck failed:', result.error);
      }
    } catch (err) {
      console.error('[CommandDock] Claim wreck error:', err);
    }
  }

  private buildLootDropDisplay(loot: any): void {
    const unitType = (loot.sourceUnitType || 'unknown').toLowerCase();
    const iconKey = SHIP_ICON_MAP[unitType] || SHIP_ICON_MAP['scout'];

    // Portrait (wreckage tint)
    if (iconKey && this.scene.textures.exists(iconKey)) {
      const img = this.scene.add.image(
        PORT_LEFT + PORT_W / 2,
        PORT_TOP + PORT_H / 2,
        iconKey,
      );
      img.setDisplaySize(PORT_W, PORT_H);
      img.setTint(0xffaa44);
      this.contentContainer.add(img);
    } else {
      const fallback = this.scene.add.text(
        PORT_LEFT + PORT_W / 2, PORT_TOP + PORT_H / 2, '📦',
        { fontSize: '32px' },
      );
      fallback.setOrigin(0.5);
      this.contentContainer.add(fallback);
    }

    // Title
    const name = this.scene.add.text(NAME_LEFT, NAME_TOP, 'COMBAT LOOT', {
      fontSize: '18px',
      fontStyle: '900',
      color: '#FFAA44',
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    name.setOrigin(0, 0.5);
    this.contentContainer.add(name);

    // Subtitle showing source ship type
    const subtitle = this.scene.add.text(NAME_LEFT, NAME_TOP + 14, `From: ${I18n.shipName(unitType)}`, {
      fontSize: '10px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    subtitle.setOrigin(0, 0);
    this.contentContainer.add(subtitle);

    // Contents summary
    const contentParts: string[] = [];
    if (loot.hasComponent) contentParts.push('Component (damaged)');
    if (loot.hasMinerals) contentParts.push('Minerals');
    if (loot.hasGold) contentParts.push('Gold');
    const contentsText = contentParts.length > 0 ? contentParts.join(' + ') : 'Empty';

    const contentsLabel = this.scene.add.text(STATS_LEFT, PORT_TOP + PORT_H + 4, `Contents: ${contentsText}`, {
      fontSize: '10px',
      color: '#AACCEE',
      fontFamily: Fonts.body,
    });
    contentsLabel.setOrigin(0, 0);
    this.contentContainer.add(contentsLabel);

    // Expiry timer
    const expiresMs = new Date(loot.expiresAt).getTime();
    const nowMs = Date.now();
    const remainSecs = Math.max(0, Math.floor((expiresMs - nowMs) / 1000));
    const mins = Math.floor(remainSecs / 60);
    const secs = remainSecs % 60;
    const expiryColor = remainSecs < 60 ? '#FF4444' : '#8899BB';

    const expiryLabel = this.scene.add.text(STATS_LEFT, PORT_TOP + PORT_H + 18, `Expires: ${mins}m ${secs}s`, {
      fontSize: '10px',
      color: expiryColor,
      fontFamily: Fonts.body,
    });
    expiryLabel.setOrigin(0, 0);
    this.contentContainer.add(expiryLabel);

    // Exclusive window info
    const exclusiveMs = new Date(loot.exclusiveUntil).getTime();
    if (exclusiveMs > nowMs) {
      const exclSecs = Math.max(0, Math.ceil((exclusiveMs - nowMs) / 1000));
      const userId = this.mmoState.getUserId();
      const isTopDealer = loot.topDealerId === userId;
      const exclText = isTopDealer
        ? `Your exclusive loot: ${exclSecs}s`
        : `Exclusive to top dealer: ${exclSecs}s`;
      const exclColor = isTopDealer ? '#44CC44' : '#FF8844';

      const exclLabel = this.scene.add.text(STATS_LEFT, PORT_TOP + PORT_H + 32, exclText, {
        fontSize: '10px',
        color: exclColor,
        fontFamily: Fonts.body,
      });
      exclLabel.setOrigin(0, 0);
      this.contentContainer.add(exclLabel);
    }

    // Stats row at bottom
    const statsY = DOCK_H - STATS_BOTTOM;
    let sx = STATS_LEFT;
    const stats: { label: string; value: string; color?: string }[] = [
      { label: 'SOURCE', value: unitType.toUpperCase(), color: '#AAAAAA' },
      { label: 'POS', value: `(${loot.hexCoord.q}, ${loot.hexCoord.r})`, color: '#556677' },
    ];
    for (const stat of stats) {
      const lbl = this.scene.add.text(sx, statsY, stat.label, {
        fontSize: '11px', color: '#8899BB', fontFamily: Fonts.body, letterSpacing: 2,
      });
      lbl.setOrigin(0, 1);
      this.contentContainer.add(lbl);
      const val = this.scene.add.text(sx + lbl.width + STAT_VALUE_GAP, statsY, stat.value, {
        fontSize: '11px', fontStyle: '900', color: stat.color || '#00FFFF', fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 1);
      this.contentContainer.add(val);
      sx += lbl.width + STAT_VALUE_GAP + val.width + STAT_GAP;
    }

    // ── Right-side: LOOT button ──
    const contentLeft = TAB_ACTIONS_LEFT;
    const btnW = 130;
    const btnH = 44;
    const btnX = contentLeft + (DOCK_W - contentLeft - 14) / 2;
    const btnY = DOCK_H / 2;

    const btnGfx = this.scene.add.graphics();
    btnGfx.fillStyle(0xaa6600, 1);
    btnGfx.fillRoundedRect(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH, 6);
    btnGfx.lineStyle(2, 0xffaa00, 0.8);
    btnGfx.strokeRoundedRect(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH, 6);
    this.contentContainer.add(btnGfx);

    const btnText = this.scene.add.text(btnX, btnY, 'CLAIM LOOT', {
      fontSize: '16px', fontStyle: '900', color: '#FFFFFF',
      fontFamily: Fonts.heading, letterSpacing: 3,
    });
    btnText.setOrigin(0.5);
    this.contentContainer.add(btnText);

    const hitZone = this.scene.add.zone(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
    this.contentContainer.add(hitZone);

    hitZone.on('pointerover', () => { this.scene.input.setDefaultCursor('pointer'); });
    hitZone.on('pointerout', () => { this.scene.input.setDefaultCursor('default'); });
    hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      this.claimLoot(loot.id, loot.hexCoord);
    });
  }

  private buildAmbientEntityDisplay(ae: any): void {
    const typeLabels: Record<string, { name: string; color: string; icon: string }> = {
      treasure_wreck: { name: 'SALVAGEABLE WRECK', color: '#F1C40F', icon: '🔧' },
      alien_artifact: { name: 'UNKNOWN OBJECT', color: '#E056FD', icon: '❓' },
      derelict_ship: { name: 'DERELICT SHIP', color: '#95A5A6', icon: '🚀' },
      debris_cluster: { name: 'DEBRIS FIELD', color: '#7F8C8D', icon: '💫' },
      memorial_beacon: { name: 'MEMORIAL BEACON', color: '#3498DB', icon: '📡' },
      asteroid_cluster: { name: 'ASTEROID CLUSTER', color: '#D35400', icon: '☄' },
      enemy_spawn: { name: 'HOSTILE CONTACT', color: '#E74C3C', icon: '⚠' },
    };
    const info = typeLabels[ae.entityType] || { name: ae.entityType.toUpperCase(), color: '#AAAAAA', icon: '?' };

    // Portrait
    const fallback = this.scene.add.text(
      PORT_LEFT + PORT_W / 2, PORT_TOP + PORT_H / 2, info.icon, { fontSize: '36px' },
    );
    fallback.setOrigin(0.5);
    this.contentContainer.add(fallback);

    // Title
    const title = this.scene.add.text(NAME_LEFT, NAME_TOP, info.name, {
      fontSize: '18px', fontStyle: '900', color: info.color,
      fontFamily: Fonts.heading, letterSpacing: 2,
    });
    title.setOrigin(0, 0.5);
    this.contentContainer.add(title);

    // Subtitle
    const subtitle = this.scene.add.text(NAME_LEFT, NAME_TOP + 14, ae.displayName || ae.entitySubtype || '', {
      fontSize: '10px', color: '#8899BB', fontFamily: Fonts.body, letterSpacing: 1,
    });
    subtitle.setOrigin(0, 0);
    this.contentContainer.add(subtitle);

    // Info lines
    let infoY = PORT_TOP + PORT_H + 4;
    if (ae.maxHp > 0) {
      const hpText = `HP: ${ae.currentHp}/${ae.maxHp}`;
      const hpLabel = this.scene.add.text(STATS_LEFT, infoY, hpText, {
        fontSize: '10px', color: '#AACCEE', fontFamily: Fonts.body,
      });
      hpLabel.setOrigin(0, 0);
      this.contentContainer.add(hpLabel);
      infoY += 14;
    }

    const tags: string[] = [];
    if (ae.lootable) tags.push('Lootable');
    if (ae.scannable) tags.push('Scannable');
    if (tags.length > 0) {
      const tagLabel = this.scene.add.text(STATS_LEFT, infoY, tags.join(' | '), {
        fontSize: '10px', color: '#44CC44', fontFamily: Fonts.body,
      });
      tagLabel.setOrigin(0, 0);
      this.contentContainer.add(tagLabel);
    }

    // Stats row at bottom
    const statsY = DOCK_H - STATS_BOTTOM;
    let sx = STATS_LEFT;
    const stats: { label: string; value: string; color?: string }[] = [
      { label: 'TYPE', value: ae.entityType, color: info.color },
      { label: 'POS', value: `(${ae.hexCoord.q}, ${ae.hexCoord.r})`, color: '#556677' },
    ];
    for (const stat of stats) {
      const lbl = this.scene.add.text(sx, statsY, stat.label, {
        fontSize: '11px', color: '#8899BB', fontFamily: Fonts.body, letterSpacing: 2,
      });
      lbl.setOrigin(0, 1);
      this.contentContainer.add(lbl);
      const val = this.scene.add.text(sx + lbl.width + STAT_VALUE_GAP, statsY, stat.value, {
        fontSize: '11px', fontStyle: '900', color: stat.color || '#00FFFF', fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 1);
      this.contentContainer.add(val);
      sx += lbl.width + STAT_VALUE_GAP + val.width + STAT_GAP;
    }

    // Right-side content area: scan/loot description or action buttons
    const contentLeft = TAB_ACTIONS_LEFT;
    const contentW = DOCK_W - contentLeft - 14;
    let cy = TAB_TOP;

    // Description from properties
    const desc = ae.properties?.description || ae.properties?.lore || '';
    if (desc) {
      const descText = this.scene.add.text(contentLeft, cy, desc, {
        fontSize: '10px', color: '#AABBCC', fontFamily: Fonts.body,
        wordWrap: { width: contentW - 10 },
      });
      descText.setOrigin(0, 0);
      this.contentContainer.add(descText);
      cy += descText.height + 8;
    }

    // Loot table summary
    if (ae.lootable && ae.properties?.loot_table) {
      const lt = ae.properties.loot_table;
      const items: string[] = [];
      if (lt.minerals) items.push(`Minerals: ${lt.minerals.map((m: any) => m.type || m.mineral_type_id).join(', ')}`);
      if (lt.components) items.push(`Components: ${lt.components.map((c: any) => c.name || c.component_id).join(', ')}`);
      if (items.length > 0) {
        const lootText = this.scene.add.text(contentLeft, cy, items.join('\n'), {
          fontSize: '9px', color: '#F1C40F', fontFamily: Fonts.body,
          wordWrap: { width: contentW - 10 },
        });
        lootText.setOrigin(0, 0);
        this.contentContainer.add(lootText);
        cy += lootText.height + 8;
      }
    }

    // Action buttons — use buildPillCTA pattern (proven to work with Phaser input)
    const btnCenterX = contentLeft + contentW / 2;
    let btnCenterY = Math.max(cy + 16, DOCK_H / 2);

    if (ae.scannable) {
      this.buildPillCTA(btnCenterX, btnCenterY, 'SCAN', 'Scan this entity', () => {
        this.scanAmbientEntity(ae.id);
      });
      btnCenterY += 28;
    }

    if (ae.lootable) {
      this.buildPillCTA(btnCenterX, btnCenterY, 'SALVAGE', 'Salvage resources from this entity', () => {
        this.salvageAmbientEntity(ae.id);
      });
    }
  }

  private salvageAmbientEntity(entityId: string): void {
    const userId = this.mmoState.getUserId();
    if (!userId) return;

    const playerUnits = this.mmoState.getPlayerUnits();
    if (playerUnits.length === 0) return;

    // Use first unit — server validates proximity
    EventBus.emit('send_message', {
      type: 'mmo_ambient_salvage',
      payload: { entityId, unitId: playerUnits[0].id },
    });
  }

  private scanAmbientEntity(entityId: string): void {
    const userId = this.mmoState.getUserId();
    if (!userId) return;

    const playerUnits = this.mmoState.getPlayerUnits();
    if (playerUnits.length === 0) return;

    // Use the first player unit — server validates proximity
    const unitId = playerUnits[0].id;

    EventBus.emit('send_message', {
      type: 'mmo_ambient_scan',
      payload: { entityId, unitId },
    });
  }

  private onAmbientScanResult(payload: any): void {
    if (!payload.success && payload.error) {
      EventBus.emit('show-notification', { text: payload.error, color: '#FF4444', duration: 4000 });
      return;
    }

    if (payload.success) {
      const data = payload.scanData || {};
      const title = data.displayName ? `Scan: ${data.displayName}` : 'Scan complete';

      // Show base description
      if (data.description) {
        EventBus.emit('show-notification', { text: `${title} — ${data.description}`, color: '#E056FD', duration: 5000 });
      } else {
        EventBus.emit('show-notification', { text: title, color: '#E056FD', duration: 4000 });
      }

      // Show revealed layers
      const layers = payload.revealedLayers || [];
      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const layerText = Object.entries(layer.data || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
        setTimeout(() => {
          EventBus.emit('show-notification', {
            text: `[${layer.label}] ${layerText}`,
            color: '#00FFFF',
            duration: 6000,
          });
        }, (i + 1) * 800);
      }

      // Show teaser for hidden layers
      if (payload.teaserMessage) {
        setTimeout(() => {
          EventBus.emit('show-notification', {
            text: payload.teaserMessage,
            color: '#FFD700',
            duration: 6000,
          });
        }, (layers.length + 1) * 800);
      }
    }
  }

  private claimLoot(lootId: string, lootPos?: { q: number; r: number }): void {
    const userId = this.mmoState.getUserId();
    if (!userId) return;

    const playerUnits = this.mmoState.getPlayerUnits();
    if (playerUnits.length === 0) {
      console.warn('[CommandDock] No player units available to claim loot');
      return;
    }

    // Pick the player's unit nearest to the loot (cube/hex distance).
    // Falls back to first unit if no loot position was provided.
    let claimerUnit = playerUnits[0];
    if (lootPos) {
      let bestDist = Infinity;
      for (const u of playerUnits) {
        const uq = u.position?.q ?? 0;
        const ur = u.position?.r ?? 0;
        const d = Math.max(
          Math.abs(uq - lootPos.q),
          Math.abs(ur - lootPos.r),
          Math.abs((-uq - ur) - (-lootPos.q - lootPos.r)),
        );
        if (d < bestDist) { bestDist = d; claimerUnit = u; }
      }
    }

    EventBus.emit('send_message', {
      type: 'mmo_loot_claim',
      payload: { lootId, unitId: claimerUnit.id },
    });
  }

  // ── Asteroid composition (right side, no tabs) ─────────────

  private buildAsteroidComposition(a: SelectedAsteroid): void {
    const MINERAL_NAMES: Record<string, string> = {
      min_iron: 'Iron', min_copper: 'Copper', min_titanium: 'Titanium',
      min_platinum: 'Platinum', min_rhodium: 'Rhodium', min_palladium: 'Palladium',
      min_iridium: 'Iridium', min_darkmat: 'Dark Matter',
    };
    const MINERAL_COLORS: Record<string, string> = {
      min_iron: '#8a8a8a', min_copper: '#b87333', min_titanium: '#c0c0d0',
      min_platinum: '#e5e4e2', min_rhodium: '#ffd700', min_palladium: '#ced8f6',
      min_iridium: '#f0f0ff', min_darkmat: '#6600cc',
    };
    const MINERAL_ORDER: string[] = [
      'min_darkmat', 'min_iridium',
      'min_rhodium', 'min_palladium',
      'min_titanium', 'min_platinum',
      'min_iron', 'min_copper',
    ];
    const MINERAL_RARITY: Record<string, string> = {
      min_iron: 'common', min_copper: 'common',
      min_titanium: 'uncommon', min_platinum: 'uncommon',
      min_rhodium: 'rare', min_palladium: 'rare',
      min_iridium: 'epic', min_darkmat: 'legendary',
    };
    const RARITY_ORDER: Record<string, number> = {
      common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 5,
    };
    const RARITY_LASER_NAME: Record<string, string> = {
      uncommon: 'Mk1', rare: 'Mk2', epic: 'Mk3', legendary: 'Mk4',
    };
    const maxRarityLevel = RARITY_ORDER[this.lastPlayerMaxRarity] || 1;

    const contentLeft = TAB_ACTIONS_LEFT;
    const contentRight = DOCK_W - 14;
    const contentW = contentRight - contentLeft;

    // "COMPOSITION" header
    const header = this.scene.add.text(contentLeft, TAB_TOP, I18n.t('asteroid.composition'), {
      fontSize: '10px', fontStyle: '900', color: '#FFFFFF',
      fontFamily: Fonts.heading, letterSpacing: 2,
    });
    header.setOrigin(0, 0);
    this.contentContainer.add(header);

    // Underline
    const underline = this.scene.add.graphics();
    underline.lineStyle(3, 0x00ffff, 1);
    underline.lineBetween(contentLeft, TAB_UNDERLINE_Y, contentLeft + header.width, TAB_UNDERLINE_Y);
    this.contentContainer.add(underline);

    if (!a.mineralComposition || Object.keys(a.mineralComposition).length === 0) {
      const empty = this.scene.add.text(contentLeft + contentW / 2, TAB_UNDERLINE_Y + 30, 'No mineral data', {
        fontSize: '11px', color: '#556677', fontFamily: Fonts.body, fontStyle: 'italic',
      });
      empty.setOrigin(0.5, 0);
      this.contentContainer.add(empty);
      return;
    }

    // Collect mineral entries and sort by rarity (rarest first)
    const mineralEntries = Object.entries(a.mineralComposition)
      .filter(([, qty]) => (Number(qty) || 0) > 0)
      .map(([id, qty]) => ({
        id,
        name: MINERAL_NAMES[id] || id,
        qty: Number(qty),
        color: MINERAL_COLORS[id] || '#ffffff',
      }))
      .sort((x, y) => {
        const xi = MINERAL_ORDER.indexOf(x.id);
        const yi = MINERAL_ORDER.indexOf(y.id);
        return (xi === -1 ? 999 : xi) - (yi === -1 ? 999 : yi);
      });

    // Mineral total for percentage calculation (excludes stellar)
    const mineralTotal = mineralEntries.reduce((s, e) => s + e.qty, 0);

    // Build display entries — USDC reward first for golden, then minerals
    const entries: { id: string; name: string; qty: number; color: string; mystery?: boolean }[] = [];
    if ((a.asteroidType === 'crypto' || a.asteroidType === 'golden' || a.asteroidType === 'special') && a.rewardValue && a.rewardValue > 0) {
      const tokenName = (a as any).cryptoToken || (a as any).tokenName || 'USDC';
      entries.push({ id: 'crypto_reward', name: tokenName, qty: 0, color: '#2775ca', mystery: true });
    }
    entries.push(...mineralEntries);

    // 2-column layout
    const COL_W = Math.floor(contentW / 2);
    const listTop = TAB_UNDERLINE_Y + 10;
    const listBottom = DOCK_H - STATS_BOTTOM - 4;
    const availH = listBottom - listTop;
    const maxRows = 4;
    const ROW_H = Math.min(Math.floor(availH / maxRows), 20);

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const col = i < maxRows ? 0 : 1;
      const row = i < maxRows ? i : i - maxRows;
      const cx = contentLeft + col * COL_W;
      const cy = listTop + row * ROW_H;

      const mineralRarity = MINERAL_RARITY[entry.id];
      const isLocked = !entry.mystery && mineralRarity && (RARITY_ORDER[mineralRarity] || 1) > maxRarityLevel;
      const dimAlpha = isLocked ? 0.35 : 1;

      // Colored dot (dimmed if locked)
      const dot = this.scene.add.graphics();
      dot.fillStyle(Phaser.Display.Color.HexStringToColor(entry.color).color, isLocked ? 0.3 : 1);
      dot.fillCircle(cx + 5, cy + 7, 4);
      this.contentContainer.add(dot);

      if (isLocked) {
        // Lock icon (small "🔒" text) + dimmed name
        const lockText = this.scene.add.text(cx + 14, cy, '\u{1F512}', {
          fontSize: '9px', color: '#ff6644', fontFamily: Fonts.body,
        });
        lockText.setOrigin(0, 0);
        this.contentContainer.add(lockText);

        const nameText = this.scene.add.text(cx + 27, cy, entry.name, {
          fontSize: '11px', fontStyle: 'bold', color: '#556677', fontFamily: Fonts.body,
        });
        nameText.setOrigin(0, 0);
        nameText.setAlpha(dimAlpha);
        this.contentContainer.add(nameText);

        // Show required laser tier
        const reqTier = RARITY_LASER_NAME[mineralRarity] || '??';
        const reqText = this.scene.add.text(cx + COL_W - 6, cy, reqTier, {
          fontSize: '10px', fontStyle: '900', color: '#ff6644', fontFamily: Fonts.body,
        });
        reqText.setOrigin(1, 0);
        reqText.setAlpha(0.7);
        this.contentContainer.add(reqText);
      } else {
        // Name
        const nameText = this.scene.add.text(cx + 15, cy, entry.name, {
          fontSize: '11px', fontStyle: 'bold', color: entry.color, fontFamily: Fonts.body,
        });
        nameText.setOrigin(0, 0);
        this.contentContainer.add(nameText);

        // Percentages: minerals sum to 100%, $STELLAR shows "??"
        const pctLabel = entry.mystery ? '??' : `${mineralTotal > 0 ? ((entry.qty / mineralTotal) * 100).toFixed(0) : '0'}%`;
        const pctText = this.scene.add.text(cx + COL_W - 6, cy, pctLabel, {
          fontSize: '11px', fontStyle: '900', color: entry.mystery ? '#00ff88' : '#aabbcc', fontFamily: Fonts.body,
        });
        pctText.setOrigin(1, 0);
        this.contentContainer.add(pctText);
      }
    }
  }

  // ── Mobile planet info panel (paginated, left-anchored) ────────

  /**
   * Mobile-only compact planet info panel.
   * Half-screen width, vertical layout, content paginated across
   * 2–3 pages (Status / Storage / Build) with dot + arrow nav.
   */
  private buildMobilePlanetPanel(p: SelectedPlanet): void {
    const isPlayerPlanet = p.ownerId === this.mmoState.getUserId();
    const isEarth = p.hexCoord.q === 0 && p.hexCoord.r === 0;
    const canBuild = isPlayerPlanet || isEarth || !p.ownerId;
    const hasStorage = isPlayerPlanet || isEarth;

    // ── Page list ─────────────────────────────────────────────
    // Page 0 = STATUS (always shown), then STORAGE + BUILD when available.
    const pages: { key: 'status' | 'storage' | 'build'; label: string }[] = [
      { key: 'status', label: 'STATUS' },
    ];
    if (hasStorage) pages.push({ key: 'storage', label: I18n.t('tab.storage').toUpperCase() });
    if (canBuild)   pages.push({ key: 'build',   label: I18n.t('tab.build').toUpperCase() });

    this.infoPanelPages = pages.length;
    if (this.infoPanelPage >= pages.length) this.infoPanelPage = 0;
    const activePage = pages[this.infoPanelPage];

    const W = this.infoPanelW;
    const H = this.infoPanelH;
    const px = MOBILE_INFO_PAD_X;
    const innerW = W - px * 2;

    // ── Header: circular portrait + name + owner ───────────────
    const headerTop = MOBILE_INFO_PAD_TOP;
    const portSize = MOBILE_INFO_PORT_SIZE;
    const portCx = px + portSize / 2;
    const portCy = headerTop + portSize / 2;

    // Circular mask + portrait image
    let planetImageKey: string | null = null;
    if (isEarth) {
      planetImageKey = 'earth';
    } else if (p.planetName) {
      const nameKey = `planet-${p.planetName.toLowerCase().replace(/\s+/g, '-')}`;
      if (this.scene.textures.exists(nameKey)) planetImageKey = nameKey;
    }

    // Portrait ring backdrop
    const ringGfx = this.scene.add.graphics();
    ringGfx.fillStyle(0x0a0e1e, 1);
    ringGfx.fillCircle(portCx, portCy, portSize / 2);
    ringGfx.lineStyle(2, 0x00ffff, 0.6);
    ringGfx.strokeCircle(portCx, portCy, portSize / 2);
    this.contentContainer.add(ringGfx);

    if (planetImageKey && this.scene.textures.exists(planetImageKey)) {
      const img = this.scene.add.image(portCx, portCy, planetImageKey);
      const tex = img.texture.getSourceImage();
      const scale = Math.min((portSize - 4) / tex.width, (portSize - 4) / tex.height);
      img.setScale(scale);
      this.contentContainer.add(img);
    } else {
      const planetColor = isPlayerPlanet ? 0x3388ee : (p.ownerId ? 0xee4444 : 0x888888);
      const g = this.scene.add.graphics();
      g.fillStyle(planetColor, 0.85);
      g.fillCircle(portCx, portCy, portSize / 2 - 6);
      this.contentContainer.add(g);
    }

    // Name (right of portrait)
    const nameX = px + portSize + 10;
    const name = this.scene.add.text(nameX, headerTop + 4, p.planetName || 'Unknown', {
      fontSize: '16px',
      fontStyle: '900',
      color: isPlayerPlanet ? '#E0E8FF' : (p.ownerId ? '#FF8866' : '#AABBCC'),
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    name.setOrigin(0, 0);
    this.contentContainer.add(name);

    // HOME badge
    if (p.isHomePlanet) {
      const badge = this.scene.add.text(nameX, headerTop + 24, '★ HOME', {
        fontSize: '8px',
        fontStyle: '900',
        color: '#FFD700',
        fontFamily: Fonts.heading,
        letterSpacing: 1,
      });
      badge.setOrigin(0, 0);
      this.contentContainer.add(badge);
    }

    // Owner + coords
    let ownerStr: string;
    if (!p.ownerId)        ownerStr = I18n.t('planet.neutral');
    else if (isPlayerPlanet) ownerStr = I18n.t('planet.your_planet');
    else                   ownerStr = I18n.t('planet.enemy');

    const ownerText = this.scene.add.text(nameX, headerTop + 38, ownerStr, {
      fontSize: '9px', color: '#8899BB', fontFamily: Fonts.body, letterSpacing: 1,
    });
    ownerText.setOrigin(0, 0);
    this.contentContainer.add(ownerText);

    const coordText = this.scene.add.text(nameX, headerTop + 52, `(${p.hexCoord.q}, ${p.hexCoord.r})`, {
      fontSize: '9px', color: '#556677', fontFamily: Fonts.body, letterSpacing: 1,
    });
    coordText.setOrigin(0, 0);
    this.contentContainer.add(coordText);

    // Page-tab row right under the header
    const tabRowY = headerTop + portSize + 10;
    this.drawMobilePlanetTabs(pages, this.infoPanelPage, px, tabRowY, innerW);

    // Divider under tabs
    const dividerY = tabRowY + 22;
    const divider = this.scene.add.graphics();
    divider.lineStyle(1, 0x334466, 0.4);
    divider.lineBetween(px, dividerY, px + innerW, dividerY);
    this.contentContainer.add(divider);

    // ── Active page body ──────────────────────────────────────
    const bodyTop = dividerY + 10;
    const bodyBottom = H - MOBILE_INFO_FOOTER_H - 8;
    const bodyH = bodyBottom - bodyTop;

    if (activePage.key === 'status') {
      this.drawMobilePlanetStatusPage(p, isEarth, px, bodyTop, innerW, bodyH);
    } else if (activePage.key === 'storage') {
      this.drawMobilePlanetStoragePage(p, isEarth, px, bodyTop, innerW, bodyH);
    } else {
      this.drawMobilePlanetBuildPage(p, canBuild, px, bodyTop, innerW, bodyH);
    }

    // ── Footer: page dots + prev/next arrows ──────────────────
    this.drawMobilePanelFooter(px, H - MOBILE_INFO_FOOTER_H, innerW, MOBILE_INFO_FOOTER_H);
  }

  /** Clickable page tabs (small) shown right below the header. */
  private drawMobilePlanetTabs(
    pages: { key: string; label: string }[],
    activeIdx: number,
    left: number, top: number, width: number,
  ): void {
    const tabW = width / pages.length;
    for (let i = 0; i < pages.length; i++) {
      const tabX = left + i * tabW;
      const isActive = i === activeIdx;

      const label = this.scene.add.text(tabX + tabW / 2, top + 10, pages[i].label, {
        fontSize: '10px',
        fontStyle: '900',
        color: isActive ? '#FFFFFF' : '#8899BB',
        fontFamily: Fonts.heading,
        letterSpacing: 1,
      });
      label.setOrigin(0.5, 0.5);
      this.contentContainer.add(label);

      if (isActive) {
        const under = this.scene.add.graphics();
        under.lineStyle(2, 0x00ffff, 1);
        under.lineBetween(
          tabX + tabW * 0.15, top + 20,
          tabX + tabW * 0.85, top + 20,
        );
        this.contentContainer.add(under);
      }

      const hit = this.scene.add.zone(tabX, top, tabW, 22).setOrigin(0, 0);
      hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, tabW, 22), Phaser.Geom.Rectangle.Contains);
      this.contentContainer.add(hit);
      const pageIdx = i;
      hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        if (this.infoPanelPage !== pageIdx) {
          this.infoPanelPage = pageIdx;
          this.buildDisplay();
        }
      });
    }
  }

  /** STATUS page: HP/Shield bars, storage bar, summary stats. */
  private drawMobilePlanetStatusPage(
    p: SelectedPlanet, isEarth: boolean,
    x: number, y: number, w: number, h: number,
  ): void {
    void h;
    const barH = 10;
    let cy = y;

    // Combined HP + Shields
    const combinedHp = p.currentHp + p.currentShields;
    const combinedMax = p.maxHp + p.maxShields;

    const hpLabel = this.scene.add.text(x, cy, I18n.t('stat.shields'), {
      fontSize: '9px', fontStyle: '900', color: '#AAFFCC',
      fontFamily: Fonts.heading, letterSpacing: 1,
    });
    this.contentContainer.add(hpLabel);
    const hpVal = this.scene.add.text(x + w, cy, `${combinedHp} / ${combinedMax}`, {
      fontSize: '9px', color: '#88AACC', fontFamily: Fonts.body,
    });
    hpVal.setOrigin(1, 0);
    this.contentContainer.add(hpVal);
    cy += 14;

    const barBg = this.scene.add.graphics();
    barBg.fillStyle(0x1a2030, 1);
    barBg.fillRoundedRect(x, cy, w, barH, 3);
    const pct = combinedMax > 0 ? Math.min(1, combinedHp / combinedMax) : 0;
    if (pct > 0) {
      barBg.fillStyle(0x00ff88, 1);
      barBg.fillRoundedRect(x, cy, w * pct, barH, 3);
    }
    barBg.lineStyle(1, 0x334466, 0.6);
    barBg.strokeRoundedRect(x, cy, w, barH, 3);
    this.contentContainer.add(barBg);
    cy += barH + 16;

    // Storage bar
    const mineralCap = isEarth ? MINERAL_STORAGE_CAPACITY.earth : (p.maxStorage || 0);
    if (mineralCap > 0) {
      const used = this.storageData
        ? this.storageData.reduce((s, m) => s + Number(m.amount), 0)
        : 0;
      const stLabel = this.scene.add.text(x, cy, I18n.t('storage.label'), {
        fontSize: '9px', fontStyle: '900', color: '#88BBFF',
        fontFamily: Fonts.heading, letterSpacing: 1,
      });
      this.contentContainer.add(stLabel);
      const stVal = this.scene.add.text(x + w, cy, `${Math.round(used)} / ${mineralCap}`, {
        fontSize: '9px', color: '#88AACC', fontFamily: Fonts.body,
      });
      stVal.setOrigin(1, 0);
      this.contentContainer.add(stVal);
      cy += 14;

      const stBg = this.scene.add.graphics();
      stBg.fillStyle(0x1a2030, 1);
      stBg.fillRoundedRect(x, cy, w, barH, 3);
      const stPct = mineralCap > 0 ? Math.min(1, used / mineralCap) : 0;
      if (stPct > 0) {
        const color = stPct > 0.9 ? 0xff4444 : (stPct > 0.7 ? 0xffaa44 : 0x4488ff);
        stBg.fillStyle(color, 1);
        stBg.fillRoundedRect(x, cy, w * stPct, barH, 3);
      }
      stBg.lineStyle(1, 0x334466, 0.6);
      stBg.strokeRoundedRect(x, cy, w, barH, 3);
      this.contentContainer.add(stBg);
      cy += barH + 16;
    }

    // Docked fleet count (if any)
    if (this.dockedShips.length > 0) {
      const dockedLabel = this.scene.add.text(x, cy, `DOCKED SHIPS`, {
        fontSize: '9px', fontStyle: '900', color: '#CCBBFF',
        fontFamily: Fonts.heading, letterSpacing: 1,
      });
      this.contentContainer.add(dockedLabel);
      const dockedVal = this.scene.add.text(x + w, cy, `${this.dockedShips.length}`, {
        fontSize: '10px', fontStyle: '900', color: '#E0E8FF', fontFamily: Fonts.body,
      });
      dockedVal.setOrigin(1, 0);
      this.contentContainer.add(dockedVal);
      cy += 18;
    }
  }

  /** STORAGE page: list of minerals at the planet. */
  private drawMobilePlanetStoragePage(
    p: SelectedPlanet, _isEarth: boolean,
    x: number, y: number, w: number, h: number,
  ): void {
    void p;
    // Lazy-load if we don't have data yet
    if (!this.storageData && !this.isLoadingStorage) {
      this.fetchStorage();
    }

    if (this.isLoadingStorage || !this.storageData) {
      const loading = this.scene.add.text(x + w / 2, y + 20, I18n.t('common.loading') || 'Loading...', {
        fontSize: '11px', color: '#667788', fontFamily: Fonts.body,
      });
      loading.setOrigin(0.5, 0);
      this.contentContainer.add(loading);
      return;
    }

    const minerals = this.storageData;
    if (minerals.length === 0) {
      const empty = this.scene.add.text(x + w / 2, y + 20, I18n.t('storage.empty') || 'No minerals stored', {
        fontSize: '10px', color: '#556677', fontFamily: Fonts.body,
      });
      empty.setOrigin(0.5, 0);
      this.contentContainer.add(empty);
      return;
    }

    const rowH = 18;
    const maxRows = Math.max(1, Math.floor(h / rowH));
    const rows = Math.min(maxRows, minerals.length);
    for (let i = 0; i < rows; i++) {
      const m = minerals[i];
      const rowY = y + i * rowH;

      // Dot
      const dot = this.scene.add.graphics();
      try {
        dot.fillStyle(parseInt(String(m.color).replace('#', '0x')) || 0x88aabb, 1);
      } catch {
        dot.fillStyle(0x88aabb, 1);
      }
      dot.fillCircle(x + 5, rowY + rowH / 2, 4);
      this.contentContainer.add(dot);

      // Name
      const nameLbl = this.scene.add.text(x + 16, rowY + 3, m.displayName || m.mineralName, {
        fontSize: '10px', color: '#CCDDEE', fontFamily: Fonts.body,
      });
      this.contentContainer.add(nameLbl);

      // Amount
      const amtLbl = this.scene.add.text(x + w - 2, rowY + 3, Math.floor(Number(m.amount)).toString(), {
        fontSize: '10px', color: '#AABBCC', fontFamily: Fonts.body,
      });
      amtLbl.setOrigin(1, 0);
      this.contentContainer.add(amtLbl);
    }

    if (rows < minerals.length) {
      const more = this.scene.add.text(x + w / 2, y + rows * rowH + 4, `+${minerals.length - rows} more...`, {
        fontSize: '9px', color: '#556677', fontFamily: Fonts.body,
      });
      more.setOrigin(0.5, 0);
      this.contentContainer.add(more);
    }
  }

  /** BUILD page: production queue summary. Tapping opens the full build dialog. */
  private drawMobilePlanetBuildPage(
    p: SelectedPlanet, canBuild: boolean,
    x: number, y: number, w: number, h: number,
  ): void {
    void h;
    if (!canBuild) {
      const msg = this.scene.add.text(x + w / 2, y + 20, I18n.t('station.no_production') || 'Cannot build here', {
        fontSize: '10px', color: '#667788', fontFamily: Fonts.body, align: 'center',
        wordWrap: { width: w - 8 },
      });
      msg.setOrigin(0.5, 0);
      this.contentContainer.add(msg);
      return;
    }

    const pendingBuilds = this.mmoState.getPendingBuilds(p.id || '');
    const oldQueue: any[] = Array.isArray(p.productionQueue) ? p.productionQueue : [];
    const queue: any[] = pendingBuilds.length > 0 ? pendingBuilds : oldQueue;

    const headerLbl = this.scene.add.text(x, y, 'PRODUCTION QUEUE', {
      fontSize: '9px', fontStyle: '900', color: '#AABBCC',
      fontFamily: Fonts.heading, letterSpacing: 1,
    });
    this.contentContainer.add(headerLbl);

    if (queue.length === 0) {
      const empty = this.scene.add.text(x + w / 2, y + 30, I18n.t('station.nothing_producing') || 'Nothing in queue', {
        fontSize: '10px', color: '#556677', fontFamily: Fonts.body,
      });
      empty.setOrigin(0.5, 0);
      this.contentContainer.add(empty);
    } else {
      const rowH = 18;
      for (let i = 0; i < Math.min(queue.length, 5); i++) {
        const item = queue[i];
        const rowY = y + 20 + i * rowH;
        const nm = item.buildingName || item.unitType || item.name || '—';
        const lbl = this.scene.add.text(x, rowY, `${i + 1}. ${nm}`, {
          fontSize: '10px', color: '#CCDDEE', fontFamily: Fonts.body,
        });
        this.contentContainer.add(lbl);
        const eta = item.turnsRemaining != null ? `${item.turnsRemaining}t` : '';
        if (eta) {
          const etaLbl = this.scene.add.text(x + w, rowY, eta, {
            fontSize: '10px', color: '#88AACC', fontFamily: Fonts.body,
          });
          etaLbl.setOrigin(1, 0);
          this.contentContainer.add(etaLbl);
        }
      }
    }

    // Hint line at the bottom (build-menu CTA is future work)
    const hint = this.scene.add.text(
      x + w / 2, y + h - 14,
      I18n.t('mobile.build_hint') || 'Use desktop to queue new builds',
      {
        fontSize: '8px', color: '#667788', fontFamily: Fonts.body,
        align: 'center', wordWrap: { width: w - 8 },
      },
    );
    hint.setOrigin(0.5, 0.5);
    this.contentContainer.add(hint);

    void p;
  }

  /** Shared page-nav footer: dots (center) + prev/next arrows (sides). */
  private drawMobilePanelFooter(x: number, y: number, w: number, h: number): void {
    const cy = y + h / 2;

    // Prev arrow (left)
    const prevEnabled = this.infoPanelPage > 0;
    const prev = this.scene.add.text(x + 8, cy, '‹', {
      fontSize: '22px',
      fontStyle: '900',
      color: prevEnabled ? '#00ffff' : '#334466',
      fontFamily: Fonts.heading,
    });
    prev.setOrigin(0, 0.5);
    this.contentContainer.add(prev);

    if (prevEnabled) {
      const prevHit = this.scene.add.zone(x, y, 44, h).setOrigin(0, 0);
      prevHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 44, h), Phaser.Geom.Rectangle.Contains);
      this.contentContainer.add(prevHit);
      prevHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        if (this.infoPanelPage > 0) {
          this.infoPanelPage--;
          this.buildDisplay();
        }
      });
    }

    // Next arrow (right)
    const nextEnabled = this.infoPanelPage < this.infoPanelPages - 1;
    const next = this.scene.add.text(x + w - 8, cy, '›', {
      fontSize: '22px',
      fontStyle: '900',
      color: nextEnabled ? '#00ffff' : '#334466',
      fontFamily: Fonts.heading,
    });
    next.setOrigin(1, 0.5);
    this.contentContainer.add(next);

    if (nextEnabled) {
      const nextHit = this.scene.add.zone(x + w - 44, y, 44, h).setOrigin(0, 0);
      nextHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 44, h), Phaser.Geom.Rectangle.Contains);
      this.contentContainer.add(nextHit);
      nextHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        if (this.infoPanelPage < this.infoPanelPages - 1) {
          this.infoPanelPage++;
          this.buildDisplay();
        }
      });
    }

    // Dots (centered)
    const dotR = 3;
    const dotGap = 10;
    const totalDotsW = this.infoPanelPages * (dotR * 2) + (this.infoPanelPages - 1) * dotGap;
    let dx = x + w / 2 - totalDotsW / 2 + dotR;
    for (let i = 0; i < this.infoPanelPages; i++) {
      const dot = this.scene.add.graphics();
      if (i === this.infoPanelPage) {
        dot.fillStyle(0x00ffff, 1);
        dot.fillCircle(dx, cy, dotR);
      } else {
        dot.fillStyle(0x334466, 1);
        dot.fillCircle(dx, cy, dotR - 0.5);
      }
      this.contentContainer.add(dot);
      dx += dotR * 2 + dotGap;
    }
  }

  // ── Planet display ──────────────────────────────────────────

  private buildPlanetDisplay(p: SelectedPlanet): void {
    const isPlayerPlanet = p.ownerId === this.mmoState.getUserId();
    const isEarth = p.hexCoord.q === 0 && p.hexCoord.r === 0;

    // Portrait image — try Earth sprite, then planet-specific sprite by name
    let planetImageKey: string | null = null;
    if (isEarth) {
      planetImageKey = 'earth';
    } else if (p.planetName) {
      const nameKey = `planet-${p.planetName.toLowerCase().replace(/\s+/g, '-')}`;
      if (this.scene.textures.exists(nameKey)) {
        planetImageKey = nameKey;
      }
    }

    if (planetImageKey && this.scene.textures.exists(planetImageKey)) {
      const img = this.scene.add.image(
        PORT_LEFT + PORT_W / 2,
        PORT_TOP + PORT_H / 2,
        planetImageKey,
      );
      const tex = img.texture.getSourceImage();
      const scale = Math.min(PORT_W / tex.width, PORT_H / tex.height);
      img.setScale(scale);
      this.contentContainer.add(img);
    } else {
      // Fallback: programmatic planet graphic
      const gfx = this.scene.add.graphics();
      const cx = PORT_LEFT + PORT_W / 2;
      const cy = PORT_TOP + PORT_H / 2;
      const planetColor = isPlayerPlanet ? 0x3388ee : (p.ownerId ? 0xee4444 : 0x888888);
      gfx.fillStyle(planetColor, 0.8);
      gfx.fillCircle(cx, cy, 30);
      gfx.lineStyle(2, 0xffffff, 0.5);
      gfx.strokeCircle(cx, cy, 30);
      gfx.fillStyle(planetColor, 0.3);
      gfx.fillCircle(cx - 8, cy - 5, 8);
      gfx.fillCircle(cx + 10, cy + 8, 6);
      this.contentContainer.add(gfx);
    }

    // Combined HP + Shields bar under portrait
    const barStartX = STATS_LEFT;
    const textY = PORT_TOP + PORT_H + 4;
    const barY = textY + 14;
    const barH = 10;
    const barW = 175;
    const gap = 16;

    const combinedHp = p.currentHp + p.currentShields;
    const combinedMax = p.maxHp + p.maxShields;
    this.drawStatBarAbove(barStartX, textY, barY, barW, barH,
      combinedHp, combinedMax, 0x00ff88, I18n.t('stat.shields'), I18n.t('tooltip.planet_structural'), 'health');

    // Storage bar (right of combined HP) — show for Earth, neutral planets, and owned planets with storage
    const mineralCap = isEarth
      ? MINERAL_STORAGE_CAPACITY.earth
      : (p.maxStorage || 0);
    if (mineralCap > 0) {
      const mineralUsed = this.storageData
        ? this.storageData.reduce((sum, m) => sum + Number(m.amount), 0)
        : 0;
      this.drawStatBarAbove(barStartX + barW + gap, textY, barY, barW, barH,
        Math.round(mineralUsed), mineralCap, 0x4488ff, I18n.t('storage.label'), I18n.t('tooltip.mineral_storage'), 'storage');
    }

    // Planet name
    const displayName = p.planetName || 'Unknown Planet';
    const name = this.scene.add.text(NAME_LEFT, NAME_TOP, displayName, {
      fontSize: '20px',
      fontStyle: '900',
      color: isPlayerPlanet ? '#E0E8FF' : (p.ownerId ? '#FF8866' : '#AABBCC'),
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    name.setOrigin(0, 0.5);
    this.contentContainer.add(name);

    // Home planet badge
    if (p.isHomePlanet) {
      const badge = this.scene.add.text(NAME_LEFT + name.width + 10, NAME_TOP, '★ HOME', {
        fontSize: '9px',
        fontStyle: '900',
        color: '#FFD700',
        fontFamily: Fonts.heading,
        letterSpacing: 1,
      });
      badge.setOrigin(0, 0.5);
      this.contentContainer.add(badge);
    }

    // Owner line — human-readable label instead of UUID
    let ownerStr: string;
    if (!p.ownerId) {
      ownerStr = I18n.t('planet.neutral');
    } else if (isPlayerPlanet) {
      ownerStr = I18n.t('planet.your_planet');
    } else {
      ownerStr = I18n.t('planet.enemy');
    }
    const infoStr = `${ownerStr}  |  (${p.hexCoord.q}, ${p.hexCoord.r})`;
    const infoLine = this.scene.add.text(OWNER_LEFT, OWNER_TOP, infoStr, {
      fontSize: '10px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    infoLine.setOrigin(0, 0);
    this.contentContainer.add(infoLine);

    // Stats row at bottom
    const dockedUnits = this.mmoState.getDockedUnitsAtPlanet(p.id);

    const stats: { label: string; value: string; tooltip: string; color: string }[] = [
      { label: I18n.t('stat.docked'), value: `${dockedUnits.length}`, tooltip: I18n.t('tooltip.docked_planet'), color: '#00FFFF' },
    ];
    if (isEarth) {
      stats.push(
        { label: I18n.t('stat.fee'), value: '0.5%', tooltip: I18n.t('tooltip.fee_earth'), color: '#00FFFF' },
        { label: I18n.t('stat.status'), value: I18n.t('stat.active'), tooltip: I18n.t('tooltip.planet_status'), color: '#00FF88' },
      );
    }

    const statsY = DOCK_H - STATS_BOTTOM;
    let sx = STATS_LEFT;

    for (const stat of stats) {
      const groupX = sx;

      const lbl = this.scene.add.text(sx, statsY, stat.label, {
        fontSize: '11px',
        color: '#8899BB',
        fontFamily: Fonts.body,
        letterSpacing: 2,
      });
      lbl.setOrigin(0, 1);
      this.contentContainer.add(lbl);

      const val = this.scene.add.text(sx + lbl.width + STAT_VALUE_GAP, statsY, stat.value, {
        fontSize: '11px',
        fontStyle: '900',
        color: stat.color,
        fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 1);
      this.contentContainer.add(val);

      const groupW = lbl.width + STAT_VALUE_GAP + val.width;

      if (stat.tooltip) {
        const hitH = 16;
        const hitZone = this.scene.add.zone(groupX, statsY - hitH, groupW, hitH).setOrigin(0, 0);
        hitZone.setInteractive(
          new Phaser.Geom.Rectangle(0, 0, groupW, hitH),
          Phaser.Geom.Rectangle.Contains,
        );
        this.contentContainer.add(hitZone);

        const tipText = stat.tooltip;
        hitZone.on('pointerover', () => {
          this.showTooltip(groupX + groupW / 2, statsY - hitH - 4, tipText);
          this.scene.input.setDefaultCursor('pointer');
        });
        hitZone.on('pointerout', () => {
          this.hideTooltip();
          this.scene.input.setDefaultCursor('default');
        });
      }

      sx += groupW + STAT_GAP;
    }

    // ── Tab bar: BUILDINGS | PRODUCING ──────────────────────
    this.buildPlanetTabBar(p);
  }

  private buildPlanetTabBar(p: SelectedPlanet): void {
    const isPlayerPlanet = p.ownerId === this.mmoState.getUserId();
    const isEarth = p.hexCoord.q === 0 && p.hexCoord.r === 0;
    const isNeutral = !p.ownerId;
    const canBuild = isPlayerPlanet || isEarth || isNeutral;

    // Only show storage tab for planets the player owns or Earth
    const showStorage = isPlayerPlanet || isEarth;

    const tabs: { label: string; key: string }[] = [];
    // SCAN tab — always available so players can scan any planet
    tabs.push({ label: 'SCAN', key: 'scan' });
    if (showStorage) {
      tabs.push({ label: I18n.t('tab.storage'), key: 'storage' });
    }
    tabs.push({ label: I18n.t('tab.build'), key: 'producing' });

    // If active tab is hidden, reset to first available tab
    const validKeys = tabs.map(t => t.key);
    if (!validKeys.includes(this.activeTab)) {
      this.activeTab = tabs[0].key as any;
    }

    let tabX = TAB_ACTIONS_LEFT;
    const tabTexts: Phaser.GameObjects.Text[] = [];
    for (const tab of tabs) {
      const t = this.scene.add.text(tabX, TAB_TOP, tab.label, {
        fontSize: '10px',
        fontStyle: '900',
        color: this.activeTab === tab.key ? '#FFFFFF' : '#8899BB',
        fontFamily: Fonts.heading,
        letterSpacing: 2,
      });
      t.setOrigin(0, 0);
      this.contentContainer.add(t);
      this.makeTabHit(t, tab.key as any);
      tabTexts.push(t);
      tabX = t.x + t.width + 14;
    }

    // Active tab underline
    const activeIdx = tabs.findIndex(t => t.key === this.activeTab);
    const activeTabText = tabTexts[activeIdx >= 0 ? activeIdx : 0];
    const underline = this.scene.add.graphics();
    underline.lineStyle(3, 0x00ffff, 1);
    underline.lineBetween(activeTabText.x, TAB_UNDERLINE_Y, activeTabText.x + activeTabText.width, TAB_UNDERLINE_Y);
    this.contentContainer.add(underline);

    // Render active tab content
    if (this.activeTab === 'producing') {
      this.buildProducingTabContent(p, canBuild);
    } else if (this.activeTab === 'scan') {
      this.buildScanTabContent(p);
    } else {
      this.buildStorageTabContent(p);
    }
  }

  // ── BUILD (producing) tab content ─────────────────────────────

  private buildProducingTabContent(p: SelectedPlanet, canBuild: boolean): void {
    // Use build queue from state manager (real-time updates from server)
    const planetId = p.id || '';
    const pendingBuilds = this.mmoState.getPendingBuilds(planetId);
    // Also check old productionQueue from planet data as fallback
    const oldQueue: any[] = Array.isArray(p.productionQueue) ? p.productionQueue : [];
    const queue = pendingBuilds.length > 0 ? pendingBuilds : oldQueue;

    // Center of the visible tab label area (BUILDINGS...PRODUCING), not the full dock
    const tabCenterX = TAB_ACTIONS_LEFT + 92;

    if (queue.length === 0) {
      // Empty state message — centered in tab area
      const emptyText = this.scene.add.text(
        tabCenterX, ACTION_ROW_TOP + 14,
        canBuild ? I18n.t('station.nothing_producing') : I18n.t('station.no_production'),
        { fontSize: '11px', color: '#556688', fontFamily: Fonts.body },
      );
      emptyText.setOrigin(0.5, 0.5);
      this.contentContainer.add(emptyText);
    } else {
      // List production queue items with countdown timers
      const startY = ACTION_ROW_TOP + 2;
      const rowH = 18;
      const maxVisible = Math.min(queue.length, 4);

      for (let i = 0; i < maxVisible; i++) {
        const item = queue[i];
        const buildType: string = item?.unitType || item?.buildType || 'Unknown';
        const displayName = I18n.shipName(buildType);
        const y = startY + i * rowH;

        // Queue position
        const posText = this.scene.add.text(ACTION_ROW_LEFT, y, `${i + 1}.`, {
          fontSize: '11px',
          fontStyle: '900',
          color: '#FFCC00',
          fontFamily: Fonts.heading,
        });
        posText.setOrigin(0, 0);
        this.contentContainer.add(posText);

        // Item name
        const itemText = this.scene.add.text(ACTION_ROW_LEFT + 18, y, displayName, {
          fontSize: '11px',
          color: i === 0 ? '#FFCC00' : '#AABBCC',
          fontFamily: Fonts.body,
          letterSpacing: 1,
        });
        itemText.setOrigin(0, 0);
        this.contentContainer.add(itemText);

        // Countdown timer for pending builds
        const remaining = (item as any).remainingSeconds;
        if (remaining !== undefined && remaining !== null) {
          const secs = Math.max(0, Math.ceil(remaining));
          const timerStr = secs > 0 ? `${secs}s` : I18n.t('station.done');
          const timerColor = secs > 0 ? '#00CCFF' : '#00FF88';
          const timerText = this.scene.add.text(
            ACTION_ROW_LEFT + 18 + itemText.width + 10, y, timerStr,
            { fontSize: '10px', fontStyle: '900', color: timerColor, fontFamily: Fonts.heading },
          );
          timerText.setOrigin(0, 0);
          this.contentContainer.add(timerText);

          // Progress bar for first item
          if (i === 0 && (item as any).totalSeconds) {
            const total = (item as any).totalSeconds;
            const pct = Math.max(0, Math.min(1, 1 - secs / total));
            const barX = ACTION_ROW_LEFT;
            const barY = y + 14;
            const barW = 160;
            const barH = 4;

            const barGfx = this.scene.add.graphics();
            barGfx.fillStyle(0x1a1a3a, 0.8);
            barGfx.fillRoundedRect(barX, barY, barW, barH, 2);
            if (pct > 0) {
              barGfx.fillStyle(0x00ccff, 1);
              barGfx.fillRoundedRect(barX, barY, Math.max(barW * pct, 4), barH, 2);
            }
            this.contentContainer.add(barGfx);
          }
        } else if (i === 0) {
          // Legacy "IN PROGRESS" tag
          const tag = this.scene.add.text(ACTION_ROW_LEFT + 18 + itemText.width + 8, y, I18n.t('station.in_progress'), {
            fontSize: '8px',
            fontStyle: '900',
            color: '#00FF88',
            fontFamily: Fonts.heading,
            letterSpacing: 1,
          });
          tag.setOrigin(0, 0);
          this.contentContainer.add(tag);
        }
      }

      if (queue.length > maxVisible) {
        const moreY = startY + maxVisible * rowH;
        const moreText = this.scene.add.text(ACTION_ROW_LEFT + 18, moreY, `+${queue.length - maxVisible} more queued`, {
          fontSize: '9px',
          color: '#556688',
          fontFamily: Fonts.body,
        });
        moreText.setOrigin(0, 0);
        this.contentContainer.add(moreText);
      }
    }

    // Ship building removed — ships are purchased from the Ship Store
  }

  // ── Pill-shaped CTA button ───────────────────────────────────

  private buildPillCTA(
    cx: number, cy: number,
    label: string, tooltip: string,
    onClick: () => void,
  ): void {
    const padH = 12;
    const padV = 7;
    const borderR = 12;
    const borderW = 2;

    // Measure text first to size the pill
    const text = this.scene.add.text(0, 0, label, {
      fontSize: '10px',
      fontStyle: '900',
      color: '#00FFFF',
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    const tw = text.width;
    const th = text.height;
    const pillW = tw + padH * 2;
    const pillH = th + padV * 2;
    const pillX = cx - pillW / 2;         // center-aligned
    const pillY = cy - pillH / 2;

    // Background pill
    const gfx = this.scene.add.graphics();
    gfx.lineStyle(borderW, 0x00ffff, 0.6);
    gfx.strokeRoundedRect(pillX, pillY, pillW, pillH, borderR);
    gfx.fillStyle(0x00ffff, 0.08);
    gfx.fillRoundedRect(pillX, pillY, pillW, pillH, borderR);
    this.contentContainer.add(gfx);

    // Position text centered in pill
    text.setPosition(pillX + padH, pillY + padV);
    text.setOrigin(0, 0);
    this.contentContainer.add(text);

    // Hit zone covers the pill
    const hit = this.scene.add.zone(pillX, pillY, pillW, pillH).setOrigin(0, 0);
    hit.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, pillW, pillH),
      Phaser.Geom.Rectangle.Contains,
    );
    this.contentContainer.add(hit);

    hit.on('pointerover', () => {
      text.setColor('#FFFFFF');
      gfx.clear();
      gfx.lineStyle(borderW, 0x00ffff, 1);
      gfx.strokeRoundedRect(pillX, pillY, pillW, pillH, borderR);
      gfx.fillStyle(0x00ffff, 0.18);
      gfx.fillRoundedRect(pillX, pillY, pillW, pillH, borderR);
      this.showTooltip(pillX + pillW / 2, pillY - 4, tooltip);
      this.scene.input.setDefaultCursor('pointer');
    });
    hit.on('pointerout', () => {
      text.setColor('#00FFFF');
      gfx.clear();
      gfx.lineStyle(borderW, 0x00ffff, 0.6);
      gfx.strokeRoundedRect(pillX, pillY, pillW, pillH, borderR);
      gfx.fillStyle(0x00ffff, 0.08);
      gfx.fillRoundedRect(pillX, pillY, pillW, pillH, borderR);
      this.hideTooltip();
      this.scene.input.setDefaultCursor('default');
    });
    hit.on('pointerdown', onClick);
  }

  // ── Platform display ────────────────────────────────────────

  private buildPlatformDisplay(p: SelectedPlatformEntity): void {
    const userId = this.mmoState.getUserId();
    const isPlayerPlatform = p.userId === userId || p.isPublic;
    const cfg = PLATFORM_TIER_CONFIG[p.platformTier] || PLATFORM_TIER_CONFIG.basic;

    // Portrait: procedural hexagonal ring
    const gfx = this.scene.add.graphics();
    const cx = PORT_LEFT + PORT_W / 2;
    const cy = PORT_TOP + PORT_H / 2;
    gfx.fillStyle(cfg.hex, 0.4);
    this.drawStationHex(gfx, cx, cy, 26, true);
    gfx.lineStyle(2, cfg.hex, 0.9);
    this.drawStationHex(gfx, cx, cy, 30, false);
    // Inner ring for higher tiers
    if (p.platformTier !== 'basic') {
      gfx.lineStyle(1, cfg.hex, 0.5);
      this.drawStationHex(gfx, cx, cy, 20, false);
    }
    this.contentContainer.add(gfx);

    // Combined HP + Shields bar under portrait
    const barStartX = STATS_LEFT;
    const textY = PORT_TOP + PORT_H + 4;
    const barY = textY + 14;
    const barH = 10;
    const barW = 175;
    const gap = 16;

    const combinedHp = p.currentHp + p.currentShields;
    const combinedMax = p.maxHp + p.maxShields;
    this.drawStatBarAbove(barStartX, textY, barY, barW, barH,
      combinedHp, combinedMax, cfg.hex, I18n.t('stat.shields'), I18n.t('tooltip.station_structural'), 'health');

    // Mineral storage bar (right of HP)
    const mineralUsed = this.storageData
      ? this.storageData.reduce((sum, m) => sum + Number(m.amount), 0)
      : 0;
    this.drawStatBarAbove(barStartX + barW + gap, textY, barY, barW, barH,
      Math.round(mineralUsed), p.mineralStorageCapacity, 0x4488ff, I18n.t('storage.label'), I18n.t('tooltip.mineral_storage'), 'storage');

    // Platform name
    const name = this.scene.add.text(NAME_LEFT, NAME_TOP, cfg.label, {
      fontSize: '20px',
      fontStyle: '900',
      color: cfg.color,
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    name.setOrigin(0, 0.5);
    this.contentContainer.add(name);

    // Tier badge
    const tierLabel = p.platformTier.toUpperCase();
    const tierBadge = this.scene.add.text(NAME_LEFT + name.width + 10, NAME_TOP, tierLabel, {
      fontSize: '9px',
      fontStyle: '900',
      color: '#8899BB',
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    tierBadge.setOrigin(0, 0.5);
    this.contentContainer.add(tierBadge);

    // Owner line
    const ownerStr = p.isPublic ? I18n.t('platform.public') : (p.userId === userId ? I18n.t('station.your_station') : I18n.t('station.enemy'));
    const infoStr = `${ownerStr}  |  (${p.hexCoord.q}, ${p.hexCoord.r})`;
    const infoLine = this.scene.add.text(OWNER_LEFT, OWNER_TOP, infoStr, {
      fontSize: '10px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    infoLine.setOrigin(0, 0);
    this.contentContainer.add(infoLine);

    // Stats row at bottom: DOCKED, capabilities
    const dockedUnits = this.mmoState.getDockedUnitsAtPlanet(p.id);
    const caps: string[] = [];
    if (p.hasRepairBay) caps.push('REPAIR');
    if (p.hasShipyard) caps.push('SHIPYARD');
    if (p.hasUnitBuilding) caps.push('BUILD');
    if (p.hasOrbitalMining) caps.push('MINING');

    const stats = [
      { label: I18n.t('stat.docked'), value: `${dockedUnits.length}`, tooltip: I18n.t('tooltip.docked_station'), color: '#00FFFF' },
      { label: I18n.t('stat.status'), value: p.status.toUpperCase(), tooltip: I18n.t('tooltip.station_status'), color: p.status === 'active' ? '#00FF88' : '#FF4444' },
      { label: 'CAPS', value: caps.length > 0 ? caps.join(' ') : 'DOCK', tooltip: I18n.t('platform.capabilities'), color: '#FFD700' },
    ];

    const statsY = DOCK_H - STATS_BOTTOM;
    let sx = STATS_LEFT;

    for (const stat of stats) {
      const lbl = this.scene.add.text(sx, statsY, stat.label, {
        fontSize: '11px',
        color: '#8899BB',
        fontFamily: Fonts.body,
        letterSpacing: 2,
      });
      lbl.setOrigin(0, 1);
      this.contentContainer.add(lbl);

      const val = this.scene.add.text(sx + lbl.width + STAT_VALUE_GAP, statsY, stat.value, {
        fontSize: '11px',
        fontStyle: '900',
        color: stat.color,
        fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 1);
      this.contentContainer.add(val);

      sx += lbl.width + STAT_VALUE_GAP + val.width + STAT_GAP;
    }

    // Tab bar: STORAGE / MANAGE / BUILD (if capable)
    if (isPlayerPlatform) {
      this.buildPlatformTabBar(p);
    } else {
      this.buildEnemyActions(p.hexCoord, p.userId || '');
    }
  }

  private buildPlatformTabBar(p: SelectedPlatformEntity): void {
    const tabDefs: { label: string; key: string }[] = [
      { label: I18n.t('tab.storage'), key: 'storage' },
      { label: I18n.t('tab.manage'), key: 'manage' },
    ];
    if (p.hasUnitBuilding) {
      tabDefs.push({ label: I18n.t('tab.build'), key: 'producing' });
    }

    let tabX = TAB_ACTIONS_LEFT;
    const tabTexts: { text: Phaser.GameObjects.Text; key: string }[] = [];
    for (const def of tabDefs) {
      const t = this.scene.add.text(tabX, TAB_TOP, def.label, {
        fontSize: '10px',
        fontStyle: '900',
        color: this.activeTab === def.key ? '#FFFFFF' : '#8899BB',
        fontFamily: Fonts.heading,
        letterSpacing: 2,
      });
      t.setOrigin(0, 0);
      this.contentContainer.add(t);
      this.makeTabHit(t, def.key as any);
      tabTexts.push({ text: t, key: def.key });
      tabX += t.width + 14;
    }

    // Active tab underline
    const activeTabEntry = tabTexts.find(t => t.key === this.activeTab) || tabTexts[0];
    const underline = this.scene.add.graphics();
    underline.lineStyle(3, 0x00ffff, 1);
    underline.lineBetween(
      activeTabEntry.text.x, TAB_UNDERLINE_Y,
      activeTabEntry.text.x + activeTabEntry.text.width, TAB_UNDERLINE_Y,
    );
    this.contentContainer.add(underline);

    // Render active tab content
    if (this.activeTab === 'storage') {
      this.buildStorageTabContent(null as unknown as SelectedPlanet);
    } else if (this.activeTab === 'manage') {
      this.buildPlatformManageTab(p);
    } else if (this.activeTab === 'producing') {
      this.buildPlatformBuildTab(p);
    }
  }

  private buildPlatformManageTab(p: SelectedPlatformEntity): void {
    const contentLeft = TAB_ACTIONS_LEFT;
    const lineY = TAB_UNDERLINE_Y + 10;
    const rowH = 18;

    const manageStats = [
      { label: 'Storage', value: `${p.mineralStorageCapacity}`, color: '#4488FF' },
      { label: 'Status', value: p.status.charAt(0).toUpperCase() + p.status.slice(1), color: p.status === 'active' ? '#00FF88' : '#FF4444' },
      { label: 'Public', value: p.isPublic ? 'Yes' : 'No', color: p.isPublic ? '#00FF88' : '#FF4444' },
    ];

    for (let i = 0; i < manageStats.length; i++) {
      const stat = manageStats[i];
      const y = lineY + i * rowH;

      const lbl = this.scene.add.text(contentLeft, y, stat.label, {
        fontSize: '10px', color: '#667799', fontFamily: Fonts.body,
      });
      lbl.setOrigin(0, 0);
      this.contentContainer.add(lbl);

      const val = this.scene.add.text(contentLeft + 120, y, stat.value, {
        fontSize: '10px', fontStyle: '900', color: stat.color, fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 0);
      this.contentContainer.add(val);
    }

    // CTA button
    const tabCenterX = TAB_ACTIONS_LEFT + 92;
    this.buildPillCTA(
      tabCenterX, DOCK_H - STATS_BOTTOM - 12,
      I18n.t('platform.manage'),
      'Open platform management panel',
      () => {
        EventBus.emit('open-platform-management');
      },
    );
  }

  private buildPlatformBuildTab(p: SelectedPlatformEntity): void {
    const userId = this.mmoState.getUserId();
    const isPlayerPlatform = p.userId === userId || p.isPublic;
    const tabCenterX = TAB_ACTIONS_LEFT + 92;

    const pendingBuilds = this.mmoState.getPendingBuilds(p.id);

    if (pendingBuilds.length === 0) {
      const emptyText = this.scene.add.text(
        tabCenterX, ACTION_ROW_TOP + 14,
        isPlayerPlatform ? I18n.t('station.nothing_producing') : I18n.t('station.no_production'),
        { fontSize: '11px', color: '#556688', fontFamily: Fonts.body },
      );
      emptyText.setOrigin(0.5, 0.5);
      this.contentContainer.add(emptyText);
    }

    // Ship building removed — ships are purchased from the Ship Store
  }

  // ── Station display ─────────────────────────────────────────

  private buildStationDisplay(s: SelectedStation): void {
    const isPlayerStation = s.userId === this.mmoState.getUserId();

    // Station type label + color
    const stationConfig: Record<string, { color: string; hex: number; label: string }> = {
      waypoint: { color: '#00FFFF', hex: 0x00cccc, label: 'Waypoint' },
      depot:    { color: '#00FF88', hex: 0x00cc66, label: 'Depot' },
      fortress: { color: '#FF8800', hex: 0xcc6600, label: 'Fortress' },
      capital:  { color: '#FF0088', hex: 0xcc0066, label: 'Capital' },
    };
    const cfg = stationConfig[s.stationType] || stationConfig.waypoint;

    // Portrait: use station type sprite
    const STATION_SPRITE_MAP: Record<string, string> = {
      waypoint: 'station-waypoint', depot: 'station-depot',
      fortress: 'station-fortress', capital: 'station-capital',
    };
    const portraitKey = STATION_SPRITE_MAP[s.stationType] || 'station-waypoint';

    if (this.scene.textures.exists(portraitKey)) {
      const img = this.scene.add.image(PORT_LEFT + PORT_W / 2, PORT_TOP + PORT_H / 2, portraitKey);
      const tex = this.scene.textures.get(portraitKey).getSourceImage();
      const scale = Math.min(PORT_W / tex.width, PORT_H / tex.height);
      img.setScale(scale);
      this.contentContainer.add(img);
    } else {
      const gfx = this.scene.add.graphics();
      const cx = PORT_LEFT + PORT_W / 2;
      const cy = PORT_TOP + PORT_H / 2;
      gfx.fillStyle(cfg.hex, 0.6);
      this.drawStationHex(gfx, cx, cy, 28, true);
      gfx.lineStyle(2, cfg.hex, 0.9);
      this.drawStationHex(gfx, cx, cy, 30, false);
      this.contentContainer.add(gfx);
    }

    // Combined HP + Shields bar under portrait
    const barStartX = STATS_LEFT;
    const textY = PORT_TOP + PORT_H + 4;
    const barY = textY + 14;
    const barH = 10;
    const barW = 175;
    const gap = 16;

    const combinedHp = s.currentHp + (s.currentShields || 0);
    const combinedMax = s.maxHp + (s.maxShields || 0);
    this.drawStatBarAbove(barStartX, textY, barY, barW, barH,
      combinedHp, combinedMax, cfg.hex, I18n.t('stat.shields'), I18n.t('tooltip.station_structural'), 'health');

    // Mineral storage bar (right of HP)
    const mineralCap = MINERAL_STORAGE_CAPACITY[s.stationType] || 1000;
    const mineralUsed = this.storageData
      ? this.storageData.reduce((sum, m) => sum + Number(m.amount), 0)
      : 0;
    this.drawStatBarAbove(barStartX + barW + gap, textY, barY, barW, barH,
      Math.round(mineralUsed), mineralCap, 0x4488ff, I18n.t('storage.label'), I18n.t('tooltip.mineral_storage'), 'storage');

    // Station name
    const name = this.scene.add.text(NAME_LEFT, NAME_TOP, cfg.label, {
      fontSize: '20px',
      fontStyle: '900',
      color: cfg.color,
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    name.setOrigin(0, 0.5);
    this.contentContainer.add(name);

    // Station tier badge
    const tierLabel = s.stationType.toUpperCase();
    const tierBadge = this.scene.add.text(NAME_LEFT + name.width + 10, NAME_TOP, tierLabel, {
      fontSize: '9px',
      fontStyle: '900',
      color: '#8899BB',
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    tierBadge.setOrigin(0, 0.5);
    this.contentContainer.add(tierBadge);

    // Owner line
    const ownerStr = isPlayerStation ? I18n.t('station.your_station') : I18n.t('station.enemy');
    const infoStr = `${ownerStr}  |  (${s.hexCoord.q}, ${s.hexCoord.r})`;
    const infoLine = this.scene.add.text(OWNER_LEFT, OWNER_TOP, infoStr, {
      fontSize: '10px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    });
    infoLine.setOrigin(0, 0);
    this.contentContainer.add(infoLine);

    // Stats row at bottom: DOCKED, FEE, STATUS
    const dockedUnits = this.mmoState.getDockedUnitsAtPlanet(s.id);
    const stats = [
      { label: I18n.t('stat.docked'), value: `${dockedUnits.length}`, tooltip: I18n.t('tooltip.docked_station'), color: '#00FFFF' },
      { label: I18n.t('stat.fee'), value: `${s.stationFeePct}%`, tooltip: I18n.t('tooltip.fee_station'), color: '#00FFFF' },
      { label: I18n.t('stat.status'), value: s.status.toUpperCase(), tooltip: I18n.t('tooltip.station_status'), color: s.status === 'active' ? '#00FF88' : '#FF4444' },
    ];

    const statsY = DOCK_H - STATS_BOTTOM;
    let sx = STATS_LEFT;

    for (const stat of stats) {
      const groupX = sx;

      const lbl = this.scene.add.text(sx, statsY, stat.label, {
        fontSize: '11px',
        color: '#8899BB',
        fontFamily: Fonts.body,
        letterSpacing: 2,
      });
      lbl.setOrigin(0, 1);
      this.contentContainer.add(lbl);

      const val = this.scene.add.text(sx + lbl.width + STAT_VALUE_GAP, statsY, stat.value, {
        fontSize: '11px',
        fontStyle: '900',
        color: stat.color,
        fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 1);
      this.contentContainer.add(val);

      const groupW = lbl.width + STAT_VALUE_GAP + val.width;

      if (stat.tooltip) {
        const hitH = 16;
        const hitZone = this.scene.add.zone(groupX, statsY - hitH, groupW, hitH).setOrigin(0, 0);
        hitZone.setInteractive(
          new Phaser.Geom.Rectangle(0, 0, groupW, hitH),
          Phaser.Geom.Rectangle.Contains,
        );
        this.contentContainer.add(hitZone);

        const tipText = stat.tooltip;
        hitZone.on('pointerover', () => {
          this.showTooltip(groupX + groupW / 2, statsY - hitH - 4, tipText);
          this.scene.input.setDefaultCursor('pointer');
        });
        hitZone.on('pointerout', () => {
          this.hideTooltip();
          this.scene.input.setDefaultCursor('default');
        });
      }

      sx += groupW + STAT_GAP;
    }

    // Tab bar: owner gets full tabs, docked non-owner gets limited tabs, others get enemy actions
    if (isPlayerStation) {
      this.buildStationTabBar(s, true);
    } else {
      const isDockedHere = this.mmoState.getDockedUnitsAtPlanet(s.id).some(
        u => u.ownerId === this.mmoState.getUserId(),
      );
      if (isDockedHere) {
        this.buildStationTabBar(s, false);
      } else {
        this.buildEnemyActions(s.hexCoord, s.userId);
      }
    }
  }

  private buildStationTabBar(s: SelectedStation, isOwner: boolean): void {
    const mobile = isMobile(this.scene);
    const tabDefs: { label: string; key: string }[] = isOwner
      ? [
        { label: mobile ? 'STORE' : I18n.t('tab.storage'), key: 'storage' },
        { label: 'MINE', key: 'mine' },
        { label: mobile ? 'MGT' : I18n.t('tab.manage'), key: 'manage' },
        { label: mobile ? 'DSN' : 'DESIGN', key: 'design' },
        { label: 'TECH', key: 'tech' },
      ]
      : [
        { label: I18n.t('tab.storage'), key: 'storage' },
      ];

    // Add alliance tab if player is alliance leader/officer with a station selected
    if (isOwner) {
      const alliance = this.mmoState.getAlliance();
      if (alliance && alliance.leaderId === this.mmoState.getUserId()) {
        tabDefs.push({ label: mobile ? 'ALI' : 'ALLIANCE', key: 'alliance' });
      }
    }

    // Ensure activeTab is valid for current tab set
    const validKeys = tabDefs.map(d => d.key);
    if (!validKeys.includes(this.activeTab)) {
      this.activeTab = tabDefs[0].key as any;
    }

    const effectiveTabLeft = mobile ? 12 : TAB_ACTIONS_LEFT;
    const letterSpacing = (mobile && isOwner) ? 1 : 2;
    let tabX = effectiveTabLeft;
    const tabTexts: { text: Phaser.GameObjects.Text; key: string }[] = [];
    for (const def of tabDefs) {
      const t = this.scene.add.text(tabX, TAB_TOP, def.label, {
        fontSize: '10px',
        fontStyle: '900',
        color: this.activeTab === def.key ? '#FFFFFF' : '#8899BB',
        fontFamily: Fonts.heading,
        letterSpacing,
      });
      t.setOrigin(0, 0);
      this.contentContainer.add(t);
      this.makeTabHit(t, def.key as any);
      tabTexts.push({ text: t, key: def.key });
      tabX += t.width + (mobile && isOwner ? 8 : 14);
    }

    // Active tab underline
    const activeTabEntry = tabTexts.find(t => t.key === this.activeTab) || tabTexts[0];
    const underline = this.scene.add.graphics();
    underline.lineStyle(3, 0x00ffff, 1);
    underline.lineBetween(
      activeTabEntry.text.x, TAB_UNDERLINE_Y,
      activeTabEntry.text.x + activeTabEntry.text.width, TAB_UNDERLINE_Y,
    );
    this.contentContainer.add(underline);

    // Render active tab content
    if (this.activeTab === 'storage') {
      this.buildStorageTabContent(null as unknown as SelectedPlanet);
    } else if (this.activeTab === 'mine') {
      this.buildStationMineTab(s);
    } else if (this.activeTab === 'manage') {
      // Directly open station management dialog
      EventBus.emit('open-station-management');
      this.activeTab = 'storage'; // Reset tab so re-clicking manage opens dialog again
    } else if (this.activeTab === 'buy_ship') {
      this.buildStationBuyShipTab(s);
    } else if (this.activeTab === 'design') {
      this.buildStationDesignTab(s);
    } else if (this.activeTab === 'tech' || this.activeTab === 'research') {
      this.buildStationTechTab(s);
    } else if (this.activeTab === 'alliance') {
      this.buildStationAllianceTab(s);
    }
  }

  // Manage tab now directly opens StationManagementDialog (no intermediate tab content)

  private buildStationBuyShipTab(_s: SelectedStation): void {
    const tabCenterX = TAB_ACTIONS_LEFT + 92;

    const line1 = this.scene.add.text(
      tabCenterX, ACTION_ROW_TOP + 6,
      'Ships are purchased with USDC / USDT',
      { fontSize: '10px', color: '#556688', fontFamily: Fonts.body },
    );
    line1.setOrigin(0.5, 0.5);
    this.contentContainer.add(line1);

    const line2 = this.scene.add.text(
      tabCenterX, ACTION_ROW_TOP + 22,
      'Open the Ship Store from the top nav',
      { fontSize: '10px', color: '#8b9dc3', fontFamily: Fonts.body },
    );
    line2.setOrigin(0.5, 0.5);
    this.contentContainer.add(line2);
  }

  private buildStationDesignTab(s: SelectedStation): void {
    const tabCenterX = TAB_ACTIONS_LEFT + 92;

    const infoText = this.scene.add.text(
      tabCenterX, ACTION_ROW_TOP + 6,
      'Create custom ship designs',
      { fontSize: '10px', color: '#556688', fontFamily: Fonts.body },
    );
    infoText.setOrigin(0.5, 0.5);
    this.contentContainer.add(infoText);

    this.buildPillCTA(
      tabCenterX, DOCK_H - STATS_BOTTOM - 12,
      'Create Design',
      'Design a custom ship configuration',
      () => EventBus.emit('open-station-design', { stationId: s.id }),
    );
  }

  // ── Planet Scan Tab ──────────────────────────────────────────

  private buildScanTabContent(p: SelectedPlanet): void {
    const contentLeft = TAB_ACTIONS_LEFT;
    const lineY = TAB_UNDERLINE_Y + 10;
    const tabCenterX = TAB_ACTIONS_LEFT + 92;

    // Request scan data for this planet
    if (!this.scanData && !this.isScanning) {
      EventBus.emit('send_message', {
        type: 'mmo_get_planet_scan',
        payload: { planetId: p.id },
      });
    }

    if (this.scanData) {
      // Show scan quality
      const qualLabel = this.scene.add.text(contentLeft, lineY, `SCAN QUALITY: ${Math.round(this.scanData.scanQuality)}%`, {
        fontSize: '10px', fontStyle: '900', color: '#00FFFF', fontFamily: Fonts.heading,
      });
      this.contentContainer.add(qualLabel);

      // Show scanned resources
      const resources = this.scanData.resources;
      const entries = Object.entries(resources);
      if (entries.length === 0) {
        const noRes = this.scene.add.text(contentLeft, lineY + 16, 'No deposits detected', {
          fontSize: '10px', color: '#556688', fontFamily: Fonts.body,
        });
        this.contentContainer.add(noRes);
      } else {
        let ry = lineY + 16;
        for (const [, info] of entries) {
          if (ry > DOCK_H - 30) break;
          const RARITY_COLORS: Record<string, string> = {
            common: '#AAAAAA', uncommon: '#00FF88', rare: '#4488FF', epic: '#CC66FF', legendary: '#FFD700',
          };
          const color = RARITY_COLORS[info.rarity] || '#AAAAAA';
          const txt = this.scene.add.text(contentLeft, ry, `${info.mineralName}: ~${Math.round(info.estimatedAmount)}`, {
            fontSize: '10px', color, fontFamily: Fonts.body,
          });
          this.contentContainer.add(txt);
          ry += 14;
        }
      }

      // Rescan button
      this.buildPillCTA(tabCenterX, DOCK_H - STATS_BOTTOM - 12, 'RESCAN', 'Scan again for better accuracy', () => {
        this.triggerPlanetScan(p);
      });
    } else {
      // No scan yet — show scan button
      const hint = this.scene.add.text(tabCenterX, lineY + 10, 'Planet not yet scanned', {
        fontSize: '11px', color: '#556688', fontFamily: Fonts.body,
      });
      hint.setOrigin(0.5, 0);
      this.contentContainer.add(hint);

      this.buildPillCTA(tabCenterX, DOCK_H - STATS_BOTTOM - 12, this.isScanning ? 'SCANNING...' : 'SCAN PLANET', 'Scan to reveal mineral deposits', () => {
        if (!this.isScanning) this.triggerPlanetScan(p);
      });
    }
  }

  private triggerPlanetScan(p: SelectedPlanet): void {
    // Find a player unit or station within 1 hex of this planet
    const units = this.mmoState.getPlayerUnits();
    const allStations = this.mmoState.getAllStations();
    const userId = this.mmoState.getUserId();

    // Check units first
    for (const u of units) {
      const dist = Math.max(Math.abs(u.position.q - p.hexCoord.q), Math.abs(u.position.r - p.hexCoord.r),
        Math.abs((-u.position.q - u.position.r) - (-p.hexCoord.q - p.hexCoord.r)));
      if (dist <= 1) {
        this.isScanning = true;
        EventBus.emit('send_message', {
          type: 'mmo_scan_planet',
          payload: { entityType: 'unit', entityId: u.id, planetId: p.id },
        });
        this.buildDisplay();
        return;
      }
    }

    // Check stations
    for (const s of allStations) {
      if (s.userId !== userId) continue;
      const dist = Math.max(Math.abs(s.position.q - p.hexCoord.q), Math.abs(s.position.r - p.hexCoord.r),
        Math.abs((-s.position.q - s.position.r) - (-p.hexCoord.q - p.hexCoord.r)));
      if (dist <= 1) {
        this.isScanning = true;
        EventBus.emit('send_message', {
          type: 'mmo_scan_planet',
          payload: { entityType: 'station', entityId: s.id, planetId: p.id },
        });
        this.buildDisplay();
        return;
      }
    }

    // No entity nearby — nothing to do
  }

  private onScanResult(payload: any): void {
    this.isScanning = false;
    this.scanData = payload;
    if (this.activeTab === 'scan') {
      this.buildDisplay();
    }
  }

  private onPlanetScanData(payload: any): void {
    if (payload.scan) {
      this.scanData = {
        scanQuality: payload.scan.scan_quality || payload.scan.scanQuality || 0,
        resources: payload.scan.scanned_resources || payload.scan.scannedResources || {},
      };
    } else {
      this.scanData = null;
    }
    if (this.activeTab === 'scan') {
      this.buildDisplay();
    }
  }

  // ── Station Mining Tab ─────────────────────────────────────

  private buildStationMineTab(s: SelectedStation): void {
    const contentLeft = TAB_ACTIONS_LEFT;
    const lineY = TAB_UNDERLINE_Y + 10;
    const tabCenterX = TAB_ACTIONS_LEFT + 92;

    // Check if station is currently mining
    const miningPlanetId = (s as any).miningPlanetId;

    if (miningPlanetId) {
      // Show mining status
      const statusLabel = this.scene.add.text(contentLeft, lineY, 'STATUS: MINING', {
        fontSize: '10px', fontStyle: '900', color: '#00FF88', fontFamily: Fonts.heading,
      });
      this.contentContainer.add(statusLabel);

      // Find the planet name
      const allPlanets = this.mmoState.getAllPlanets();
      const miningPlanet = allPlanets.find(p => p.id === miningPlanetId);
      const planetName = miningPlanet?.name || 'Unknown Planet';

      const targetLabel = this.scene.add.text(contentLeft, lineY + 16, `Target: ${planetName}`, {
        fontSize: '10px', color: '#8899BB', fontFamily: Fonts.body,
      });
      this.contentContainer.add(targetLabel);

      // Stop mining button
      this.buildPillCTA(tabCenterX, DOCK_H - STATS_BOTTOM - 12, 'STOP MINING', 'Stop extracting minerals', () => {
        EventBus.emit('send_message', {
          type: 'mmo_station_stop_mining',
          payload: { stationId: s.id },
        });
      });
    } else {
      // Show available scanned planets nearby
      const allPlanets = this.mmoState.getAllPlanets();
      const nearbyPlanets = allPlanets.filter(p => {
        const dist = Math.max(Math.abs(s.hexCoord.q - p.position.q), Math.abs(s.hexCoord.r - p.position.r),
          Math.abs((-s.hexCoord.q - s.hexCoord.r) - (-p.position.q - p.position.r)));
        return dist <= 1;
      });

      if (nearbyPlanets.length === 0) {
        const noTarget = this.scene.add.text(tabCenterX, lineY + 10, 'No planets in range', {
          fontSize: '11px', color: '#556688', fontFamily: Fonts.body,
        });
        noTarget.setOrigin(0.5, 0);
        this.contentContainer.add(noTarget);

        const hint = this.scene.add.text(tabCenterX, lineY + 28, 'Move station within 1 hex of a planet', {
          fontSize: '9px', color: '#445566', fontFamily: Fonts.body,
        });
        hint.setOrigin(0.5, 0);
        this.contentContainer.add(hint);
      } else {
        const header = this.scene.add.text(contentLeft, lineY, 'NEARBY PLANETS', {
          fontSize: '9px', fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 2,
        });
        this.contentContainer.add(header);

        let ry = lineY + 16;
        for (const planet of nearbyPlanets) {
          if (ry > DOCK_H - 40) break;
          const name = planet.name || `(${planet.position.q}, ${planet.position.r})`;
          const btn = this.scene.add.text(contentLeft, ry, `▶ MINE ${name}`, {
            fontSize: '10px', fontStyle: '900', color: '#00FFFF', fontFamily: Fonts.heading,
          });
          this.contentContainer.add(btn);

          const hitZone = this.scene.add.zone(contentLeft, ry, btn.width + 10, 14).setOrigin(0, 0);
          hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btn.width + 10, 14), Phaser.Geom.Rectangle.Contains);
          this.contentContainer.add(hitZone);

          const planetRef = planet;
          hitZone.on('pointerover', () => {
            btn.setColor('#FFFFFF');
            this.scene.input.setDefaultCursor('pointer');
          });
          hitZone.on('pointerout', () => {
            btn.setColor('#00FFFF');
            this.scene.input.setDefaultCursor('default');
          });
          hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            pointer.event.stopPropagation();
            EventBus.emit('send_message', {
              type: 'mmo_station_start_mining',
              payload: { stationId: s.id, planetId: planetRef.id },
            });
          });

          ry += 18;
        }
      }
    }
  }

  private onStationMineStarted(_payload: any): void {
    this.buildDisplay();
  }

  private onStationMineStopped(_payload: any): void {
    this.buildDisplay();
  }

  private onStationMineResult(_payload: any): void {
    // Mining tick result — could show a toast or just refresh storage
    if (this.activeTab === 'storage') {
      this.fetchStorage();
    }
  }

  private buildStationTechTab(s: SelectedStation): void {
    const tabCenterX = TAB_ACTIONS_LEFT + 92;
    let btnY = ACTION_ROW_TOP - 4;

    this.buildPillCTA(
      tabCenterX, btnY,
      'RESEARCH PROJECTS',
      'Open research board for this station',
      () => EventBus.emit('open-station-research', { stationId: s.id }),
    );

    btnY += 32;

    this.buildPillCTA(
      tabCenterX, btnY,
      'TECH TREE',
      'View technologies available at this station',
      () => EventBus.emit('open-station-tech', { stationId: s.id }),
    );
  }

  // ── Alliance tab: Set as HQ, Tax Rate, Deploy Structures ───

  private buildStationAllianceTab(s: SelectedStation): void {
    const tabCenterX = TAB_ACTIONS_LEFT + 92;
    let btnY = ACTION_ROW_TOP - 4;
    const alliance = this.mmoState.getAlliance();
    if (!alliance) return;

    const mmoNetwork = ServiceLocator.get<MMONetworkManager>(Services.MMO_NETWORK);

    // Check if this station is already the HQ by examining territories
    const territories = this.mmoState.getTerritories();
    const myTerritory = territories.find(t => t.allianceId === alliance.id);
    const isCurrentHQ = myTerritory && myTerritory.hqQ === s.hexCoord.q && myTerritory.hqR === s.hexCoord.r;

    // "Set as HQ" button — only show if NOT already HQ
    if (!isCurrentHQ) {
      this.buildPillCTA(
        tabCenterX, btnY,
        'SET AS HQ',
        'Designate this station as alliance headquarters',
        () => mmoNetwork.sendDesignateHQ(s.id),
      );
    } else {
      const hqLabel = this.scene.add.text(tabCenterX, btnY, 'ALLIANCE HQ', {
        fontSize: '10px', fontStyle: '900', color: '#00FF88', fontFamily: Fonts.heading, letterSpacing: 2,
      });
      hqLabel.setOrigin(0.5, 0.5);
      this.contentContainer.add(hqLabel);
    }

    btnY += 28;

    // Tax rate display + slider (0-50%)
    const currentTaxRate = myTerritory ? Math.round((myTerritory as any).taxRate ?? 0) : 0;
    const taxLabel = this.scene.add.text(tabCenterX - 60, btnY, `TAX: ${currentTaxRate}%`, {
      fontSize: '10px', fontStyle: '900', color: '#FFCC00', fontFamily: Fonts.heading, letterSpacing: 1,
    });
    taxLabel.setOrigin(0, 0.5);
    this.contentContainer.add(taxLabel);

    // Simple slider: bar + draggable handle
    const sliderX = tabCenterX - 20;
    const sliderW = 100;
    const sliderY = btnY;

    // Track
    const trackGfx = this.scene.add.graphics();
    trackGfx.fillStyle(0x334455, 1);
    trackGfx.fillRoundedRect(sliderX, sliderY - 3, sliderW, 6, 3);
    this.contentContainer.add(trackGfx);

    // Fill
    const fillGfx = this.scene.add.graphics();
    const fillW = (currentTaxRate / 50) * sliderW;
    fillGfx.fillStyle(0xffcc00, 0.8);
    fillGfx.fillRoundedRect(sliderX, sliderY - 3, fillW, 6, 3);
    this.contentContainer.add(fillGfx);

    // Clickable track zone for setting tax rate
    const trackHit = this.scene.add.zone(sliderX, sliderY - 8, sliderW, 16).setOrigin(0, 0);
    trackHit.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, sliderW, 16),
      Phaser.Geom.Rectangle.Contains,
    );
    this.contentContainer.add(trackHit);

    trackHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const localX = pointer.x - this.x - sliderX;
      const pct = Math.max(0, Math.min(50, Math.round((localX / sliderW) * 50)));
      mmoNetwork.sendSetTaxRate(pct);
      taxLabel.setText(`TAX: ${pct}%`);
      fillGfx.clear();
      fillGfx.fillStyle(0xffcc00, 0.8);
      fillGfx.fillRoundedRect(sliderX, sliderY - 3, (pct / 50) * sliderW, 6, 3);
    });

    btnY += 28;

    // Deploy structure buttons
    const structureTypes = [
      { type: 'observation_post', label: 'OBS POST' },
      { type: 'turret', label: 'TURRET' },
      { type: 'warp_disruptor', label: 'DISRUPTOR' },
      { type: 'shield_relay', label: 'SHIELD' },
    ];

    let deployX = tabCenterX - 70;
    for (const st of structureTypes) {
      const pillLabel = st.label;
      const padH = 6;
      const padV = 4;
      const text = this.scene.add.text(0, 0, pillLabel, {
        fontSize: '8px', fontStyle: '900', color: '#00FFFF', fontFamily: Fonts.heading, letterSpacing: 1,
      });
      const tw = text.width;
      const th = text.height;
      const pillW = tw + padH * 2;
      const pillH = th + padV * 2;
      const pillX = deployX;
      const pillY = btnY - pillH / 2;

      const gfx = this.scene.add.graphics();
      gfx.lineStyle(1, 0x00ffff, 0.5);
      gfx.strokeRoundedRect(pillX, pillY, pillW, pillH, 6);
      gfx.fillStyle(0x00ffff, 0.06);
      gfx.fillRoundedRect(pillX, pillY, pillW, pillH, 6);
      this.contentContainer.add(gfx);

      text.setPosition(pillX + padH, pillY + padV);
      text.setOrigin(0, 0);
      this.contentContainer.add(text);

      const hit = this.scene.add.zone(pillX, pillY, pillW, pillH).setOrigin(0, 0);
      hit.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, pillW, pillH),
        Phaser.Geom.Rectangle.Contains,
      );
      this.contentContainer.add(hit);

      const structureType = st.type;
      hit.on('pointerover', () => {
        text.setColor('#FFFFFF');
        this.scene.input.setDefaultCursor('pointer');
      });
      hit.on('pointerout', () => {
        text.setColor('#00FFFF');
        this.scene.input.setDefaultCursor('default');
      });
      hit.on('pointerdown', () => {
        EventBus.emit('enter-structure-deploy-mode', { structureType });
      });

      deployX += pillW + 4;
    }
  }

  /** Draw a flat-top hexagon for station portraits */
  private drawStationHex(
    graphics: Phaser.GameObjects.Graphics,
    cx: number, cy: number, size: number, fill: boolean,
  ): void {
    graphics.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      const px = cx + size * Math.cos(angle);
      const py = cy + size * Math.sin(angle);
      if (i === 0) graphics.moveTo(px, py);
      else graphics.lineTo(px, py);
    }
    graphics.closePath();
    if (fill) graphics.fillPath();
    else graphics.strokePath();
  }

  // ── Stats row ─────────────────────────────────────────────

  private buildStats(u: SelectedUnit): void {
    const statTooltipMap = getStatTooltips();
    const stats = [
      { label: I18n.t('stat.speed'), value: u.speed, tooltipKey: 'SPEED' },
      { label: I18n.t('stat.attack'), value: u.attack, tooltipKey: 'ATTACK' },
      { label: I18n.t('stat.defense'), value: u.defense, tooltipKey: 'DEFENSE' },
    ];

    const statsY = DOCK_H - STATS_BOTTOM;
    let sx = STATS_LEFT;

    for (const stat of stats) {
      const groupX = sx;

      // Label
      const lbl = this.scene.add.text(sx, statsY, stat.label, {
        fontSize: '11px',
        color: '#8899BB',
        fontFamily: Fonts.body,
        letterSpacing: 2,
      });
      lbl.setOrigin(0, 1);
      this.contentContainer.add(lbl);

      // Value (cyan, to the right of label)
      const val = this.scene.add.text(sx + lbl.width + STAT_VALUE_GAP, statsY, String(stat.value ?? 0), {
        fontSize: '11px',
        fontStyle: '900',
        color: '#00FFFF',
        fontFamily: Fonts.heading,
      });
      val.setOrigin(0, 1);
      this.contentContainer.add(val);

      const groupW = lbl.width + STAT_VALUE_GAP + val.width;

      // Tooltip hit zone over label + value
      const tipText = statTooltipMap[stat.tooltipKey];
      if (tipText) {
        const hitH = 16;
        const hitZone = this.scene.add.zone(groupX, statsY - hitH, groupW, hitH).setOrigin(0, 0);
        hitZone.setInteractive(
          new Phaser.Geom.Rectangle(0, 0, groupW, hitH),
          Phaser.Geom.Rectangle.Contains,
        );
        this.contentContainer.add(hitZone);

        hitZone.on('pointerover', () => {
          this.showTooltip(groupX + groupW / 2, statsY - hitH - 4, tipText);
          this.scene.input.setDefaultCursor('pointer');
        });
        hitZone.on('pointerout', () => {
          this.hideTooltip();
          this.scene.input.setDefaultCursor('default');
        });
      }

      sx += groupW + STAT_GAP;
    }
  }

  // ── Action area (tabs + buttons) ──────────────────────────

  private buildActionArea(u: SelectedUnit): void {
    const capacity = this.cargoData?.cargoCapacity ?? u.cargoCapacity ?? 100;
    const used = this.cargoData?.cargoUsed ?? u.cargoUsed ?? 0;
    const cargoFull = capacity > 0 && used >= capacity;
    const cargoLabel = cargoFull ? `\u26A0 CARGO ${Math.round(used)}/${capacity}` : `CARGO ${Math.round(used)}/${capacity}`;

    const tabDefs: { label: string; key: 'actions' | 'abilities' | 'cargo' | 'crew'; fullWarning?: boolean }[] = [
      { label: I18n.t('tab.actions'), key: 'actions' },
      { label: I18n.t('tab.abilities'), key: 'abilities' },
      { label: cargoLabel, key: 'cargo', fullWarning: cargoFull },
      { label: I18n.t('tab.crew'), key: 'crew' },
    ];

    // Sidebar mode: tabs spill across the viewport as wide horizontal text in
    // the legacy renderer. Route to the compact 2×2 grid header instead and
    // dispatch to sidebar-sized tab content builders.
    const mobileMode = isMobile(this.scene);
    if (mobileMode && this.inSidebarMode) {
      this.buildSidebarTabsHeader(cargoFull, used, capacity);
      if (this.activeTab === 'actions') {
        this.buildActionButtons();
      } else if (this.activeTab === 'abilities') {
        this.buildSidebarAbilitiesContent(u);
      } else if (this.activeTab === 'cargo') {
        this.buildSidebarCargoContent(u);
      } else if (this.activeTab === 'crew') {
        this.buildSidebarCrewContent(u);
      }
      return;
    }

    const effectiveTabLeft = mobileMode ? 12 : TAB_ACTIONS_LEFT;
    let tabX = effectiveTabLeft;
    const tabTexts: { text: Phaser.GameObjects.Text; key: string }[] = [];
    this._lastTabRightEdge = effectiveTabLeft;
    for (const def of tabDefs) {
      let color: string;
      if (def.fullWarning) {
        color = '#ff4444';
      } else {
        color = this.activeTab === def.key ? '#FFFFFF' : '#8899BB';
      }

      const t = this.scene.add.text(tabX, TAB_TOP, def.label, {
        fontSize: '10px',
        fontStyle: '900',
        color,
        fontFamily: Fonts.heading,
        letterSpacing: 2,
      });
      t.setOrigin(0, 0);
      this.contentContainer.add(t);
      this.makeTabHit(t, def.key);
      tabTexts.push({ text: t, key: def.key });

      // Tooltip on hover for cargo-full warning
      if (def.fullWarning) {
        const hitZone = this.scene.add.zone(tabX, TAB_TOP, t.width, t.height).setOrigin(0, 0).setInteractive({ useHandCursor: true });
        hitZone.on('pointerover', () => {
          this.showTooltip(tabX + t.width / 2, TAB_TOP - 4, 'Cargo full! Dock at a planet or station to deposit.');
        });
        hitZone.on('pointerout', () => { this.hideTooltip(); });
        hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          pointer.event.stopPropagation();
          this.hideTooltip();
          this.activeTab = 'cargo';
          this.buildDisplay();
        });
        this.contentContainer.add(hitZone);
      }

      this._lastTabRightEdge = tabX + t.width;
      tabX += t.width + 18;
    }

    // Active tab underline — red when cargo is full and cargo tab is active
    const activeTabEntry = tabTexts.find(t => t.key === this.activeTab) || tabTexts[0];
    const underlineColor = (cargoFull && this.activeTab === 'cargo') ? 0xff4444 : 0x00ffff;
    const underline = this.scene.add.graphics();
    underline.lineStyle(3, underlineColor, 1);
    underline.lineBetween(activeTabEntry.text.x, TAB_UNDERLINE_Y, activeTabEntry.text.x + activeTabEntry.text.width, TAB_UNDERLINE_Y);
    this.contentContainer.add(underline);

    // Tab content
    if (this.activeTab === 'actions') {
      this.buildActionButtons();
    } else if (this.activeTab === 'abilities') {
      this.buildAbilityButtons(u);
    } else if (this.activeTab === 'cargo') {
      this.buildCargoTab(u);
    } else if (this.activeTab === 'crew') {
      this.buildCrewTab(u);
    }
  }

  private makeTabHit(tab: Phaser.GameObjects.Text, which: 'actions' | 'abilities' | 'cargo' | 'crew' | 'upgrades' | 'buildings' | 'producing' | 'storage' | 'manage' | 'buy_ship' | 'design' | 'research' | 'tech' | 'scan' | 'mine'): void {
    const hit = this.scene.add.zone(tab.x, tab.y, tab.width, tab.height + 12).setOrigin(0, 0);
    hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, tab.width, tab.height + 12), Phaser.Geom.Rectangle.Contains);
    this.contentContainer.add(hit);
    hit.on('pointerdown', () => {
      if (which === 'cargo') { this.cargoData = null; this.isLoadingCargo = false; }
      if (which === 'crew') { this.crewData = null; this.isLoadingCrew = false; }
      this.activeTab = which; this.buildDisplay();
    });
    hit.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
    hit.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
  }

  // ── Cargo tab content ─────────────────────────────────────

  private buildCargoTab(u: SelectedUnit): void {
    const MINERAL_NAMES: Record<string, string> = {
      min_iron: 'Iron', min_copper: 'Copper', min_titanium: 'Titanium',
      min_platinum: 'Platinum', min_rhodium: 'Rhodium', min_palladium: 'Palladium',
      min_iridium: 'Iridium', min_darkmat: 'Dark Matter',
    };
    const MINERAL_COLORS: Record<string, string> = {
      min_iron: '#8a8a8a', min_copper: '#b87333', min_titanium: '#c0c0d0',
      min_platinum: '#e5e4e2', min_rhodium: '#ffd700', min_palladium: '#ced8f6',
      min_iridium: '#f0f0ff', min_darkmat: '#6600cc',
    };
    // Rarity order: legendary → epic → rare → uncommon → common (rarest first)
    const MINERAL_ORDER: string[] = [
      'min_darkmat',                   // legendary
      'min_iridium',                   // epic
      'min_rhodium', 'min_palladium',     // rare
      'min_titanium', 'min_platinum',  // uncommon
      'min_iron', 'min_copper',        // common
    ];

    // Content area: ACTIONS left edge → right edge of last tab label
    const contentLeft = TAB_ACTIONS_LEFT;
    const contentRight = this._lastTabRightEdge;
    const contentW = contentRight - contentLeft;

    // Capacity bar spanning ACTIONS → end of CARGO label
    const capacity = this.cargoData?.cargoCapacity ?? u.cargoCapacity ?? 100;
    const used = this.cargoData?.cargoUsed ?? u.cargoUsed ?? 0;
    const pct = capacity > 0 ? Math.min(used / capacity, 1) : 0;
    const barY = TAB_UNDERLINE_Y + 6;
    const barW = contentW;
    const barH = 8;

    const barBg = this.scene.add.graphics();
    barBg.fillStyle(0x1a2a3a, 1);
    barBg.fillRoundedRect(contentLeft, barY, barW, barH, 3);
    this.contentContainer.add(barBg);

    if (pct > 0) {
      const fillColor = pct >= 0.9 ? 0xff4444 : pct >= 0.7 ? 0xffaa00 : 0x00ccff;
      const barFill = this.scene.add.graphics();
      barFill.fillStyle(fillColor, 0.9);
      barFill.fillRoundedRect(contentLeft, barY, Math.max(barW * pct, 6), barH, 3);
      this.contentContainer.add(barFill);
    }

    // Fetch cargo data if needed
    if (!this.cargoData && !this.isLoadingCargo) {
      this.fetchCargo(u.id);
    }

    const listTop = barY + barH + 8;
    const listBottom = DOCK_H - STATS_BOTTOM - 4;
    const availH = listBottom - listTop;

    if (this.isLoadingCargo) {
      const loading = this.scene.add.text(contentLeft, listTop, 'Loading cargo...', {
        fontSize: '11px', color: '#6688aa', fontFamily: Fonts.body,
      });
      loading.setOrigin(0, 0);
      this.contentContainer.add(loading);
      return;
    }

    if (this.cargoData) {
      const entries = Object.entries(this.cargoData.minerals).filter(([, qty]) => qty > 0);

      const hasCrypto = (this.cargoData.cryptoTokens?.length ?? 0) > 0;
      const hasComponents = (this.cargoData.components?.length ?? 0) > 0;
      if (entries.length === 0 && this.cargoData.stellarValue <= 0 && !hasComponents && !hasCrypto) {
        const empty = this.scene.add.text(contentLeft + contentW / 2, listTop + 16, 'Cargo hold is empty', {
          fontSize: '11px', color: '#556677', fontFamily: Fonts.body, fontStyle: 'italic',
        });
        empty.setOrigin(0.5, 0);
        this.contentContainer.add(empty);
        return;
      }

      // Collect entries, prepend USDC reward (from golden asteroid mining), then sort minerals by rarity
      const allEntries: { id: string; name: string; qty: number; color: string; valueLabel?: string }[] = [];
      if (this.cargoData.stellarValue > 0) {
        allEntries.push({
          id: 'usdc_reward',
          name: 'USDC',
          qty: Math.ceil(this.cargoData.stellarValue / 0.01), // cargo units for bar sizing
          color: '#2775ca',
          valueLabel: `$${this.cargoData.stellarValue.toFixed(2)}`,
        });
      }
      const mineralEntries = entries.map(([id, qty]) => ({
        id,
        name: MINERAL_NAMES[id] || id,
        qty: Number(qty),
        color: MINERAL_COLORS[id] || '#ffffff',
      }));
      mineralEntries.sort((a, b) => {
        const ai = MINERAL_ORDER.indexOf(a.id);
        const bi = MINERAL_ORDER.indexOf(b.id);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
      allEntries.push(...mineralEntries);

      // Crypto tokens from special asteroids
      const CRYPTO_COLORS: Record<string, string> = {
        GUSDC: '#2775ca', GUSDT: '#26a17b', GALA: '#FFD700', GISD: '#00ff88', GSOL: '#9945ff',
      };
      const CRYPTO_NAMES: Record<string, string> = {
        GUSDC: 'USDC', GUSDT: 'USDT', GALA: 'GALA', GISD: 'ISD', GSOL: 'SOL',
      };
      for (const ct of (this.cargoData.cryptoTokens || [])) {
        allEntries.push({
          id: `crypto_${ct.name}`,
          name: CRYPTO_NAMES[ct.name] || ct.name,
          qty: Math.ceil(ct.value / 0.01), // cargo units for bar sizing
          color: CRYPTO_COLORS[ct.name] || '#00ccff',
          valueLabel: `$${ct.value.toFixed(2)}`,
        });
      }

      // 2-column layout spanning full tab content width
      const COL_W = Math.floor(contentW / 2);
      const maxRows = 4;
      const ROW_H = Math.min(Math.floor(availH / maxRows), 20);

      for (let i = 0; i < allEntries.length; i++) {
        const entry = allEntries[i];
        const col = i < maxRows ? 0 : 1;
        const row = i < maxRows ? i : i - maxRows;
        const cx = contentLeft + col * COL_W;
        const cy = listTop + row * ROW_H;

        // Colored dot
        const dot = this.scene.add.graphics();
        dot.fillStyle(Phaser.Display.Color.HexStringToColor(entry.color).color, 1);
        dot.fillCircle(cx + 5, cy + 7, 4);
        this.contentContainer.add(dot);

        // Name
        const nameText = this.scene.add.text(cx + 15, cy, entry.name, {
          fontSize: '11px', fontStyle: 'bold', color: entry.color, fontFamily: Fonts.body,
        });
        nameText.setOrigin(0, 0);
        this.contentContainer.add(nameText);

        // Quantity (right-aligned within column) — show valueLabel for crypto, raw qty for minerals
        const qtyStr = (entry as any).valueLabel || Math.floor(entry.qty).toString();
        const qtyText = this.scene.add.text(cx + COL_W - 6, cy, qtyStr, {
          fontSize: '11px', color: '#aabbcc', fontFamily: Fonts.body,
        });
        qtyText.setOrigin(1, 0);
        this.contentContainer.add(qtyText);
      }

      // ── Components in cargo ──
      const RARITY_COMP_COLORS: Record<string, string> = {
        common: '#8a8a8a', uncommon: '#4fc3f7', rare: '#ffd700', epic: '#ce93d8', legendary: '#ff6600',
      };
      const comps = this.cargoData.components || [];
      if (comps.length > 0) {
        // Place components below minerals
        const compTop = listTop + Math.ceil(allEntries.length / 2) * ROW_H + 4;
        const divider = this.scene.add.graphics();
        divider.lineStyle(1, 0x334455, 0.5);
        divider.lineBetween(contentLeft, compTop, contentLeft + contentW, compTop);
        this.contentContainer.add(divider);

        const compLabel = this.scene.add.text(contentLeft, compTop + 2, 'COMPONENTS', {
          fontSize: '9px', color: '#556677', fontFamily: Fonts.body, fontStyle: 'bold',
        });
        compLabel.setOrigin(0, 0);
        this.contentContainer.add(compLabel);

        const compListTop = compTop + 14;
        for (let ci = 0; ci < comps.length; ci++) {
          const comp = comps[ci];
          const cy = compListTop + ci * ROW_H;
          const rarityColor = RARITY_COMP_COLORS[comp.rarity] || '#ffffff';

          // Colored dot for rarity
          const cDot = this.scene.add.graphics();
          cDot.fillStyle(Phaser.Display.Color.HexStringToColor(rarityColor).color, 1);
          cDot.fillCircle(contentLeft + 5, cy + 7, 4);
          this.contentContainer.add(cDot);

          // Component name
          const cName = this.scene.add.text(contentLeft + 15, cy, comp.name, {
            fontSize: '11px', fontStyle: 'bold', color: rarityColor, fontFamily: Fonts.body,
          });
          cName.setOrigin(0, 0);
          this.contentContainer.add(cName);

          // Star rating + category (right-aligned)
          const stars = '\u2605'.repeat(comp.starRating);
          const info = `${stars} ${comp.category}`;
          const cInfo = this.scene.add.text(contentLeft + contentW - 6, cy, info, {
            fontSize: '10px', color: '#8899aa', fontFamily: Fonts.body,
          });
          cInfo.setOrigin(1, 0);
          this.contentContainer.add(cInfo);
        }
      }
    }
  }

  private async fetchCargo(unitId: string): Promise<void> {
    this.isLoadingCargo = true;
    try {
      const token = localStorage.getItem('crimsonmandate_token');
      const resp = await fetch(`/api/minerals/cargo/${unitId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        this.cargoData = {
          minerals: data.minerals || {},
          stellarValue: data.stellarValue || data.goldValue || 0,
          cargoCapacity: data.cargoCapacity,
          cargoUsed: data.cargoUsed,
          components: data.components || [],
          cryptoTokens: data.cryptoTokens || [],
        };
      } else {
        this.cargoData = { minerals: {}, stellarValue: 0, components: [] };
      }
    } catch {
      this.cargoData = { minerals: {}, stellarValue: 0, components: [] };
    }
    this.isLoadingCargo = false;
    if (this.activeTab === 'cargo') {
      this.buildDisplay();
    }
  }

  // ── Crew tab content ─────────────────────────────────────

  private buildCrewTab(u: SelectedUnit): void {
    const contentLeft = TAB_ACTIONS_LEFT;
    const contentRight = this._lastTabRightEdge;
    const contentW = contentRight - contentLeft;
    const listTop = TAB_UNDERLINE_Y + 8;
    const XP_PER_LEVEL = 1000;

    // Fetch crew data if needed
    if (!this.crewData && !this.isLoadingCrew) {
      this.fetchCrewData(u.id);
    }

    if (this.isLoadingCrew) {
      const loading = this.scene.add.text(contentLeft, listTop, I18n.t('crew.loading'), {
        fontSize: '11px', color: '#6688aa', fontFamily: Fonts.body,
      });
      loading.setOrigin(0, 0);
      this.contentContainer.add(loading);
      return;
    }

    if (this.crewData) {
      if (!this.crewData.hasCrew || !this.crewData.crew) {
        const empty = this.scene.add.text(contentLeft + contentW / 2, listTop + 16, I18n.t('crew.none_assigned'), {
          fontSize: '11px', color: '#556677', fontFamily: Fonts.body, fontStyle: 'italic',
        });
        empty.setOrigin(0.5, 0);
        this.contentContainer.add(empty);
        return;
      }

      const c = this.crewData.crew;

      // Clickable crew card
      const cardH = 52;
      const cardBg = this.scene.add.graphics();
      cardBg.fillStyle(0x1a2a44, 0.8);
      cardBg.fillRoundedRect(contentLeft, listTop, contentW, cardH, 4);
      cardBg.lineStyle(1, 0x00ffff, 0.15);
      cardBg.strokeRoundedRect(contentLeft, listTop, contentW, cardH, 4);
      this.contentContainer.add(cardBg);

      // Crew name
      const nameText = this.scene.add.text(contentLeft + 10, listTop + 6, c.name, {
        fontSize: '13px', fontStyle: '900', color: '#E0E8FF', fontFamily: Fonts.heading,
      });
      nameText.setOrigin(0, 0);
      this.contentContainer.add(nameText);

      // Level badge
      const levelText = this.scene.add.text(contentLeft + 10 + nameText.width + 8, listTop + 8, `LV ${c.level}`, {
        fontSize: '11px', fontStyle: '700', color: '#FFD700', fontFamily: Fonts.heading,
      });
      levelText.setOrigin(0, 0);
      this.contentContainer.add(levelText);

      // XP progress bar
      const totalXp = (c as any).totalXp || 0;
      const xpInLevel = totalXp % XP_PER_LEVEL;
      const xpRemaining = XP_PER_LEVEL - xpInLevel;
      const xpPct = xpInLevel / XP_PER_LEVEL;

      const barX = contentLeft + 10;
      const barY = listTop + 26;
      const barW = contentW - 20;
      const barH = 8;

      const barBgGfx = this.scene.add.graphics();
      barBgGfx.fillStyle(0x0a1428, 1);
      barBgGfx.fillRoundedRect(barX, barY, barW, barH, 3);
      this.contentContainer.add(barBgGfx);

      if (xpPct > 0) {
        const barFill = this.scene.add.graphics();
        barFill.fillStyle(0x4488ff, 1);
        barFill.fillRoundedRect(barX, barY, Math.max(4, barW * xpPct), barH, 3);
        this.contentContainer.add(barFill);
      }

      // XP remaining text
      const xpText = this.scene.add.text(barX, barY + barH + 3, `${xpRemaining} XP to next level`, {
        fontSize: '10px', color: '#6688aa', fontFamily: Fonts.body,
      });
      xpText.setOrigin(0, 0);
      this.contentContainer.add(xpText);

      // Click zone — opens CrewManagementDialog
      const hitZone = this.scene.add.zone(contentLeft, listTop, contentW, cardH).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, contentW, cardH), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => {
        cardBg.clear();
        cardBg.fillStyle(0x1a2a44, 0.9);
        cardBg.fillRoundedRect(contentLeft, listTop, contentW, cardH, 4);
        cardBg.lineStyle(1, 0x00ffff, 0.35);
        cardBg.strokeRoundedRect(contentLeft, listTop, contentW, cardH, 4);
      });
      hitZone.on('pointerout', () => {
        cardBg.clear();
        cardBg.fillStyle(0x1a2a44, 0.8);
        cardBg.fillRoundedRect(contentLeft, listTop, contentW, cardH, 4);
        cardBg.lineStyle(1, 0x00ffff, 0.15);
        cardBg.strokeRoundedRect(contentLeft, listTop, contentW, cardH, 4);
      });
      hitZone.on('pointerdown', () => {
        EventBus.emit('open-crew-management');
      });
      this.contentContainer.add(hitZone);
    }
  }

  private async fetchCrewData(shipId: string): Promise<void> {
    this.isLoadingCrew = true;
    try {
      const token = localStorage.getItem('crimsonmandate_token');
      const resp = await fetch(`/api/crew/bonuses/${shipId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        this.crewData = {
          hasCrew: data.hasCrew,
          crew: data.crew ? {
            id: data.crew.id,
            name: data.crew.name,
            level: data.crew.level,
            combatSkill: data.crew.combatSkill,
            pilotingSkill: data.crew.pilotingSkill,
            navigationSkill: data.crew.navigationSkill,
            engineeringSkill: data.crew.engineeringSkill,
            scienceSkill: data.crew.scienceSkill,
            totalXp: data.crew.totalXp || 0,
            status: data.crew.status,
          } : undefined,
        };
      } else {
        this.crewData = { hasCrew: false };
      }
    } catch {
      this.crewData = { hasCrew: false };
    }
    this.isLoadingCrew = false;
    if (this.activeTab === 'crew') {
      this.buildDisplay();
    }
  }

  // ── Upgrades (Burn Sink) tab content ─────────────────────

  // Paint color sub-panel state



  // ── Storage tab content (planet mineral inventory) ──────

  private buildStorageTabContent(_p: SelectedPlanet): void {
    const RARITY_COLORS: Record<string, string> = {
      common: '#8a8a8a', uncommon: '#c0c0d0', rare: '#ffd700', epic: '#f0f0ff', legendary: '#6600cc',
    };

    const contentLeft = TAB_ACTIONS_LEFT;
    const contentW = DOCK_W - contentLeft - 20;
    const listTop = TAB_UNDERLINE_Y + 8;
    const listBottom = DOCK_H - STATS_BOTTOM - 4;
    const availH = listBottom - listTop;

    if (!this.storageData && !this.isLoadingStorage) {
      this.fetchStorage();
    }

    if (this.isLoadingStorage) {
      const loading = this.scene.add.text(contentLeft, listTop, 'Loading inventory...', {
        fontSize: '11px', color: '#6688aa', fontFamily: Fonts.body,
      });
      loading.setOrigin(0, 0);
      this.contentContainer.add(loading);
      return;
    }

    if (this.storageData) {
      const nonZero = this.storageData.filter(m => m.amount > 0);

      if (nonZero.length === 0) {
        const empty = this.scene.add.text(contentLeft + contentW / 2, listTop + 16, 'No minerals stored', {
          fontSize: '11px', color: '#556677', fontFamily: Fonts.body, fontStyle: 'italic',
        });
        empty.setOrigin(0.5, 0);
        this.contentContainer.add(empty);
        return;
      }

      const COL_W = Math.floor(contentW / 2);
      const maxRows = 4;
      const ROW_H = Math.min(Math.floor(availH / maxRows), 20);

      for (let i = 0; i < nonZero.length; i++) {
        const m = nonZero[i];
        const col = i < maxRows ? 0 : 1;
        const row = i < maxRows ? i : i - maxRows;
        const cx = contentLeft + col * COL_W;
        const cy = listTop + row * ROW_H;
        const color = m.color || RARITY_COLORS[m.rarity] || '#ffffff';

        const dot = this.scene.add.graphics();
        dot.fillStyle(Phaser.Display.Color.HexStringToColor(color).color, 1);
        dot.fillCircle(cx + 5, cy + 7, 4);
        this.contentContainer.add(dot);

        const nameText = this.scene.add.text(cx + 15, cy, m.displayName || m.mineralName, {
          fontSize: '11px', fontStyle: 'bold', color, fontFamily: Fonts.body,
        });
        nameText.setOrigin(0, 0);
        this.contentContainer.add(nameText);

        const qtyText = this.scene.add.text(cx + COL_W - 6, cy, Math.floor(Number(m.amount)).toString(), {
          fontSize: '11px', color: '#aabbcc', fontFamily: Fonts.body,
        });
        qtyText.setOrigin(1, 0);
        this.contentContainer.add(qtyText);
      }
    }
  }

  private async fetchStorage(): Promise<void> {
    this.isLoadingStorage = true;
    try {
      const token = localStorage.getItem('crimsonmandate_token');
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

      // For planets, only fetch storage if player owns it or it's Earth
      if (this.currentPlanet) {
        const isPlayerPlanet = this.currentPlanet.ownerId === this.mmoState.getUserId();
        const isEarth = this.currentPlanet.hexCoord.q === 0 && this.currentPlanet.hexCoord.r === 0;
        if (!isPlayerPlanet && !isEarth) {
          this.storageData = [];
          this.isLoadingStorage = false;
          return;
        }
      }

      // Use location-specific endpoint when viewing a station or planet
      const locId = this.currentStation?.id || this.currentPlanet?.id;
      if (locId) {
        const locType = locId.startsWith('planet_') ? 'planet' : 'station';
        const resp = await fetch(`/api/minerals/location/${locType}/${locId}`, { headers });
        if (resp.ok) {
          const data = await resp.json();
          this.storageData = data.minerals || [];
        } else {
          this.storageData = [];
        }
      } else {
        // Fallback to aggregated inventory
        const resp = await fetch('/api/minerals/inventory', { headers });
        if (resp.ok) {
          const data = await resp.json();
          this.storageData = data.minerals || [];
        } else {
          this.storageData = [];
        }
      }
    } catch {
      this.storageData = [];
    }
    this.isLoadingStorage = false;
    if (this.activeTab === 'storage') {
      this.buildDisplay();
    }
  }

  // ── Action buttons ────────────────────────────────────────

  private buildActionButtons(): void {
    const u = this.currentUnit!;
    const isDocked = !!u.dockedAtPlanetId;
    const canDock = !isDocked && this.isInDockingRange(u);

    // Shared undock-then-move helper — capture unit ref before the undock
    // (this.currentUnit gets cleared when the CommandDock refreshes on undock)
    const unitRef = u;
    const undockAndMove = () => {
      // Listen for undock confirmation, then enter move mode
      const onUndocked = (payload: { unitId: string }) => {
        if (payload.unitId === unitRef.id) {
          EventBus.off('mmo_unit_undocked', onUndocked);
          // Small delay to let CommandDock finish rebuilding for the undocked unit
          this.scene.time.delayedCall(50, () => {
            EventBus.emit('mmo-action-move', { entity: unitRef });
          });
        }
      };
      EventBus.on('mmo_unit_undocked', onUndocked);
      EventBus.emit('mmo-action-undock', { entity: unitRef });
      // Safety timeout: if server doesn't respond within 3s, clean up listener
      this.scene.time.delayedCall(3000, () => {
        EventBus.off('mmo_unit_undocked', onUndocked);
      });
    };
    const undockOnly = () => {
      EventBus.emit('mmo-action-undock', { entity: unitRef });
    };

    // Determine dock/undock state
    const actionTooltips = getActionTooltips();
    const dockAction = isDocked
      ? {
          label: I18n.t('action.undock'),
          icon: 'icon-face',
          tooltip: actionTooltips['UNDOCK'],
          action: undockOnly,
          disabled: false,
          accent: 0xff4444 as number | undefined, // red glow for undock
        }
      : {
          label: I18n.t('action.dock'),
          icon: 'icon-face',
          tooltip: canDock ? actionTooltips['DOCK'] : I18n.t('tooltip.dock_disabled'),
          action: () => this.handleDockAction(),
          disabled: !canDock,
          accent: undefined as number | undefined,
        };

    // If docked, MOVE auto-undocks first and then asks for a destination.
    const moveAction = isDocked
      ? undockAndMove
      : () => EventBus.emit('mmo-action-move', { entity: this.currentUnit });

    // Determine MINE/STOP button state
    const isMining = !!u.miningAsteroidId;
    const mineState = this.getMineButtonState(u);
    const canRepair = u.currentHp < u.maxHp;

    const mineAction = isMining
      ? () => EventBus.emit('mmo-action-stop-mining', { entity: this.currentUnit })
      : () => EventBus.emit('mmo-action-mine', { entity: this.currentUnit });
    const mineLabel = isMining ? I18n.t('action.stop') : I18n.t('action.mine');
    const mineTooltip = isMining ? 'Stop mining' : mineState.tooltip;
    const mineDisabled = isMining ? false : !mineState.enabled;
    const mineAccent: number | undefined = isMining ? 0xff4444 : (mineState.enabled ? 0xffaa00 : undefined);

    // Check for enemies nearby (within attack range or 1 hex)
    const hasEnemiesNearby = this.hasEnemiesNearby(u);

    // Build actions list — only include actions that are currently available
    type ActionDef = { label: string; icon: string; tooltip: string; action: () => void; disabled: boolean; accent: number | undefined };
    const actions: ActionDef[] = [];

    // MOVE — always available (undocks first if docked)
    actions.push({ label: I18n.t('action.move'), icon: 'icon-move', tooltip: isDocked ? I18n.t('tooltip.undock_move') : actionTooltips['MOVE'], action: moveAction, disabled: false, accent: this.isInMoveMode ? 0xff4444 as number | undefined : undefined as number | undefined });

    // ATTACK — only show when not docked (enemies don't need to be nearby since player may want to enter attack mode)
    if (!isDocked) {
      actions.push({ label: I18n.t('action.attack'), icon: 'icon-attack', tooltip: hasEnemiesNearby ? actionTooltips['ATTACK'] : I18n.t('tooltip.attack'), action: () => EventBus.emit('mmo-action-attack', { entity: this.currentUnit }), disabled: false, accent: hasEnemiesNearby ? 0xff4444 as number | undefined : undefined as number | undefined });
    }

    // DOCK/UNDOCK — show UNDOCK when docked, show DOCK only when near a dockable entity
    if (isDocked || canDock) {
      actions.push(dockAction);
    }

    // MINE/STOP — only show when mining or when near an asteroid
    if (isMining || mineState.enabled) {
      actions.push({ label: mineLabel, icon: 'icon-attack', tooltip: mineTooltip, action: mineAction, disabled: mineDisabled, accent: mineAccent });
    }

    // REPAIR — only show when unit is damaged
    if (canRepair) {
      actions.push({ label: I18n.t('action.repair'), icon: 'icon-tow', tooltip: I18n.t('tooltip.repair_hull'), action: () => EventBus.emit('mmo-action-repair', { entity: this.currentUnit }), disabled: false, accent: 0x44ff44 as number | undefined });
    }

    // MODIFY — only show when docked
    if (isDocked) {
      actions.push({ label: I18n.t('action.modify'), icon: 'icon-tow', tooltip: I18n.t('tooltip.modify_components'), action: () => EventBus.emit('open_ship_modification', { unitId: u.id, stationId: u.dockedAtPlanetId }), disabled: false, accent: 0xffaa00 as number | undefined });
    }

    // RETURN — show when undocked (sends unit to nearest friendly station and auto-docks)
    if (!isDocked) {
      actions.push({ label: I18n.t('action.return'), icon: 'icon-move', tooltip: I18n.t('tooltip.return_to_base'), action: () => EventBus.emit('mmo-action-return', { entity: this.currentUnit }), disabled: false, accent: 0x4488ff as number | undefined });
    }

    // GOTO — show when not docked
    if (!isDocked) {
      actions.push({ label: I18n.t('action.goto'), icon: 'icon-move', tooltip: I18n.t('tooltip.goto'), action: () => this.showGotoInput(), disabled: false, accent: undefined as number | undefined });
    }

    const mobile = isMobile(this.scene);
    if (mobile && this.inSidebarMode) {
      // Mobile SIDEBAR: single vertical column of action buttons.
      // Each slot = 42px circle + 3px gap + ~8px label = ~53px.
      // Buttons start BELOW the 2×2 tab-grid header rendered earlier by
      // buildSidebarTabsHeader, hence the top offset uses MOBILE_SIDEBAR_TABS_H.
      const btn = MOBILE_SIDEBAR_BTN_SIZE;       // 42
      const labelReserve = 11;                   // 3px gap + ~8px glyph
      const slotH = btn + labelReserve;          // total visual footprint per button
      const gap = MOBILE_SIDEBAR_BTN_VGAP;
      const count = actions.length;
      const stackH = count * slotH + Math.max(0, count - 1) * gap;
      const contentTop = MOBILE_SIDEBAR_TABS_H;  // tabs + spacer replaces old PAD_TOP
      const totalH = contentTop + stackH + MOBILE_SIDEBAR_PAD_BOTTOM;

      // Cap the sidebar to the usable vertical space so it never exceeds the
      // viewport. On very short phones (~330px landscape) a big action count
      // may require tighter packing — clamp and evenly distribute.
      // Also enforce MOBILE_SIDEBAR_MIN_CONTENT_H so the panel is the same
      // height regardless of whether ACTIONS has 1 or 5 entries — prevents
      // the bottom edge from jumping when users tap different tabs.
      const { height } = this.scene.cameras.main;
      const maxH = Math.max(160, height - MOBILE_SIDEBAR_TOP - 32);
      const minH = contentTop + MOBILE_SIDEBAR_MIN_CONTENT_H + MOBILE_SIDEBAR_PAD_BOTTOM;
      this.sidebarH = Math.min(Math.max(totalH, minH), maxH);
      // Clamp the gap to a non-negative minimum so if the viewport is tighter
      // than the natural stack height, buttons simply pack flush without
      // overlapping each other's labels. Better to lose a few px of air than
      // have MOVE's label collide with ENGAGE's circle.
      const rawGap = count > 1
        ? (this.sidebarH - contentTop - MOBILE_SIDEBAR_PAD_BOTTOM - count * slotH) / (count - 1)
        : 0;
      const effectiveGap = Math.max(0, rawGap);
      const cx = MOBILE_SIDEBAR_W / 2;
      for (let i = 0; i < count; i++) {
        // Circle center Y: top of slot + btn/2
        const slotTop = contentTop + i * (slotH + effectiveGap);
        const cy = slotTop + btn / 2;
        const def = actions[i];
        this.createSidebarButton(cx, cy, def.label, def.icon, def.tooltip, def.action, def.disabled, def.accent);
      }

      // Redraw the background with the final computed height and anchor the
      // panel to the TOP of the usable area. We intentionally do NOT center
      // vertically — that caused the tab header to jump every time the user
      // swapped tabs, which was terrible UX. Pinning to the top means the
      // tabs stay rock solid under the finger regardless of content.
      this.drawBackground(this.currentDockW, this.sidebarH);
      this.setY(MOBILE_SIDEBAR_TOP);
    } else if (mobile) {
      // Mobile HORIZONTAL (non-unit modes like asteroid, wreck, etc.):
      // 2 rows × 4 buttons, tight packing so row 2 fits inside DOCK_H=144.
      const mobileBtnSize = 42;
      const mobileGap = 8;
      const rowGap = 10;
      const dockW = this.currentDockW;
      const totalBtnW = 4 * mobileBtnSize + 3 * mobileGap;
      const startX = (dockW - totalBtnW) / 2;
      const rowTop = 22;
      for (let i = 0; i < actions.length; i++) {
        const col = i % 4;
        const row = Math.floor(i / 4);
        const cx = startX + col * (mobileBtnSize + mobileGap) + mobileBtnSize / 2;
        const cy = rowTop + row * (mobileBtnSize + rowGap) + mobileBtnSize / 2;
        const def = actions[i];
        this.createCircleButton(cx, cy, def.label, def.icon, def.tooltip, def.action, 0, def.disabled, def.accent);
      }
    } else {
      const cy = ACTION_ROW_TOP + ACTION_BTN_SIZE / 2;
      for (let i = 0; i < actions.length; i++) {
        const def = actions[i];
        const cx = ACTION_ROW_LEFT + i * (ACTION_BTN_SIZE + ACTION_BTN_GAP) + ACTION_BTN_SIZE / 2;
        this.createCircleButton(cx, cy, def.label, def.icon, def.tooltip, def.action, 0, def.disabled, def.accent);
      }
    }
  }

  /** Show a DOM overlay with Q/R coordinate inputs for the GOTO action. */
  private showGotoInput(): void {
    // Close any existing popup first
    this.closeGotoInput();

    // Disable Phaser keyboard so typing in inputs doesn't trigger game hotkeys
    if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = false;

    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();

    // Position the popup. On mobile the desktop CommandDock is hidden and the
    // mobile sidebar lives on the left edge, so the desktop-dock-relative math
    // below would land the popup off-screen — keep the input centered in the
    // viewport instead so the player always sees it. On desktop, anchor it
    // above the GOTO action button (index 5) in the dock as before.
    let popupPixelX: number;
    let popupPixelY: number;
    const onMobile = isMobile(this.scene);
    if (onMobile) {
      // Center horizontally; place a bit above viewport center so the iOS soft
      // keyboard (when it appears for the number inputs) doesn't cover it.
      popupPixelX = canvasRect.left + canvasRect.width / 2;
      popupPixelY = canvasRect.top + canvasRect.height * 0.4;
    } else {
      const gotoIdx = 5;
      const btnCenterX = ACTION_ROW_LEFT + gotoIdx * (ACTION_BTN_SIZE + ACTION_BTN_GAP) + ACTION_BTN_SIZE / 2;
      const dockX = (this.scene.cameras.main.width - DOCK_W) / 2;
      const dockY = this.scene.cameras.main.height - DOCK_H;
      const scaleX = canvasRect.width / this.scene.cameras.main.width;
      const scaleY = canvasRect.height / this.scene.cameras.main.height;
      popupPixelX = canvasRect.left + (dockX + btnCenterX) * scaleX;
      popupPixelY = canvasRect.top + (dockY + ACTION_ROW_TOP) * scaleY;
    }

    // Create overlay backdrop (click outside to dismiss). On mobile, give it
    // a faint dim layer so users perceive it as a modal and don't keep tapping
    // the dock buttons behind it expecting interaction.
    const overlay = document.createElement('div');
    overlay.style.cssText = `position:fixed;top:0;left:0;width:100%;height:100%;z-index:9999;${
      onMobile ? 'background:rgba(0,0,0,0.45);' : ''
    }`;
    overlay.addEventListener('pointerdown', (e) => {
      if (e.target === overlay) this.closeGotoInput();
    });

    // Create popup container. On mobile we center it via translate(-50%, -50%)
    // around the chosen point (40% down the viewport); on desktop we anchor
    // above the GOTO button via translate(-50%, -100%) and a -10px lift.
    const popup = document.createElement('div');
    const popupTransform = onMobile ? 'translate(-50%, -50%)' : 'translate(-50%, -100%)';
    const popupTop = onMobile ? popupPixelY : popupPixelY - 10;
    popup.style.cssText = `
      position:absolute;
      left:${popupPixelX}px;
      top:${popupTop}px;
      transform:${popupTransform};
      background:#1a1530;
      border:2px solid #00ff88;
      border-radius:8px;
      padding:${onMobile ? '14px 16px' : '10px 12px'};
      display:flex;
      align-items:center;
      gap:${onMobile ? '10px' : '8px'};
      font-family:monospace;
      box-shadow:0 4px 16px rgba(0,0,0,0.6);
    `;

    // Note: 16px input font on mobile prevents iOS Safari/Chrome from
    // auto-zooming when the field is focused, which otherwise messes with
    // the pinch-zoom layout we set up for the canvas.
    const inputStyle = `
      width:${onMobile ? '78px' : '60px'};
      padding:${onMobile ? '8px 10px' : '4px 6px'};
      background:#0d0a1a;
      border:1px solid #444;
      border-radius:4px;
      color:#00ff88;
      font-family:monospace;
      font-size:${onMobile ? '16px' : '14px'};
      text-align:center;
      outline:none;
    `;

    const labelStyle = `color:#aaa;font-size:${onMobile ? '14px' : '12px'};`;

    const qLabel = document.createElement('span');
    qLabel.textContent = 'Q';
    qLabel.style.cssText = labelStyle;

    const qInput = document.createElement('input');
    qInput.type = 'number';
    qInput.style.cssText = inputStyle;
    qInput.placeholder = '0';

    const rLabel = document.createElement('span');
    rLabel.textContent = 'R';
    rLabel.style.cssText = labelStyle;

    const rInput = document.createElement('input');
    rInput.type = 'number';
    rInput.style.cssText = inputStyle;
    rInput.placeholder = '0';

    const goBtn = document.createElement('button');
    goBtn.textContent = 'GO';
    goBtn.style.cssText = `
      padding:4px 12px;
      background:#00ff88;
      color:#0d0a1a;
      border:none;
      border-radius:4px;
      font-family:monospace;
      font-size:14px;
      font-weight:bold;
      cursor:pointer;
    `;

    const submitGoto = () => {
      const q = parseInt(qInput.value, 10);
      const r = parseInt(rInput.value, 10);
      if (isNaN(q) || isNaN(r)) return;
      EventBus.emit('mmo-action-goto', { entity: this.currentUnit, targetHex: { q, r } });
      this.closeGotoInput();
    };

    goBtn.addEventListener('click', submitGoto);

    // Submit on Enter key in either input
    const onKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter') submitGoto();
      if (e.key === 'Escape') this.closeGotoInput();
    };
    qInput.addEventListener('keydown', onKeyDown);
    rInput.addEventListener('keydown', onKeyDown);

    // Also stop other key events from reaching Phaser
    qInput.addEventListener('keyup', (e) => e.stopPropagation());
    rInput.addEventListener('keyup', (e) => e.stopPropagation());
    qInput.addEventListener('keypress', (e) => e.stopPropagation());
    rInput.addEventListener('keypress', (e) => e.stopPropagation());

    popup.append(qLabel, qInput, rLabel, rInput, goBtn);
    overlay.appendChild(popup);
    document.body.appendChild(overlay);
    this.gotoOverlay = overlay;

    // Focus the Q input after a frame so the browser picks it up
    requestAnimationFrame(() => qInput.focus());
  }

  /** Remove the GOTO coordinate input popup and restore Phaser keyboard. */
  private closeGotoInput(): void {
    if (this.gotoOverlay) {
      document.body.removeChild(this.gotoOverlay);
      this.gotoOverlay = null;
    }
    if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = true;
  }

  /** Determine whether the MINE button should be enabled and what tooltip to show.
   *  Checks the unit's own hex (grandfathered units) and the 6 adjacent hexes. */
  private getMineButtonState(u: SelectedUnit): { enabled: boolean; tooltip: string } {
    const hexesToCheck = [u.hexCoord, ...hexNeighbors(u.hexCoord)];

    for (const hex of hexesToCheck) {
      for (const entity of this.mmoState.getEntitiesAt(hex)) {
        if ('resourceRichness' in entity) {
          const asteroid = entity as { asteroidType?: string };
          if (!asteroid.asteroidType || asteroid.asteroidType === 'regular') {
            return { enabled: true, tooltip: 'Mine minerals from this asteroid' };
          }
          if (asteroid.asteroidType === 'golden') {
            return { enabled: true, tooltip: 'Mine Gold and minerals from this Cryptoid' };
          }
          if (asteroid.asteroidType === 'special') {
            return { enabled: true, tooltip: 'Mine crypto tokens from this Cryptoid' };
          }
        }
      }
    }

    return { enabled: false, tooltip: 'Move adjacent to an asteroid to mine' };
  }

  /** Check if there are enemy units within 1 hex of the given unit */
  private hasEnemiesNearby(u: SelectedUnit): boolean {
    const userId = this.mmoState.getUserId();
    const hexesToCheck = [u.hexCoord, ...hexNeighbors(u.hexCoord)];
    for (const hex of hexesToCheck) {
      for (const entity of this.mmoState.getEntitiesAt(hex)) {
        if ('ownerId' in entity && 'attackRange' in entity) {
          const unit = entity as { ownerId: string };
          if (unit.ownerId !== userId) return true;
        }
      }
    }
    return false;
  }

  /** Check if a unit is within docking range (1 hex) of any planet, station, or platform */
  private isInDockingRange(u: SelectedUnit): boolean {
    const DOCK_RANGE = 1;
    const allPlanets = this.mmoState.getAllPlanets();
    const allStations = this.mmoState.getAllStations();
    const allPlatforms = this.mmoState.getAllPlatforms();

    // Check planets (Earth, colonies, etc.)
    for (const p of allPlanets) {
      if (hexDistance(u.hexCoord, p.position) <= DOCK_RANGE) return true;
    }
    // Check platforms (highest docking priority)
    for (const pl of allPlatforms) {
      const plPos = { q: pl.positionQ, r: pl.positionR };
      if (hexDistance(u.hexCoord, plPos) <= DOCK_RANGE) return true;
    }
    for (const s of allStations) {
      const footprintSize = (STATION_FOOTPRINT[s.stationType] || 1) as FootprintSize;
      if (distanceToFootprint(u.hexCoord, s.position, footprintSize) <= DOCK_RANGE) return true;
    }
    // Check for Alien Leviathan units (3-hex footprint)
    for (const unit of this.mmoState.getAllUnits()) {
      if ((unit.type || '').toLowerCase().includes('leviathan')) {
        if (distanceToFootprint(u.hexCoord, unit.position, 3 as FootprintSize) <= DOCK_RANGE) return true;
      }
    }
    return false;
  }

  /** Handle dock action: find the nearest dockable station/platform and dock there.
   *  Uses footprint-aware distance so multi-hex stations work.
   *  Priority: platforms > stations > leviathans */
  private handleDockAction(): void {
    const u = this.currentUnit;
    if (!u) return;

    const DOCK_RANGE = 1; // must be within 1 hex of any footprint tile

    // Gather all known entities
    const allPlanets = this.mmoState.getAllPlanets();
    const allStations = this.mmoState.getAllStations();
    const allPlatforms = this.mmoState.getAllPlatforms();

    // Check planets within docking range
    const nearbyPlanets = allPlanets.filter((p) => {
      return hexDistance(u.hexCoord, p.position) <= DOCK_RANGE;
    });

    // Check platforms within docking range (1-hex footprint)
    const nearbyPlatforms = allPlatforms.filter((pl) => {
      const plPos = { q: pl.positionQ, r: pl.positionR };
      return hexDistance(u.hexCoord, plPos) <= DOCK_RANGE;
    });

    // Check stations within docking range (footprint-aware)
    const nearbyStations = allStations.filter((s) => {
      const footprintSize = (STATION_FOOTPRINT[s.stationType] || 1) as FootprintSize;
      return distanceToFootprint(u.hexCoord, s.position, footprintSize) <= DOCK_RANGE;
    });

    // Check for Alien Leviathan units within docking range (3-hex footprint)
    const nearbyLeviathans = this.mmoState.getAllUnits().filter((unit) => {
      if (!(unit.type || '').toLowerCase().includes('leviathan')) return false;
      return distanceToFootprint(u.hexCoord, unit.position, 3 as FootprintSize) <= DOCK_RANGE;
    });

    if (nearbyPlanets.length === 0 && nearbyPlatforms.length === 0 && nearbyStations.length === 0 && nearbyLeviathans.length === 0) {
      this.showTooltip(
        ACTION_ROW_LEFT + ACTION_BTN_SIZE * 2 + ACTION_BTN_GAP * 2 + ACTION_BTN_SIZE / 2,
        ACTION_ROW_TOP - 8,
        'No station or planet within docking range',
      );
      return;
    }

    // Prefer platforms > stations > planets > leviathans
    if (nearbyPlatforms.length > 0) {
      EventBus.emit('mmo-action-dock', { entity: u, platformId: nearbyPlatforms[0].id });
      return;
    }

    if (nearbyStations.length > 0) {
      EventBus.emit('mmo-action-dock', { entity: u, stationId: nearbyStations[0].id });
      return;
    }

    if (nearbyPlanets.length > 0) {
      EventBus.emit('mmo-action-dock', { entity: u, planetId: nearbyPlanets[0].id });
      return;
    }

    // Alien Leviathan docking
    if (nearbyLeviathans.length > 0) {
      EventBus.emit('mmo-action-dock', { entity: u, alienUnitId: nearbyLeviathans[0].id });
    }
  }

  private buildAbilityButtons(_u: SelectedUnit): void {
    // Abilities disabled — to be redesigned
    if (true) return; // eslint-disable-line
    const u = _u;
    const abilities = u.abilities || [];

    // Build all ability buttons
    interface AbilityDef { label: string; icon: string; tooltip: string; cooldown: number; action: () => void }
    const allAbilities: AbilityDef[] = [];

    // Unit-specific abilities
    for (const ab of abilities) {
      const abName = (ab.name || ab.abilityTypeId || 'Ability').toString();
      const abKey = abName.toLowerCase().replace(/\s+/g, '_');
      const effectKey = (ab.effectType || '').toLowerCase();

      const iconKey = ABILITY_ICON_MAP[abKey]
        || ABILITY_ICON_MAP[effectKey]
        || '';

      const abilityTips = getAbilityTooltips();
      const tooltip = abilityTips[abKey]
        || abilityTips[effectKey]
        || `${abName} (${ab.cooldownTicks} tick cooldown)`;

      // Use short canonical name, fallback to truncated raw name
      const abilityNames = getAbilityShortNames();
      const shortName = abilityNames[abKey]
        || abilityNames[effectKey]
        || abName.toUpperCase().substring(0, 6);

      allAbilities.push({
        label: shortName,
        icon: iconKey,
        tooltip,
        cooldown: ab.currentCooldown || 0,
        action: () => EventBus.emit('mmo-action-ability', { entity: u, abilityId: ab.abilityTypeId }),
      });
    }

    if (allAbilities.length === 0) {
      const noAb = this.scene.add.text(
        ACTION_ROW_LEFT + 80, ACTION_ROW_TOP + ACTION_BTN_SIZE / 2,
        I18n.t('ability.none'), {
          fontSize: '11px', color: '#556688', fontFamily: Fonts.body,
        },
      );
      noAb.setOrigin(0.5, 0.5);
      this.contentContainer.add(noAb);
      return;
    }

    const cy = ACTION_ROW_TOP + ACTION_BTN_SIZE / 2;
    for (let i = 0; i < allAbilities.length; i++) {
      const def = allAbilities[i];
      const cx = ACTION_ROW_LEFT + i * (ACTION_BTN_SIZE + ACTION_BTN_GAP) + ACTION_BTN_SIZE / 2;
      this.createCircleButton(cx, cy, def.label, def.icon, def.tooltip, def.action, def.cooldown);
    }
  }

  // ── Circular action button with badge label ───────────────

  private createCircleButton(
    cx: number, cy: number,
    label: string, iconKey: string,
    tooltip: string,
    onClick: () => void,
    cooldown = 0,
    disabled = false,
    accent?: number, // optional accent color (overrides default cyan)
  ): void {
    const r = ACTION_BTN_SIZE / 2;
    const color = accent || 0x00ffff; // default cyan

    // Circle
    const circle = this.scene.add.graphics();
    if (disabled) {
      this.drawCircleDisabled(circle, cx, cy, r);
    } else if (accent) {
      this.drawCircleAccent(circle, cx, cy, r, accent, false);
    } else {
      this.drawCircle(circle, cx, cy, r, false);
    }
    this.contentContainer.add(circle);

    // Icon
    if (iconKey && this.scene.textures.exists(iconKey)) {
      const icon = this.scene.add.image(cx, cy, iconKey);
      icon.setDisplaySize(ACTION_ICON_SIZE, ACTION_ICON_SIZE);
      if (disabled) icon.setAlpha(0.3);
      if (accent) icon.setTint(accent);
      this.contentContainer.add(icon);
    }

    // Cooldown overlay (semi-transparent dark + tick count)
    if (cooldown > 0) {
      const cdOverlay = this.scene.add.graphics();
      cdOverlay.fillStyle(0x000000, 0.6);
      cdOverlay.fillCircle(cx, cy, r);
      this.contentContainer.add(cdOverlay);

      const cdText = this.scene.add.text(cx, cy, `${cooldown}`, {
        fontSize: '14px',
        fontStyle: '900',
        color: '#FFFFFF',
        fontFamily: Fonts.heading,
      });
      cdText.setOrigin(0.5, 0.5);
      cdText.setShadow(1, 1, '#000000', 3, true, true);
      this.contentContainer.add(cdText);
    }

    // Label badge (auto-sized with 5px padding, at absolute 106px from top)
    const badgeColor = disabled ? 0x445566 : color;
    const badgeTextColor = disabled ? '#667788' : '#030C23';

    const lbl = this.scene.add.text(0, 0, label, {
      fontSize: '7px',
      fontStyle: '900',
      color: badgeTextColor,
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    const badgeW = lbl.width + BADGE_PAD * 2;
    const badgeH = lbl.height + BADGE_PAD * 2;

    const badgeBg = this.scene.add.graphics();
    badgeBg.fillStyle(badgeColor, 1);
    badgeBg.fillRect(cx - badgeW / 2, BADGE_TOP, badgeW, badgeH);
    badgeBg.lineStyle(BADGE_STROKE, 0x131026, 1);
    badgeBg.strokeRect(cx - badgeW / 2, BADGE_TOP, badgeW, badgeH);
    this.contentContainer.add(badgeBg);

    lbl.setPosition(cx, BADGE_TOP + badgeH / 2);
    lbl.setOrigin(0.5, 0.5);
    this.contentContainer.add(lbl);

    // Hit zone (covers circle + badge area)
    const hitH = ACTION_BTN_SIZE + badgeH + 4;
    const hitZone = this.scene.add.zone(cx - r, cy - r, ACTION_BTN_SIZE, hitH).setOrigin(0, 0);
    hitZone.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, ACTION_BTN_SIZE, hitH),
      Phaser.Geom.Rectangle.Contains,
    );
    this.contentContainer.add(hitZone);

    if (disabled) {
      // Disabled: show tooltip on hover but don't trigger action
      hitZone.on('pointerover', () => {
        this.showTooltip(cx, ACTION_ROW_TOP - 8, tooltip);
        this.scene.input.setDefaultCursor('not-allowed');
      });
      hitZone.on('pointerout', () => {
        this.hideTooltip();
        this.scene.input.setDefaultCursor('default');
      });
    } else {
      hitZone.on('pointerover', () => {
        if (accent) {
          this.drawCircleAccent(circle, cx, cy, r, accent, true);
        } else {
          this.drawCircle(circle, cx, cy, r, true);
        }
        this.showTooltip(cx, ACTION_ROW_TOP - 8, tooltip);
        this.scene.input.setDefaultCursor('pointer');
      });

      hitZone.on('pointerout', () => {
        if (accent) {
          this.drawCircleAccent(circle, cx, cy, r, accent, false);
        } else {
          this.drawCircle(circle, cx, cy, r, false);
        }
        this.hideTooltip();
        this.scene.input.setDefaultCursor('default');
      });

      hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        onClick();
      });
    }
  }

  /**
   * Compact circle action button for the mobile left sidebar.
   * Uses MOBILE_SIDEBAR_BTN_SIZE (42px) circle with a tiny label placed
   * immediately under each button (not at the absolute BADGE_TOP used by
   * the desktop horizontal dock). Sized so four stacked buttons + labels
   * still fit between the tab header and the bottom of the viewport on a
   * ~400px-tall landscape phone.
   */
  private createSidebarButton(
    cx: number, cy: number,
    label: string, iconKey: string,
    tooltip: string,
    onClick: () => void,
    disabled = false,
    accent?: number,
  ): void {
    const r = MOBILE_SIDEBAR_BTN_SIZE / 2;       // 21
    const iconSize = 18;                         // proportioned for a 42px button
    const color = accent || 0x00ffff;

    // Circle background
    const circle = this.scene.add.graphics();
    if (disabled) {
      this.drawCircleDisabled(circle, cx, cy, r);
    } else if (accent) {
      this.drawCircleAccent(circle, cx, cy, r, accent, false);
    } else {
      this.drawCircle(circle, cx, cy, r, false);
    }
    this.contentContainer.add(circle);

    // Icon
    if (iconKey && this.scene.textures.exists(iconKey)) {
      const icon = this.scene.add.image(cx, cy, iconKey);
      icon.setDisplaySize(iconSize, iconSize);
      if (disabled) icon.setAlpha(0.3);
      if (accent) icon.setTint(accent);
      this.contentContainer.add(icon);
    }

    // Small label BELOW each circle (sidebar-local, not absolute). Kept at
    // 8px so four buttons + labels fit vertically inside the sidebar
    // without clipping the final label off the bottom of the viewport.
    const labelColor = disabled ? '#667788' : '#C8E8FF';
    const lbl = this.scene.add.text(cx, cy + r + 3, label, {
      fontSize: '8px',
      fontStyle: '900',
      color: labelColor,
      fontFamily: Fonts.heading,
      letterSpacing: 1,
      align: 'center',
    });
    lbl.setOrigin(0.5, 0);
    // Constrain label width to sidebar to avoid clipping
    if (lbl.width > MOBILE_SIDEBAR_W - 6) {
      lbl.setScale((MOBILE_SIDEBAR_W - 6) / lbl.width);
    }
    this.contentContainer.add(lbl);

    // Hit zone: covers circle only (labels are decorative). Touch targets
    // are 42×42 — within a hair of the 44px iOS/Android HIG minimum and
    // chosen to keep four action buttons visible on short landscape phones.
    const hitZone = this.scene.add.zone(cx - r, cy - r, MOBILE_SIDEBAR_BTN_SIZE, MOBILE_SIDEBAR_BTN_SIZE).setOrigin(0, 0);
    hitZone.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, MOBILE_SIDEBAR_BTN_SIZE, MOBILE_SIDEBAR_BTN_SIZE),
      Phaser.Geom.Rectangle.Contains,
    );
    this.contentContainer.add(hitZone);

    if (disabled) {
      hitZone.on('pointerover', () => {
        this.showTooltip(cx, cy - r - 4, tooltip);
        this.scene.input.setDefaultCursor('not-allowed');
      });
      hitZone.on('pointerout', () => {
        this.hideTooltip();
        this.scene.input.setDefaultCursor('default');
      });
    } else {
      hitZone.on('pointerover', () => {
        if (accent) {
          this.drawCircleAccent(circle, cx, cy, r, accent, true);
        } else {
          this.drawCircle(circle, cx, cy, r, true);
        }
        this.showTooltip(cx, cy - r - 4, tooltip);
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        if (accent) {
          this.drawCircleAccent(circle, cx, cy, r, accent, false);
        } else {
          this.drawCircle(circle, cx, cy, r, false);
        }
        this.hideTooltip();
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        onClick();
      });
    }
    // Silence unused variable lint: `color` is retained for future glow work
    void color;
  }

  // ── Mobile sidebar tab header + per-tab content ─────────────────
  // These run only when `mobileMode && this.inSidebarMode`. They render a
  // compact 2×2 grid of tab chips at the top of the 60-px sidebar and a
  // narrow, vertically-stacked view below for whichever tab is active.

  /**
   * Render a 2×2 grid of tab chips (ACTIONS, ABILITIES, CARGO, CREW) at the
   * top of the sidebar. Each chip is ~26×20 with an abbreviated 3-letter
   * label. Active chip has a cyan fill; inactive chips sit dark. Tapping a
   * chip sets `this.activeTab` and rebuilds the display.
   */
  private buildSidebarTabsHeader(cargoFull: boolean, cargoUsed: number, cargoCapacity: number): void {
    void cargoUsed; void cargoCapacity; // retained for potential future micro-labels
    const chips: { abbrev: string; key: 'actions' | 'abilities' | 'cargo' | 'crew'; warn?: boolean }[] = [
      { abbrev: 'ACT', key: 'actions' },
      { abbrev: 'ABI', key: 'abilities' },
      { abbrev: 'CAR', key: 'cargo', warn: cargoFull },
      { abbrev: 'CRW', key: 'crew' },
    ];

    const cellW = MOBILE_SIDEBAR_TAB_W;
    const cellH = MOBILE_SIDEBAR_TAB_H;
    const gapX = MOBILE_SIDEBAR_TAB_GAP_X;
    const gapY = MOBILE_SIDEBAR_TAB_GAP_Y;
    // Horizontal centering: 2 cols * cellW + 1 gap = 54, left pad = (60 - 54) / 2 = 3
    const leftPad = Math.floor((MOBILE_SIDEBAR_W - (2 * cellW + gapX)) / 2);

    for (let i = 0; i < chips.length; i++) {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = leftPad + col * (cellW + gapX);
      const y = MOBILE_SIDEBAR_TAB_TOP + row * (cellH + gapY);
      const chip = chips[i];
      const active = this.activeTab === chip.key;
      const warn = !!chip.warn;

      // Chip background
      const bg = this.scene.add.graphics();
      const fill = active
        ? (warn ? 0xff4444 : 0x00ffff)
        : 0x12203A;
      const alpha = active ? 0.85 : 0.55;
      bg.fillStyle(fill, alpha);
      bg.fillRoundedRect(x, y, cellW, cellH, 4);
      bg.lineStyle(1, active ? 0xffffff : (warn ? 0xff4444 : 0x2a3e5c), active ? 0.9 : 0.6);
      bg.strokeRoundedRect(x, y, cellW, cellH, 4);
      this.contentContainer.add(bg);

      // Abbreviated label — scales with sidebar width (see MOBILE_SIDEBAR_TAB_W)
      const txt = this.scene.add.text(x + cellW / 2, y + cellH / 2, chip.abbrev, {
        fontSize: '12px',
        fontStyle: '900',
        color: active ? '#0A0F1F' : (warn ? '#FF8888' : '#A8BED8'),
        fontFamily: Fonts.heading,
        letterSpacing: 1,
      });
      txt.setOrigin(0.5, 0.5);
      this.contentContainer.add(txt);

      // Hit zone
      const hit = this.scene.add.zone(x, y, cellW, cellH).setOrigin(0, 0);
      hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, cellW, cellH), Phaser.Geom.Rectangle.Contains);
      hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        pointer.event.stopPropagation();
        this.hideTooltip();
        this.activeTab = chip.key;
        this.buildDisplay();
      });
      this.contentContainer.add(hit);
    }
  }

  /**
   * Compact abilities view for the narrow sidebar. Abilities are currently
   * disabled (see buildAbilityButtons), so render a short placeholder.
   */
  private buildSidebarAbilitiesContent(u: SelectedUnit): void {
    void u;
    const cx = MOBILE_SIDEBAR_W / 2;
    const cy = MOBILE_SIDEBAR_TABS_H + 30;
    const msg = this.scene.add.text(cx, cy, 'No\nabilities\nyet', {
      fontSize: '11px',
      color: '#6A88B0',
      fontFamily: Fonts.body,
      align: 'center',
      fontStyle: 'italic',
      lineSpacing: 2,
    });
    msg.setOrigin(0.5, 0);
    this.contentContainer.add(msg);
    // Consistent height across all tabs (see ACTIONS comment) — use the same
    // minimum content height so the panel bottom doesn't move when the user
    // taps between ACT / ABI / CAR / CRW.
    this.sidebarH = MOBILE_SIDEBAR_TABS_H + MOBILE_SIDEBAR_MIN_CONTENT_H + MOBILE_SIDEBAR_PAD_BOTTOM;
    this.drawBackground(this.currentDockW, this.sidebarH);
    // Anchor to top — see buildActionButtons for rationale.
    this.setY(MOBILE_SIDEBAR_TOP);
  }

  /**
   * Compact cargo view — vertical progress bar + "used/cap" text stacked under
   * the tab header. Any deeper breakdown (mineral list, credits) is only
   * rendered if vertical space allows; otherwise the user is pointed at the
   * Fleet panel via a small hint.
   */
  private buildSidebarCargoContent(u: SelectedUnit): void {
    const cx = MOBILE_SIDEBAR_W / 2;
    const top = MOBILE_SIDEBAR_TABS_H + 4;
    const capacity = this.cargoData?.cargoCapacity ?? u.cargoCapacity ?? 100;
    const used = this.cargoData?.cargoUsed ?? u.cargoUsed ?? 0;
    const pct = capacity > 0 ? Math.min(used / capacity, 1) : 0;

    // Header text
    const header = this.scene.add.text(cx, top, 'CARGO', {
      fontSize: '11px', fontStyle: '900', color: '#A8BED8',
      fontFamily: Fonts.heading, letterSpacing: 1,
    });
    header.setOrigin(0.5, 0);
    this.contentContainer.add(header);

    // Vertical progress bar — sized so CARGO's natural height lines up
    // with the other tabs' MIN_CONTENT_H floor, keeping the panel bottom
    // edge stable when swapping between ACT / ABI / CAR / CRW.
    const barW = 12;
    const barH = 120;
    const barX = cx - barW / 2;
    const barY = top + 16;

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0a1428, 1);
    bg.fillRoundedRect(barX, barY, barW, barH, 3);
    bg.lineStyle(1, 0x2a3e5c, 0.8);
    bg.strokeRoundedRect(barX, barY, barW, barH, 3);
    this.contentContainer.add(bg);

    if (pct > 0) {
      const fillColor = pct >= 0.9 ? 0xff4444 : pct >= 0.7 ? 0xffaa00 : 0x00ccff;
      const fillH = Math.max(4, barH * pct);
      const fill = this.scene.add.graphics();
      fill.fillStyle(fillColor, 0.9);
      // Fill bottom-up: rectangle anchored at bottom
      fill.fillRoundedRect(barX, barY + (barH - fillH), barW, fillH, 3);
      this.contentContainer.add(fill);
    }

    // Numeric readout below the bar
    const valueTxt = this.scene.add.text(cx, barY + barH + 8, `${Math.round(used)}`, {
      fontSize: '14px', fontStyle: '900', color: '#E0E8FF', fontFamily: Fonts.heading,
    });
    valueTxt.setOrigin(0.5, 0);
    this.contentContainer.add(valueTxt);

    const slashTxt = this.scene.add.text(cx, barY + barH + 24, '/', {
      fontSize: '11px', color: '#6A88B0', fontFamily: Fonts.heading,
    });
    slashTxt.setOrigin(0.5, 0);
    this.contentContainer.add(slashTxt);

    const capTxt = this.scene.add.text(cx, barY + barH + 34, `${capacity}`, {
      fontSize: '13px', fontStyle: '900', color: '#A8BED8', fontFamily: Fonts.heading,
    });
    capTxt.setOrigin(0.5, 0);
    this.contentContainer.add(capTxt);

    // Fetch detailed cargo in background (used elsewhere if user expands view)
    if (!this.cargoData && !this.isLoadingCargo) {
      this.fetchCargo(u.id);
    }

    // Finalize sidebar height — use the shared minimum so it matches the
    // other tabs. Anchor to top so the tab header stays put when switching.
    const contentBottom = barY + barH + 52;
    const natural = contentBottom + MOBILE_SIDEBAR_PAD_BOTTOM;
    const consistent = MOBILE_SIDEBAR_TABS_H + MOBILE_SIDEBAR_MIN_CONTENT_H + MOBILE_SIDEBAR_PAD_BOTTOM;
    this.sidebarH = Math.max(natural, consistent);
    this.drawBackground(this.currentDockW, this.sidebarH);
    this.setY(MOBILE_SIDEBAR_TOP);
  }

  /**
   * Compact crew view — single crew member displayed as name + level + XP bar
   * sized for the 60px sidebar. If there is no crew, show a short "NONE" hint.
   */
  private buildSidebarCrewContent(u: SelectedUnit): void {
    const cx = MOBILE_SIDEBAR_W / 2;
    const top = MOBILE_SIDEBAR_TABS_H + 4;

    const header = this.scene.add.text(cx, top, 'CREW', {
      fontSize: '11px', fontStyle: '900', color: '#A8BED8',
      fontFamily: Fonts.heading, letterSpacing: 1,
    });
    header.setOrigin(0.5, 0);
    this.contentContainer.add(header);

    if (!this.crewData && !this.isLoadingCrew) {
      this.fetchCrewData(u.id);
    }

    let cursorY = top + 20;
    if (this.isLoadingCrew) {
      const loading = this.scene.add.text(cx, cursorY, '...', {
        fontSize: '13px', color: '#6A88B0', fontFamily: Fonts.body,
      });
      loading.setOrigin(0.5, 0);
      this.contentContainer.add(loading);
      cursorY += 24;
    } else if (this.crewData && this.crewData.hasCrew && this.crewData.crew) {
      const c = this.crewData.crew;
      // Truncated name — sidebar is 90px wide now, so allow ~12 chars
      const displayName = c.name.length > 12 ? c.name.slice(0, 11) + '…' : c.name;
      const nameTxt = this.scene.add.text(cx, cursorY, displayName, {
        fontSize: '12px', fontStyle: '900', color: '#E0E8FF', fontFamily: Fonts.heading,
      });
      nameTxt.setOrigin(0.5, 0);
      this.contentContainer.add(nameTxt);
      cursorY += 18;

      // Level badge
      const lvlTxt = this.scene.add.text(cx, cursorY, `LV ${c.level}`, {
        fontSize: '11px', fontStyle: '700', color: '#FFD700', fontFamily: Fonts.heading,
      });
      lvlTxt.setOrigin(0.5, 0);
      this.contentContainer.add(lvlTxt);
      cursorY += 18;

      // Horizontal XP bar — fits within sidebar
      const XP_PER_LEVEL = 1000;
      const totalXp = (c as any).totalXp || 0;
      const xpInLevel = totalXp % XP_PER_LEVEL;
      const xpPct = xpInLevel / XP_PER_LEVEL;

      const barW = MOBILE_SIDEBAR_W - 20;
      const barH = 8;
      const barX = (MOBILE_SIDEBAR_W - barW) / 2;
      const barY = cursorY;

      const bg = this.scene.add.graphics();
      bg.fillStyle(0x0a1428, 1);
      bg.fillRoundedRect(barX, barY, barW, barH, 2);
      this.contentContainer.add(bg);

      if (xpPct > 0) {
        const fill = this.scene.add.graphics();
        fill.fillStyle(0x4488ff, 1);
        fill.fillRoundedRect(barX, barY, Math.max(3, barW * xpPct), barH, 2);
        this.contentContainer.add(fill);
      }
      cursorY += barH + 8;
    } else {
      const none = this.scene.add.text(cx, cursorY, 'NONE', {
        fontSize: '12px', color: '#6A88B0', fontFamily: Fonts.body, fontStyle: 'italic',
      });
      none.setOrigin(0.5, 0);
      this.contentContainer.add(none);
      cursorY += 20;
    }

    const natural = cursorY + MOBILE_SIDEBAR_PAD_BOTTOM;
    const consistent = MOBILE_SIDEBAR_TABS_H + MOBILE_SIDEBAR_MIN_CONTENT_H + MOBILE_SIDEBAR_PAD_BOTTOM;
    this.sidebarH = Math.max(natural, consistent);
    this.drawBackground(this.currentDockW, this.sidebarH);
    // Anchor to top so tab header stays fixed when swapping tabs.
    this.setY(MOBILE_SIDEBAR_TOP);
  }

  /** Draw a circle with a custom accent color (used for UNDOCK red glow) */
  private drawCircleAccent(
    gfx: Phaser.GameObjects.Graphics,
    cx: number, cy: number, r: number,
    color: number, hovered: boolean,
  ): void {
    gfx.clear();
    if (hovered) {
      // Brighter fill + glow
      gfx.fillStyle(color, 0.25);
      gfx.fillCircle(cx, cy, r);
      gfx.lineStyle(3, color, 1);
      gfx.strokeCircle(cx, cy, r);
      // Outer glow ring
      gfx.lineStyle(1, color, 0.3);
      gfx.strokeCircle(cx, cy, r + 3);
    } else {
      // Subtle pulsing fill
      gfx.fillStyle(color, 0.15);
      gfx.fillCircle(cx, cy, r);
      gfx.lineStyle(3, color, 0.8);
      gfx.strokeCircle(cx, cy, r);
    }
  }

  /** Draw a greyed-out disabled circle */
  private drawCircleDisabled(
    gfx: Phaser.GameObjects.Graphics,
    cx: number, cy: number, r: number,
  ): void {
    gfx.clear();
    gfx.fillStyle(0x222233, 0.4);
    gfx.fillCircle(cx, cy, r);
    gfx.lineStyle(2, 0x445566, 0.4);
    gfx.strokeCircle(cx, cy, r);
  }

  // ── Tooltip ─────────────────────────────────────────────────

  private showTooltip(cx: number, bottomY: number, text: string): void {
    this.hideTooltip();

    const container = this.scene.add.container(0, 0);

    // Measure text first
    const tipText = this.scene.add.text(cx, 0, text, {
      fontSize: '10px',
      color: '#E0E8FF',
      fontFamily: Fonts.body,
      letterSpacing: 1,
      align: 'center',
      wordWrap: { width: 160 },
    });
    tipText.setOrigin(0.5, 1);

    const padX = 10;
    const padY = 6;
    const tipW = tipText.width + padX * 2;
    const tipH = tipText.height + padY * 2;
    const tipY = bottomY - tipH;

    // Background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0a0e1e, 0.95);
    bg.fillRoundedRect(cx - tipW / 2, tipY, tipW, tipH, 4);
    bg.lineStyle(1, 0x00ffff, 0.3);
    bg.strokeRoundedRect(cx - tipW / 2, tipY, tipW, tipH, 4);
    container.add(bg);

    // Position text
    tipText.setY(tipY + tipH - padY);
    container.add(tipText);

    // Small arrow/triangle pointing down
    const arrowG = this.scene.add.graphics();
    arrowG.fillStyle(0x0a0e1e, 0.95);
    arrowG.fillTriangle(cx - 5, tipY + tipH, cx + 5, tipY + tipH, cx, tipY + tipH + 5);
    container.add(arrowG);

    this.contentContainer.add(container);
    this.tooltipContainer = container;
  }

  private hideTooltip(): void {
    if (this.tooltipContainer) {
      this.tooltipContainer.destroy();
      this.tooltipContainer = null;
    }
  }

  private drawCircle(
    gfx: Phaser.GameObjects.Graphics,
    cx: number, cy: number, r: number,
    hovered: boolean,
  ): void {
    gfx.clear();
    if (hovered) {
      // Hover: brighter fill, stronger border
      gfx.fillStyle(0x1a2a4a, 1);
      gfx.fillCircle(cx, cy, r);
      gfx.lineStyle(3, 0x00ffff, 0.8);
      gfx.strokeCircle(cx, cy, r);
    } else {
      // Normal: #00FFFF at 20% fill, 3px solid cyan border
      gfx.fillStyle(0x00ffff, 0.2);
      gfx.fillCircle(cx, cy, r);
      gfx.lineStyle(3, 0x00ffff, 1);
      gfx.strokeCircle(cx, cy, r);
    }
  }

  // ── Docked ship tabs (Chrome-style, above card) ────────────

  private clearDockedTabs(): void {
    if (this.dockedTabContainer) {
      this.dockedTabContainer.destroy(true);
      this.dockedTabContainer = null;
    }
  }

  private buildDockedTabs(): void {
    this.clearDockedTabs();

    // Only show tabs if there are docked ships
    if (this.dockedShips.length === 0) return;

    const container = this.scene.add.container(0, -DOCKED_TAB_H - 2);
    this.add(container);
    this.dockedTabContainer = container;

    let tabX = 0;

    // First tab: the parent entity (planet/station)
    const parentLabel = this.dockedParentPlanet
      ? (this.dockedParentPlanet.planetName || 'Planet')
      : (this.dockedParentStation
        ? (this.dockedParentStation.stationType.charAt(0).toUpperCase() + this.dockedParentStation.stationType.slice(1))
        : 'Location');
    const isParentActive = this.activeDockedTab === -1;

    tabX = this.drawDockedTab(container, tabX, parentLabel, null, isParentActive, () => {
      this.activeDockedTab = -1;
      // Restore the parent entity view
      if (this.dockedParentPlanet) {
        this.currentPlanet = this.dockedParentPlanet;
        this.currentUnit = null;
        this.activeTab = 'buildings';
      } else if (this.dockedParentStation) {
        this.currentStation = this.dockedParentStation;
        this.currentUnit = null;
      }
      this.buildDisplay();
    });

    // One tab per docked ship
    for (let i = 0; i < this.dockedShips.length; i++) {
      const ship = this.dockedShips[i];
      const unitType = (ship.unitType || ship.type || 'scout').toLowerCase();
      const displayName = I18n.shipName(unitType);
      const iconKey = SHIP_ICON_MAP[unitType] || SHIP_ICON_MAP['scout'];
      const isActive = this.activeDockedTab === i;

      const idx = i;
      tabX = this.drawDockedTab(container, tabX, displayName, iconKey, isActive, () => {
        this.activeDockedTab = idx;
        this.activeTab = 'actions';
        this.buildDisplay();
      });
    }
  }

  /** Draw a single docked-ship tab and return the next x position */
  private drawDockedTab(
    container: Phaser.GameObjects.Container,
    x: number,
    label: string,
    iconKey: string | null,
    isActive: boolean,
    onClick: () => void,
  ): number {
    // Measure label
    const tempText = this.scene.add.text(0, 0, label, {
      fontSize: '9px',
      fontStyle: '900',
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    const textW = tempText.width;
    tempText.destroy();

    const iconW = iconKey && this.scene.textures.exists(iconKey) ? DOCKED_TAB_ICON_SIZE + 4 : 0;
    const tabW = DOCKED_TAB_PAD_H * 2 + iconW + textW;

    // Tab background (rounded top, flat bottom to sit flush on card)
    const gfx = this.scene.add.graphics();
    if (isActive) {
      gfx.fillStyle(BG_COLOR, 1);
      gfx.fillRoundedRect(x, 0, tabW, DOCKED_TAB_H, { tl: 6, tr: 6, bl: 0, br: 0 });
      gfx.lineStyle(2, 0x00ffff, 0.6);
      // Draw top + left + right borders only (no bottom, flush with card)
      gfx.beginPath();
      gfx.moveTo(x, DOCKED_TAB_H);
      gfx.lineTo(x, 6);
      gfx.arc(x + 6, 6, 6, Math.PI, Math.PI * 1.5);
      gfx.lineTo(x + tabW - 6, 0);
      gfx.arc(x + tabW - 6, 6, 6, Math.PI * 1.5, 0);
      gfx.lineTo(x + tabW, DOCKED_TAB_H);
      gfx.strokePath();
    } else {
      gfx.fillStyle(0x0a0e1e, 0.8);
      gfx.fillRoundedRect(x, 2, tabW, DOCKED_TAB_H - 2, { tl: 6, tr: 6, bl: 0, br: 0 });
      gfx.lineStyle(1, 0x334466, 0.4);
      gfx.beginPath();
      gfx.moveTo(x, DOCKED_TAB_H);
      gfx.lineTo(x, 8);
      gfx.arc(x + 6, 8, 6, Math.PI, Math.PI * 1.5);
      gfx.lineTo(x + tabW - 6, 2);
      gfx.arc(x + tabW - 6, 8, 6, Math.PI * 1.5, 0);
      gfx.lineTo(x + tabW, DOCKED_TAB_H);
      gfx.strokePath();
    }
    container.add(gfx);

    // Icon
    let contentX = x + DOCKED_TAB_PAD_H;
    if (iconKey && this.scene.textures.exists(iconKey)) {
      const icon = this.scene.add.image(contentX + DOCKED_TAB_ICON_SIZE / 2, DOCKED_TAB_H / 2, iconKey);
      icon.setDisplaySize(DOCKED_TAB_ICON_SIZE, DOCKED_TAB_ICON_SIZE);
      if (!isActive) icon.setAlpha(0.5);
      container.add(icon);
      contentX += DOCKED_TAB_ICON_SIZE + 4;
    }

    // Label
    const text = this.scene.add.text(contentX, DOCKED_TAB_H / 2, label, {
      fontSize: '9px',
      fontStyle: '900',
      color: isActive ? '#E0E8FF' : '#667799',
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    text.setOrigin(0, 0.5);
    container.add(text);

    // Hit zone
    const hit = this.scene.add.zone(x, 0, tabW, DOCKED_TAB_H).setOrigin(0, 0);
    hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, tabW, DOCKED_TAB_H), Phaser.Geom.Rectangle.Contains);
    container.add(hit);

    hit.on('pointerover', () => {
      if (!isActive) text.setColor('#CCDDFF');
      this.scene.input.setDefaultCursor('pointer');
    });
    hit.on('pointerout', () => {
      if (!isActive) text.setColor('#667799');
      this.scene.input.setDefaultCursor('default');
    });
    hit.on('pointerdown', onClick);

    return x + tabW + DOCKED_TAB_GAP;
  }

  // ── Show / Hide ───────────────────────────────────────────

  private show(): void {
    if (this.isVisible) return;

    // Kill any in-progress hide tween and ensure Phaser visibility is restored
    this.scene.tweens.killTweensOf(this);
    this.isVisible = true;
    this.setVisible(true);

    const { width, height } = this.scene.cameras.main;

    if (this.inSidebarMode) {
      // ── Mobile left sidebar slide-in ──
      // Start off-screen LEFT, slide to x=MOBILE_SIDEBAR_MARGIN_X. Y has
      // already been set by buildDisplay/buildActionButtons to the correctly
      // centered value.
      const targetX = MOBILE_SIDEBAR_MARGIN_X;
      const targetY = this.y;
      this.setX(-(MOBILE_SIDEBAR_W + 20));
      this.scene.tweens.add({
        targets: this,
        x: targetX,
        duration: Anim.panel,
        ease: 'Back.easeOut',
      });
      // Emit with dockH=0 so bottom-of-screen overlays (chat strip, FABs,
      // mini-radar) do NOT shift — the sidebar doesn't occupy bottom space.
      // `dockLeftW` tells the game scene how wide a left-edge column to
      // swallow so map drags don't start inside the panel.
      EventBus.emit('mmo-command-dock-shown', {
        dockTopY: targetY,
        dockH: 0,
        dockLeftW: MOBILE_SIDEBAR_W + MOBILE_SIDEBAR_MARGIN_X + 8,
      });
      return;
    }

    if (this.inInfoPanelMode) {
      // ── Mobile left info-panel slide-in ──
      const targetX = MOBILE_INFO_MARGIN_X;
      const targetY = MOBILE_INFO_TOP;
      this.setX(-(this.infoPanelW + 24));
      this.setY(targetY);
      this.scene.tweens.add({
        targets: this,
        x: targetX,
        duration: Anim.panel,
        ease: 'Back.easeOut',
      });
      EventBus.emit('mmo-command-dock-shown', {
        dockTopY: targetY,
        dockH: 0,
        dockLeftW: this.infoPanelW + MOBILE_INFO_MARGIN_X + 8,
      });
      return;
    }

    this.setX((width - this.currentDockW) / 2);
    this.setY(height);

    // On mobile, nudge the dock 25px DOWN (offset -5 instead of the old +20)
    // so the green border flushes tight with the screen's bottom edge in both
    // portrait and landscape. The dock has no bottom border stroke so the
    // final 5px simply bleeds off-screen — safe because nothing draws there.
    const mobileOffset = isMobile(this.scene) ? -5 : 0;
    const dockTopY = height - DOCK_H - mobileOffset;
    this.scene.tweens.add({
      targets: this,
      y: dockTopY,
      duration: Anim.panel,
      ease: 'Back.easeOut',
    });

    // Broadcast so mobile overlays (chat strip, FABs) can step out of the way.
    EventBus.emit('mmo-command-dock-shown', { dockTopY, dockH: DOCK_H });
  }

  private hide(): void {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.closeGotoInput();
    this.hideShipPicker();
    this.clearDockedTabs();
    this.currentUnit = null;
    this.currentAsteroid = null;
    this.currentPlanet = null;
    this.currentStation = null;
    this.currentPlatform = null;
    this.dockedShips = [];
    this.activeDockedTab = -1;
    this.dockedParentPlanet = null;
    this.dockedParentStation = null;
    this.displayMode = null;

    const wasSidebar = this.inSidebarMode;
    const wasInfoPanel = this.inInfoPanelMode;
    const { height } = this.scene.cameras.main;

    if (wasSidebar) {
      // Slide the sidebar back off the left edge.
      this.scene.tweens.add({
        targets: this,
        x: -(MOBILE_SIDEBAR_W + 20),
        duration: Anim.normal,
        ease: 'Quad.easeIn',
        onComplete: () => {
          if (!this.isVisible) this.setVisible(false);
          this.inSidebarMode = false;
        },
      });
    } else if (wasInfoPanel) {
      // Slide the info panel back off the left edge.
      this.scene.tweens.add({
        targets: this,
        x: -(this.infoPanelW + 24),
        duration: Anim.normal,
        ease: 'Quad.easeIn',
        onComplete: () => {
          if (!this.isVisible) this.setVisible(false);
          this.inInfoPanelMode = false;
        },
      });
    } else {
      this.scene.tweens.add({
        targets: this,
        y: height + 10,
        duration: Anim.normal,
        ease: 'Quad.easeIn',
        onComplete: () => {
          if (!this.isVisible) this.setVisible(false);
        },
      });
    }

    // Broadcast so mobile overlays can restore their original position.
    EventBus.emit('mmo-command-dock-hidden');
  }

  // ── Cleanup ───────────────────────────────────────────────

  /** Programmatically select a docked ship tab by unit ID (used by FLEET sidebar) */
  public selectDockedShipTab(unitId: string): void {
    const idx = this.dockedShips.findIndex(s => s.id === unitId);
    if (idx >= 0) {
      this.activeDockedTab = idx;
      this.activeTab = 'actions';
      this.buildDisplay();
    }
  }

  destroy(fromScene?: boolean): void {
    this.clearDockedTabs();
    EventBus.off('mmo-entity-selection-resolved', this.onEntitySelected, this);
    EventBus.off('mmo-deselect', this.onDeselect, this);
    EventBus.off('mmo-entity-deselected', this.onDeselect, this);
    EventBus.off('build-queue-updated', this.onBuildQueueUpdated, this);
    EventBus.off('fleet-activate-docked-tab', this.onFleetActivateDockedTab, this);
    EventBus.off('mmo-cargo-unloaded', this.onCargoUnloaded, this);
    EventBus.off('mmo-movement-mode-started', this.onMovementModeStarted, this);
    EventBus.off('mmo-movement-mode-cancelled', this.onMovementModeCancelled, this);
    super.destroy(fromScene);
  }
}
